// The agent run engine — the deterministic pipeline behind the workspace's
// daily operations. No JSX, no React, no Math.random in anything that decides
// an outcome. Given the same rows and the same mirror it picks the same leads
// and lists the same items every time, which is the only reason a human can be
// asked to trust the result.
//
// THREE OPERATIONS, one engine (see OPERATIONS below):
//   1. initial-outreach-50  — 50 new leads: nobody at their company contacted.
//   2. followup-50          — Daily Tasks: the rep's HubSpot tasks, overdue or
//                             due today, in the rep's own wording.
//   3. reply-open-threads   — Follow-Up: the customer lanes off the portal's
//                             usage data in HubSpot — inactive users, trials
//                             ending within 7 days, expired trials, payment
//                             failed — each card wearing its status.
//
// The ids of operations 2 and 3 predate what they do (2026-09-23 recut, at the
// owner's request): daily-task records and stored run receipts reference
// operations by id, and renaming them would orphan every one. The label is the
// truth; the id is a key. The openclaw cadence engine that used to run under
// operation 2 (wait gate, reply freeze, 7-touch ladder, rank-and-pin) has no
// operation any more and was removed from this file with the recut; the
// arithmetic in lib/cadence.js and the pinned-batch contract in lib/batchPin.js
// stay, because initial outreach and the stored receipts' provenance views
// still read them.
//
// WHO IS PICKED (operation 1) is decided by row facts alone. Its research step
// (the 'enriched' node) then decorates the ALREADY-PICKED leads with web
// research through the enrichment API — the ONE step in this file that can
// spend money; it is bounded and can never change the selection. Operations 2
// and 3 spend nothing: they read the HubSpot mirror and hand the facts over.
//
// WE DO NOT WRITE TO OUR OWN PEOPLE. Every operation builds one set of "our"
// addresses and domains per run (readOwnOrg, from the signed-in session, the
// team roster, the CRM owners and the mirror's own derivation) and refuses
// them: initial outreach steps past one of ours in a row's packed contacts,
// and Follow-Up never lists a colleague or a test fixture as a customer to
// chase. The Settings exclusion list (lib/taskExclusions.js) is consulted
// first, everywhere.
//
// It is NOT network-free, and nothing here should claim otherwise: operation 1
// reads the workspace activity log (GET /api/events), the HubSpot mirror
// (GET /api/hubspot/*) and the enrichment API (/api/enrich); operation 2 reads
// the team roster, the CRM owner list and the mirror's task rows
// (GET /api/hubspot/metrics?rows=tasks); operation 3 reads the mirror's
// product-usage view (GET /api/hubspot/users) and its email engagements. No
// mail is ever sent, and this file writes nothing anywhere — the run's own
// ledger row is written by its caller (lib/runHost.js). When a read is
// degraded the pass says so instead of assuming a clean answer: unread is
// never rendered as empty.
//
// Each operation is written as a GRAPH OF PASSES (see PASS_DEFS) so the UI can
// render the work as it happens: each pass announces itself (status
// 'running'), does one honest thing, then reports what it did in a headline a
// person can read. Every node declares `dependsOn`, and runGraph() starts a
// node the moment its own dependencies settle — so Follow-Up's two roots (the
// usage read and the who-we-are read) are genuinely in flight together.
//
// Every node also records, per lead, what it decided about that lead — the
// result carries `trace[leadKey] = [{nodeId, verdict, note}]` keyed by keyOf().
// That is the ONLY source the provenance view may answer "why is this lead
// here?" from: it is written by the node at the moment it decides, never
// reconstructed afterwards. See makeTracer().
//
// The CRM is READ, never written (hubspot.js has no write path at all), and a
// mirror that is missing or unreachable costs a run nothing but a line of
// explanation. A lead is never dropped because a system was down.
//
// HARD SAFETY RULE, all three operations: this engine never sends email, and it
// creates nothing anywhere. Every run ENDS AT ITS OWN OUTPUT: the leads it
// picked, the tasks it listed, the customers it flagged land on the run
// receipt, which is a message in the chat and nothing more. A human reads it
// and does the work by hand.
//
// HONEST COUNTS, all three operations: when 12 tasks are due the run says 12.
// There is no padding to the headline number in the operation's label, and an
// operation with nothing to do says so and creates nothing.
import { keyOf } from './companyMeta.js'
import { readPin, savePin, pinOrder, pinSummaryLine } from './batchPin.js'
import { knownRecipients } from './outreachContacts.js'
import { gmailStatus, gmailSentIndex } from './gmail.js'
import { listEvents } from './events.js'
import {
  hubspotMetrics,
  isOpenTask,
  hubspotDate,
  hubspotIndex,
  hubspotCompany,
  normDomain,
  nameSlug,
  isOpenDeal,
  hubspotOwners,
  hubspotUsers,
} from './hubspot.js'
import {
  readMailHistory,
  classifyThread,
  threadMessages,
  chicagoDay as mailChicagoDay,
  isRobotAddress,
} from './mailHistory.js'
import { currentScope } from './userScope.js'
import { readExclusions, exclusionSet, isExcluded } from './taskExclusions.js'
import { listMembers } from './team.js'
import { roleOrEmpty } from './roles.js'
import {
  enrichLead,
  researchedKeys,
  startEnrich,
  enrichStatus,
  MAX_ENRICH_ROWS,
} from './enrich.js'

// How long each pass lingers before reporting. Not a fake progress bar: the
// work really is instant, and a run that flashes through six states in one
// frame is a run nobody can audit. Slow enough to read, short enough to wait.
const BEAT_MS = 450

export const OPERATIONS = [
  {
    id: 'initial-outreach-50',
    label: 'Initial outreach — 50 new leads',
    blurb:
      'Pick 50 uncontacted, reachable leads and research each one with OpenAI web search (cached dossiers cost nothing). The batch lands on the run receipt with a draft per lead — nothing else is created, and nothing is sent.',
    targetCount: 50,
  },
  {
    // The id predates the openclaw port and stays EXACTLY as-is: daily-task
    // records and stored run receipts reference operations by id, and renaming
    // it would orphan every one of them. The label is the truth; the id is a key.
    id: 'followup-50',
    label: 'Daily Tasks',
    blurb:
      'The rep’s own HubSpot tasks — overdue, or due today on the Chicago calendar — in their own wording, read straight from the mirror and linked back to each record. Nothing is created, and nothing is sent.',
    targetCount: 50,
  },
  {
    // Same story: the id stays for the receipts, the label carries the meaning.
    id: 'reply-open-threads',
    label: 'Follow-Up',
    blurb:
      'The customer follow-up, in four sections off the portal’s usage data in HubSpot: Inactive Users (no software access in over 7 days, each with a copy-pastable check-in), Trials ending within 7 days, Expired trials, and Payment failed. Every card wears its status. It creates nothing and sends nothing.',
    targetCount: 40,
  },
]

export function getOperation(operationId) {
  return OPERATIONS.find((o) => o.id === operationId) || null
}

// Shape of every pass of every operation, keyed by operation id. Titles and
// subtitles live here so initialPasses() can render a whole pipeline greyed-out
// before anything runs.
//
// THIS IS A GRAPH, NOT A LIST. Every node declares `dependsOn: [id, …]`, and the
// scheduler in runGraph() starts a node the moment ITS OWN dependencies have
// settled — so two nodes that name the same dependencies genuinely run at the
// same time (one Promise each, both in flight), rather than being staggered to
// look busy. `lane` is a layout hint for the canvas: parallel siblings get
// 0, 1, … so the view can draw them as branches without re-deriving the fork.
// The array order is still the emission order the UI keys on (onPass(index) —
// Chat.jsx merges by index), so nodes stay listed in a sensible reading order.
//
// PASS IDS ARE REUSED ACROSS OPERATIONS ON PURPOSE. AgentRunView keys its icon
// map (PASS_ICON) off the pass id and falls back to a generic sparkle for
// anything it does not know. Reusing source/reachable/contacted/enriched/
// history/freeze means every pass of every operation draws the icon that
// matches what it does, without this file reaching into the view's icon set.
// The id is a shape, not a claim — the title and headline carry the meaning.
// Ids are unique WITHIN an operation, because dependsOn names them.
// ── WHERE EACH NODE'S CODE LIVES ────────────────────────────────────────────
//
// Every node below carries `exec`: the file and function that implement it,
// and the lambdas (with THEIR handler files) it reads through. It is stored on
// the receipt with the pass (chat.js#packPass) and shown in the draft
// provenance's Developer view as "the code that ran" for that step — so a
// reader who thinks a step decided wrongly is handed the exact place to go and
// correct it, not a paragraph about what the step is for.
//
// Declared HERE, next to the node table, and nowhere else: a description that
// lived in the view would describe the code it remembered, not the code that
// ran. Keep it truthful when a node changes what it reads. Runtime-produced
// records (the research dossier's, the draft's) come from the lambdas
// themselves and override these on the passes they own.
const AGENT_FILE = 'src/lib/agentRun.js'
const LAMBDA_ROOT = 'modules/lambda/python3.12/api-gateway'
const LAMBDAS = {
  leads: { function: 'revailo-sales-leads', file: `${LAMBDA_ROOT}/leads/handler.py`, route: 'GET /api/leads/<category>/<region>' },
  events: { function: 'revailo-sales-events', file: `${LAMBDA_ROOT}/events/handler.py`, route: 'GET /api/events' },
  hubspot: { function: 'revailo-sales-hubspot', file: `${LAMBDA_ROOT}/hubspot/handler.py`, route: 'GET /api/hubspot/*' },
  gmail: { function: 'revailo-sales-gmail', file: `${LAMBDA_ROOT}/gmail/handler.py`, route: 'GET /api/gmail/*' },
  enrich: { function: 'revailo-sales-enrich', file: `${LAMBDA_ROOT}/enrich/handler.py`, route: 'POST /api/enrich/start · GET /api/enrich/lead · GET /api/enrich/status' },
  enrichWorker: { function: 'revailo-sales-enrich-worker', file: `${LAMBDA_ROOT}/enrich-worker/handler.py`, route: 'SQS revailo-sales-enrich-jobs (one row per message)' },
  collaboration: { function: 'revailo-sales-collaboration', file: `${LAMBDA_ROOT}/collaboration/handler.py`, route: 'GET /api/collaboration' },
}
const browserExec = (fn, calls = [], lambdas = [], notes = '') => ({
  runtime: 'browser',
  language: 'javascript',
  file: AGENT_FILE,
  fn,
  calls,
  lambdas,
  model: '',
  ...(notes ? { notes } : {}),
})
const INITIAL_EXEC = {
  source: browserExec('runInitialOutreach#source', ['rankRows', 'scoreOf', 'keyOf (lib/companyMeta.js)'], [LAMBDAS.leads]),
  reachable: browserExec(
    'runInitialOutreach#reachable',
    ['emailOfExcluding', 'emailOf', 'readOwnOrg', 'isOwnAddress', 'listMembers (lib/team.js)', 'hubspotOwners (lib/hubspot.js)', 'currentScope (lib/userScope.js)'],
    [LAMBDAS.collaboration, LAMBDAS.hubspot],
    'Also refuses OUR OWN addresses as recipients: the signed-in session, the team roster and the CRM owner list say who “we” are, and a row whose only contact is one of ours is dropped.',
  ),
  contacted: browserExec(
    'runInitialOutreach#contacted',
    ['knownRecipients (lib/outreachContacts.js)', 'readOutboundHistory', 'readSentRecipients', 'readMailHistory (lib/mailHistory.js)', 'buildCompanyGate', 'companyTouchOf'],
    [LAMBDAS.events, LAMBDAS.hubspot, LAMBDAS.gmail],
    'The HubSpot email engagements are the system of record; the workspace log and connected Gmail sent-mail add to it.',
  ),
  'hs-match': browserExec('runInitialOutreach#hs-match', ['hubspotIndex (lib/hubspot.js)', 'crmCompanyId', 'leadLookup (lib/hubspot.js)'], [LAMBDAS.hubspot]),
  'hs-deals': browserExec('runInitialOutreach#hs-deals', ['probeCompanies', 'hubspotCompany (lib/hubspot.js)', 'rollupOpenDeals', 'isOpenDeal (lib/hubspot.js)'], [LAMBDAS.hubspot]),
  'hs-tasks': browserExec('runInitialOutreach#hs-tasks', ['probeCompanies', 'hubspotCompany (lib/hubspot.js)', 'isOpenTask (lib/hubspot.js)'], [LAMBDAS.hubspot]),
  'hs-screen': browserExec('runInitialOutreach#hs-screen', []),
  enriched: browserExec(
    'runInitialOutreach#enriched',
    ['researchedKeys (lib/enrich.js)', 'applyBatchPin', 'pinOrder (lib/batchPin.js)', 'lookupCachedDossiers', 'startEnrich (lib/enrich.js)', 'enrichStatus (lib/enrich.js)', 'researchedRowOf'],
    [LAMBDAS.enrich, LAMBDAS.enrichWorker],
    'The dossier itself is written by the enrich-worker lambda (OpenAI web search); its own exec record rides on the dossier. The email text is not written here either: each lead card asks POST /api/enrich/draft (enrich/handler.py#_draft) to write it, and that draft carries its own exec record.',
  ),
}
const DAILY_TASKS_EXEC = {
  identity: browserExec(
    'runDailyTasks#identity',
    ['readOwnOrg', 'resolveTaskReps', 'roleOrEmpty (lib/roles.js)', 'listMembers (lib/team.js)', 'hubspotOwners (lib/hubspot.js)', 'currentScope (lib/userScope.js)'],
    [LAMBDAS.collaboration, LAMBDAS.hubspot],
    'The roster’s rep role decides WHO; the CRM owner list decides WHICH records are theirs. The signed-in session is a fallback only when the roster names no rep.',
  ),
  tasks: browserExec(
    'runDailyTasks#tasks',
    ['readBoTasks', 'judgeBoTasks', 'isOpenTask (lib/hubspot.js)', 'hubspotDate (lib/hubspot.js)', 'hubspotMetrics (lib/hubspot.js)', 'isExcluded (lib/taskExclusions.js)'],
    [LAMBDAS.hubspot],
    'One GET /api/hubspot/metrics?rows=tasks read per rep with the owner id passed explicitly; windowed to overdue-or-due-today on the Chicago calendar; tasks at the Settings-excluded domains left off and counted. Read-only.',
  ),
  context: browserExec(
    'runDailyTasks#context',
    ['probeCompanies', 'hubspotCompany (lib/hubspot.js)', 'readCadenceHistory', 'readMailHistory (lib/mailHistory.js)', 'hubspotUsers (lib/hubspot.js)', 'judgeTaskEmails', 'isOwnAddress', 'isRobotAddress (lib/mailHistory.js)'],
    [LAMBDAS.hubspot, LAMBDAS.events, LAMBDAS.collaboration],
    'Reads, per task, the company’s contacts, deals and notes, the real mail thread with that company (HubSpot email engagements) and the account’s product usage, then picks the person the email goes to. The email itself is NOT written here: each task lands on the receipt draft-pending, the receipt’s resolver asks POST /api/enrich/draft (kind task_followup) to write it from the task, the thread and the notes, and the template in lib/outreachDraft.js stands in only when that call fails — labelled as the fallback.',
  ),
  briefing: browserExec('runDailyTasks#briefing', [], [], 'Assembles and counts. Nothing is stored or created.'),
}
const WORKLIST_EXEC = {
  users: browserExec(
    'runFollowUp#users',
    ['hubspotUsers (lib/hubspot.js)', 'customerUsageUnread'],
    [LAMBDAS.hubspot],
    'Read-only, like every mirror read. Fail-soft is the contract: an unreadable users API costs this run its four lanes and one honest sentence, never a crash.',
  ),
  history: browserExec(
    'runFollowUp#history',
    ['readOwnOrg', 'readCadenceHistory', 'readMailHistory (lib/mailHistory.js)', 'readOutboundHistory'],
    [LAMBDAS.hubspot, LAMBDAS.events, LAMBDAS.collaboration],
    'Who we are (never a customer to chase) and each address’s last conversation — shown on the cards, never used to filter anyone out.',
  ),
  customers: browserExec(
    'runFollowUp#customers',
    ['judgeCustomerLanes', 'isOursCustomer', 'isOwnAddress', 'isRobotAddress (lib/mailHistory.js)', 'isExcluded (lib/taskExclusions.js)'],
    [],
    'Only the inactive lane owes an email: each item lands on the receipt draft-pending, the receipt’s resolver asks POST /api/enrich/draft (kind inactive_user) to write it, and the template in lib/outreachDraft.js stands in only when that call fails — labelled as the fallback. The other three lanes are facts for Bo’s own pitch. Who is “ours” is the Settings exclusion list FIRST (Daily Tasks & Follow-Up → excluded domains and addresses; revailo.com and chicagomasonry.com by default), then the run’s own org-identity derivation, with the two corporate domains as a hard floor beneath both.',
  ),
  briefing: browserExec('runFollowUp#briefing', [], [], 'Assembles the four sections and counts. Nothing is stored or created.'),
}
const NODE_EXEC = {
  'initial-outreach-50': INITIAL_EXEC,
  'followup-50': DAILY_TASKS_EXEC,
  'reply-open-threads': WORKLIST_EXEC,
}

const PASS_DEFS = {
  'initial-outreach-50': [
    {
      id: 'source',
      title: 'Select candidates',
      subtitle: 'Every masonry lead in the directory, deduped and ranked by score.',
      dependsOn: [],
    },
    {
      id: 'reachable',
      title: 'Filter to reachable',
      subtitle: 'A lead with no email address is not a lead we can contact.',
      dependsOn: ['source'],
    },
    {
      id: 'contacted',
      title: 'Exclude contacted companies',
      subtitle:
        'New means nobody at the company yet: one touch to any address there — a logged send, HubSpot’s own email history, or real sent mail — takes the whole company off the table.',
      dependsOn: ['reachable'],
    },
    {
      id: 'hs-match',
      title: 'Open the HubSpot mirror',
      subtitle: 'One read of the match index — the CRM is read here, never written.',
      dependsOn: ['contacted'],
    },
    // ── the fork ──────────────────────────────────────────────────────────
    // Both of these depend on hs-match and on nothing else, so both start in
    // the same tick and overlap for as long as the slower one takes. They ask
    // different questions of the mirror (deal rollups vs per-company tasks),
    // which is why running them together is real work and not decoration.
    {
      id: 'hs-deals',
      title: 'Check open deals',
      subtitle: 'Is any of these leads already inside a live deal?',
      dependsOn: ['hs-match'],
      lane: 0,
    },
    {
      id: 'hs-tasks',
      title: 'Check open tasks',
      subtitle: 'Open CRM tasks somebody already owns on these companies.',
      dependsOn: ['hs-match'],
      lane: 1,
    },
    // ── the join ──────────────────────────────────────────────────────────
    {
      id: 'hs-screen',
      title: 'Apply the CRM screen',
      subtitle: 'Anyone in an open deal is out; everything else carries forward.',
      dependsOn: ['hs-deals', 'hs-tasks'],
    },
    {
      // The id predates real research and STAYS 'enriched': stored receipts
      // resolve their trace entries and icons by this id, and renaming it would
      // orphan every one of them. The title carries the new meaning.
      id: 'enriched',
      title: 'Research the leads',
      subtitle:
        'OpenAI web research on each picked lead — cached dossiers are free, fresh ones spend, all under a hard time budget. The last step: the batch it cuts is the run’s whole output.',
      dependsOn: ['hs-screen'],
    },
  ],
  // DAILY TASKS — the rep's HubSpot tasks (recut 2026-09-23; the cadence graph
  // that used to sit under this id is gone with its engine). Linear on
  // purpose: every node reads what the one before produced.
  'followup-50': [
    {
      id: 'identity',
      title: 'Find the rep',
      subtitle:
        'The roster’s rep, resolved to a HubSpot owner by e-mail, then first name — never the signed-in session.',
      dependsOn: [],
    },
    {
      id: 'tasks',
      title: 'Read the HubSpot tasks',
      subtitle:
        'The rep’s open tasks from the mirror — overdue, or due today on the Chicago calendar — in the rep’s own wording. Our own domains (Settings) are left off and counted.',
      dependsOn: ['identity'],
    },
    {
      id: 'context',
      title: 'Read each task’s conversation',
      subtitle:
        'Per company: who to write to, what was last said in the real mail thread, the rep’s notes, open deals and product usage — the situation each email is drafted from.',
      dependsOn: ['tasks'],
    },
    {
      id: 'briefing',
      title: 'Assemble the emails',
      subtitle:
        'One email card per task, due today first, then the longest overdue. The last step: nothing is created or sent — the drafts are written onto the receipt for the rep to copy.',
      dependsOn: ['context'],
    },
  ],
  // FOLLOW-UP — the four customer lanes (recut 2026-09-23). Two independent
  // roots — the usage read and the who-we-are/mail-history read — joined by
  // the lanes node, because the lanes need the usage attributes AND the run's
  // org identity. A root that cannot be read is REPORTED, never fatal: the
  // lanes node says the check-ins could not be built, and the receipt says
  // unread, never empty. The last node assembles the sections and STORES
  // NOTHING.
  'reply-open-threads': [
    {
      id: 'users',
      title: 'Read customer usage',
      subtitle:
        'The portal’s product-usage attributes in HubSpot — last software access, trial and subscription ends, payment signals. Unreadable is reported, never fatal.',
      dependsOn: [],
      lane: 0,
    },
    {
      id: 'history',
      title: 'Read who we are and the mail history',
      subtitle:
        'Our own addresses and domains (never a customer to chase), and each address’s last conversation off the HubSpot email engagements — shown on the cards, never a filter.',
      dependsOn: [],
      lane: 1,
    },
    {
      id: 'customers',
      title: 'Build the four lanes',
      subtitle:
        'Inactive users, trials ending within 7 days, expired trials and failed payments — real customers only; our own and test accounts are excluded and counted.',
      dependsOn: ['users', 'history'],
    },
    {
      id: 'briefing',
      title: 'Assemble the follow-up',
      subtitle:
        'Four sections, every card wearing its status — nothing created, nothing sent. The rep’s HubSpot tasks live in Daily Tasks.',
      dependsOn: ['customers'],
    },
  ],
}

// ── small pure helpers (exported so the UI and tests can reuse them) ─────────

const fmt = (n) => Number(n || 0).toLocaleString('en-US')
const plural = (n, one, many) => (n === 1 ? one : many)

// Deliberately NOT global: a /g regex carries lastIndex between calls and would
// start skipping matches. First match wins, which for the packed contacts blob
// is the first listed contact — a stable, deterministic choice.
const EMAIL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/i

// The address we would actually write to: the row's own `email` column, else
// the first address buried in the packed `contacts` field ("Email: x@y.com").
// Lowercased, because the contacted-set is matched case-insensitively.
export function emailOf(row) {
  if (!row) return ''
  const direct = String(row.email || '').match(EMAIL_RE)
  if (direct) return direct[0].toLowerCase()
  const packed = String(row.contacts || '').match(EMAIL_RE)
  return packed ? packed[0].toLowerCase() : ''
}

// The SAME pattern as EMAIL_RE, global, so emailOfExcluding() below cannot
// drift from emailOf(): "the first address" and "every address, in order" have
// to agree about what an address is, or the two answer differently on the same
// row. Built from EMAIL_RE.source for exactly that reason.
const EMAIL_ALL_RE = new RegExp(EMAIL_RE.source, 'gi')

/**
 * emailOf() with a veto: the first address on the row — the email column, then
 * the packed contacts blob, in the row's own order — that `skip` does NOT
 * refuse.
 *
 * WHY THIS EXISTS. Revailo's sister company is in the masonry import, and its
 * packed contacts blob lists our OWN founders ("Jacob Willner - Co-Founder -
 * jacob@revailo.com"). emailOf() takes the first address it finds, so the
 * pipeline picked one of us as the lead's recipient and a follow-up draft went
 * out opening "Hi Jacob," pitching Revailo to Revailo's co-founder. Stepping
 * past our own address to the next real contact on the row keeps the lead
 * usable instead of throwing away a real company over who happens to be listed
 * first; when there is nobody on the row but us, the caller drops it and says
 * why.
 *
 * Deterministic: the row's own order decides, exactly as before.
 *
 * @param {object} row
 * @param {(address:string)=>boolean} [skip]
 * @returns {{email:string, refused:string[]}}  `refused` is every address the
 *   veto turned down, in order, so the caller can name them.
 */
export function emailOfExcluding(row, skip) {
  const refuse = typeof skip === 'function' ? skip : () => false
  const refused = []
  if (!row) return { email: '', refused }
  for (const src of [row.email, row.contacts]) {
    for (const raw of String(src || '').match(EMAIL_ALL_RE) || []) {
      const addr = raw.toLowerCase()
      if (refuse(addr)) {
        if (!refused.includes(addr)) refused.push(addr)
        continue
      }
      return { email: addr, refused }
    }
  }
  return { email: '', refused }
}

// The research columns that ride along with the import. These are the fields a
// human (or an earlier offline pass) would otherwise pay an LLM to produce.
export const ENRICHMENT_FIELDS = [
  { field: 'score', label: 'lead score' },
  { field: 'sales_angles', label: 'sales angles' },
  { field: 'objections', label: 'objections' },
  { field: 'owners', label: 'owners' },
  { field: 'has_estimator', label: 'estimator' },
  { field: 'capital_backing', label: 'capital backing' },
  { field: 'contact_name', label: 'named contact' },
]

const hasField = (row, field) => String(row?.[field] ?? '').trim() !== ''

export function enrichmentSignals(row) {
  return ENRICHMENT_FIELDS.filter((f) => hasField(row, f.field)).map((f) => f.label)
}

// Two signals, not one. A row carrying only `contact_name` has a scraped fact,
// not research; two or more of these columns means the research pass genuinely
// ran on this row. (In the shipped masonry data every reachable row carries at
// least four, so this threshold is conservative rather than convenient.)
export const MIN_IMPORT_SIGNALS = 2

export function isEnrichedOnImport(row) {
  return enrichmentSignals(row).length >= MIN_IMPORT_SIGNALS
}

// ── web research (the enrichment API) ───────────────────────────────────────
// The machinery the 'enriched' node and the follow-up's cached merge share.
// Nothing in this section ever drops a lead: research is personalization, and
// a lead whose research failed drafts from its own import row.

// Hard cap on the WHOLE research step of one run. On expiry the run proceeds:
// unfinished leads draft from their rows and their trace says so. The seam
// below exists so a harness can run the timeout path in milliseconds.
export const RESEARCH_BUDGET_MS = 240_000
// How often each outstanding batch is polled while the budget lasts.
export const RESEARCH_POLL_MS = 4_000
// How many free cache lookups fly at once. Bounded politeness, not a spend cap
// — GET /enrich/lead never costs anything.
const RESEARCH_LOOKUP_CHUNK = 10

let researchBudgetMs = RESEARCH_BUDGET_MS
let researchPollMs = RESEARCH_POLL_MS

// Test seam (same idea as hubspot.js#_resetHubspotIndexCache): shrink the
// budget and poll cadence so a harness can walk the timeout path without
// waiting four real minutes. Call with no arguments to restore the defaults.
export function _setResearchTimings({ budgetMs, pollMs } = {}) {
  researchBudgetMs = Number.isFinite(budgetMs) ? budgetMs : RESEARCH_BUDGET_MS
  researchPollMs = Number.isFinite(pollMs) ? pollMs : RESEARCH_POLL_MS
}

// The dossier's sources[] are full URLs the model actually opened; what the
// citations and the receipt carry are their bare domains, deduped, bounded.
function sourceDomainsOf(dossier) {
  const out = []
  for (const raw of Array.isArray(dossier?.sources) ? dossier.sources : []) {
    const d = normDomain(raw)
    if (d && !out.includes(d)) out.push(d)
    if (out.length >= 6) break
  }
  return out
}

// One researched value, normalized: arrays (pain_points, buying_signals) join
// to one string, and the model's honest "Unknown" reads as absent — a draft
// must never carry the word "Unknown" as if it were a fact.
function researchFact(value) {
  const v = Array.isArray(value)
    ? value.map((x) => String(x || '').trim()).filter(Boolean).join('; ')
    : String(value ?? '').trim()
  return /^unknown\.?$/i.test(v) ? '' : v
}

// buying_signals is an ARRAY of dated, individually-cited signals. The story
// shows ONE — the freshest thing the research found — so the merge carries the
// first real entry rather than the joined blob researchFact() would make. A
// dossier that answered with a plain string has already given us that one
// signal. "Unknown" still reads as absent.
function firstSignal(value) {
  const first = Array.isArray(value)
    ? value.map((x) => String(x || '').trim()).find(Boolean) || ''
    : value
  return researchFact(first)
}

// The dossier's sources[] are the URLs the model actually opened. The domains
// above are what a citation LINE reads; these are what a LINK points at, so
// they are kept WHOLE — parsed, http(s) only, deduped, bounded by count, never
// reshaped and never truncated. Anything that does not parse as a web URL is
// not a source link and is dropped entire: a broken href under a "researched"
// heading is exactly the invented evidence the standing rule forbids.
function sourceUrlsOf(dossier) {
  const out = []
  for (const raw of Array.isArray(dossier?.sources) ? dossier.sources : []) {
    const value = String(raw || '').trim()
    if (!value) continue
    let parsed
    try {
      parsed = new URL(value)
    } catch {
      continue
    }
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') continue
    if (!parsed.hostname.includes('.')) continue
    if (!out.includes(value)) out.push(value)
    if (out.length >= 6) break
  }
  return out
}

// A copy of the row with the dossier merged on, per the engine↔draft contract:
// `_research` is the full dossier, the research_* fields are the convenience
// copies the draft builders read (verbatim dossier text — inline "(per …)"
// citations are stripped at the draft boundary, not here), `researched` /
// `researchMode` / `researchedAt` are the provenance. A COPY, never a
// mutation: the source row belongs to React state upstream.
//
// EXPORTED because the receipt finishes what this node starts: a lead whose
// dossier lands after the run is merged by the SAME function on the client
// (see Chat.jsx's research resolver), so a draft written a minute later reads
// exactly the fields a draft written inside the run would have read. Two
// merge implementations would be two definitions of what "researched" means.
export function researchedRowOf(row, dossier, { mode, at, exec = null }) {
  const merged = {
    ...row,
    _research: dossier || {},
    researched: true,
    researchMode: mode,
  }
  if (at) merged.researchedAt = at
  // THE CODE THAT PRODUCED THE DOSSIER — the enrich-worker's own exec record
  // (models, prompt shas, its source sha), returned by the API next to the
  // result. Carried onto the row so the receipt can store it with the lead and
  // the Developer view can show it on the research step. Absent on a dossier
  // researched before exec records existed; the view says so.
  if (exec && typeof exec === 'object') merged.research_exec = exec
  const fields = {
    research_service_area: researchFact(dossier?.market_footprint),
    research_current_solution: researchFact(dossier?.current_solution),
    research_recent_activity: researchFact(dossier?.recent_activity),
    research_pain_points: researchFact(dossier?.pain_points),
    research_owners: researchFact(dossier?.owners),
    // The synthesis line: one sentence naming a REAL specific of theirs, tied
    // to takeoff time or bid capacity, written to be sent as-is.
    research_opener: researchFact(dossier?.opener),
    // One dated signal, not the joined list — see firstSignal().
    research_buying_signal: firstSignal(dossier?.buying_signals),
  }
  for (const [field, value] of Object.entries(fields)) {
    if (value) merged[field] = value
  }
  const domains = sourceDomainsOf(dossier)
  if (domains.length) merged.research_sources = domains
  // The real URLs, whole, for the story's clickable evidence. Absent when the
  // dossier carried none — the story then says the research reported no source
  // links, rather than dressing an internal citation up as a website.
  const urls = sourceUrlsOf(dossier)
  if (urls.length) merged.research_source_urls = urls
  return merged
}

// The trace note for a researched lead: what ran, when the research is from,
// and which domains it stands on — every value off the merged row itself, so
// the note can never name a date or a domain the dossier does not hold.
function researchNoteOf(row) {
  const when = row.researchedAt ? shortDay(row.researchedAt) : ''
  const src = (row.research_sources || []).slice(0, 3).join(', ')
  const head =
    row.researchMode === 'cached'
      ? `researched — cached dossier${when ? ` from ${when}` : ''}`
      : `researched — fresh OpenAI web research${when ? ` on ${when}` : ''}`
  return `${head}${src ? `, sources: ${src}` : ''}`
}

/**
 * Free cache lookups for a set of items ({ key, row }), in bounded chunks.
 * Merges a found dossier onto item.row (via `merge(item, dossier, at)`) and
 * reports { cached, failed, misses, unavailable }. When the WHOLE first chunk
 * rejects, the API itself is judged unreachable (`unavailable` carries the
 * first error's own words) and nothing further is asked.
 */
async function lookupCachedDossiers({ items, category, signal, deadline, merge }) {
  let cached = 0
  let failed = 0
  const misses = []
  let unavailable = ''
  for (let s = 0; s < items.length; s += RESEARCH_LOOKUP_CHUNK) {
    if (signal?.aborted) throw abortError()
    const chunk = items.slice(s, s + RESEARCH_LOOKUP_CHUNK)
    let settled
    try {
      settled = await raceResearch(
        Promise.all(
          chunk.map((item) =>
            enrichLead(category, item.key, { signal })
              .then((data) => ({ item, data }))
              .catch((err) => {
                if (err?.name === 'AbortError') throw err
                return { item, err }
              }),
          ),
        ),
        { signal, deadline },
      )
    } catch (err) {
      if (err?.name !== 'ResearchBudgetError') throw err
      // The budget ran dry mid-lookup (or a lookup fetch hung past it):
      // everything not yet answered is a plain miss. The caller's paid
      // pipeline — whose deadline has equally passed — hands these leads the
      // timeout admission; nothing here reads as "the API is down".
      misses.push(...items.slice(s))
      break
    }
    if (signal?.aborted) throw abortError()
    if (s === 0 && settled.length && settled.every((r) => r.err)) {
      // Not one answer in the whole first chunk: that is the API being
      // unreachable, not ten separate coincidences.
      unavailable = String(settled[0].err?.message || 'the enrichment API could not be reached')
      break
    }
    for (const r of settled) {
      if (r.err) {
        failed += 1
        misses.push(r.item)
        continue
      }
      if (r.data?.found && r.data.result) {
        merge(r.item, r.data.result, String(r.data.enrichedAt || ''), r.data.exec || null)
        cached += 1
      } else {
        // THE SERVER'S IN-FLIGHT MARKER, carried out with the miss. It is the
        // only thing that separates "no dossier YET because a worker is
        // running" from "no dossier and nobody is buying one", and the local
        // spend ledger cannot tell those apart inside its 30-minute grace
        // window. The paid pass below needs it: a row the ledger makes it skip
        // is only "still coming" if the server says something is coming.
        r.item.serverPending = r.data?.pending === true
        misses.push(r.item)
      }
    }
  }
  return { cached, failed, misses, unavailable }
}

// The import carries a `score` column. Parse it once, here, so every caller
// agrees on what "unscored" means. A blank, "n/a", or malformed score yields
// null rather than 0: an unscored lead is not a zero-scoring lead, and
// collapsing the two would bury genuinely bad leads among merely unmeasured
// ones (and vice versa).
export function scoreOf(row) {
  const n = Number.parseFloat(String(row?.score ?? '').trim())
  return Number.isFinite(n) ? n : null
}

// A TOTAL order over rows — never returns 0 for two different companies.
//
// This matters more than it looks. Array#sort is only guaranteed stable for
// the input order, and the input order here is "whichever region's fetch
// resolved first", which is a race. Without a total order the same directory
// could hand back a different 50 on every run, and "deterministic" would be a
// lie. Score desc, then name, then company key — the key is unique, so the
// chain always terminates in a decision.
//
// Comparison is by code unit (< / >), not localeCompare: locale collation
// varies by browser and OS, and this ordering has to be reproducible off this
// machine (that is what makes it unit-testable).
// ── Bo's ICP hard caps, applied at ranking time ─────────────────────────────
//
// SCORING_RUBRIC.md on the openclaw box is unambiguous about who is NOT the
// buyer, and it caps them by score rather than arguing about them:
//
//   "General contractor -> 1 (all the way down). ANY 'general contractor' in
//    the trade list."
//   "Residential-only -> 2. Not worth it (commercial is the buyer)."
//   "...core masonry (NOT a general contractor / landscaper / concrete shop /
//    greenhouse)"
//
// The imported directory carries its own score from a different rubric, so
// those caps were never applied here: a run of the top 50 by score held two
// General Contractors at 8 and 7, a painting contractor, a demolition firm, a
// consulting engineer and a building-materials supplier — all ahead of real
// masonry shops further down the list. This is the rubric, in code.
//
// It DOWN-RANKS, it does not drop: the lead stays visible and the receipt says
// how many were capped and why. Dropping a row on a trade string would hide a
// mislabelled masonry contractor, and the directory's trade column is exactly
// the kind of field that carries a stale label.
const GC_TRADE = /\bgeneral contractor/i
const NON_CORE_TRADE =
  /\b(landscap|greenhouse|nursery|paint|demolition|building materials|supplier|engineers?--consulting|consulting engineer|construction management|tenant fit|cleaning|janitorial|roofing|plumbing|electrical|hvac)/i
const RESIDENTIAL_ONLY = /residential/i
const COMMERCIAL_ANY = /commercial|institutional|industrial/i

/**
 * The cap Bo's rubric puts on this row, or null when it is core masonry.
 * @returns {{cap: number, why: string}|null}
 */
export function icpCapOf(row) {
  const trade = String(row?.trade ?? row?.trades ?? '')
  if (GC_TRADE.test(trade)) {
    return { cap: 1, why: 'a general contractor — the rubric caps any GC at 1, they buy nothing per estimator seat' }
  }
  const cr = String(row?.commercial_residential ?? row?.commercialResidential ?? '')
  if (cr && RESIDENTIAL_ONLY.test(cr) && !COMMERCIAL_ANY.test(cr)) {
    return { cap: 2, why: 'residential-only — the rubric caps these at 2, commercial is the buyer' }
  }
  if (trade && NON_CORE_TRADE.test(trade) && !/mason|brick|block|stone|cmu|veneer/i.test(trade)) {
    return { cap: 2, why: `not core masonry (${trade.split(';')[0].trim()}) — the rubric wants brick, block and stone shops` }
  }
  return null
}

/** The score a row RANKS on: its own, held under Bo's cap when one applies. */
export function rankScoreOf(row) {
  const raw = scoreOf(row)
  const capped = icpCapOf(row)
  if (raw === null) return null
  return capped ? Math.min(raw, capped.cap) : raw
}

// Decision makers first, exactly as the prospect skill ranks them: "1. Decision
// makers (estimators/VPs/owners)." A tie on score used to fall straight to
// alphabetical, which put a receptionist ahead of a chief estimator at the same
// company score.
const DM_TITLE = /\b(estimator|owner|president|principal|founder|vp|vice president|chief|director|precon|preconstruction|partner|ceo|coo|cfo|gm|general manager)\b/i
function dmRank(row) {
  const role = String(row?.contact_role ?? row?.contactRole ?? '')
  if (DM_TITLE.test(role)) return 0
  const hasEstimator = String(row?.has_estimator ?? row?.hasEstimator ?? '')
  if (/^yes/i.test(hasEstimator)) return 1
  return 2
}

export function compareCandidates(a, b) {
  const sa = rankScoreOf(a)
  const sb = rankScoreOf(b)
  // Unscored rows sort last, whatever the scored ones look like.
  if (sa === null && sb !== null) return 1
  if (sb === null && sa !== null) return -1
  if (sa !== null && sb !== null && sa !== sb) return sb - sa
  // Same score band -> the decision maker goes first (prospect skill, ranking
  // rule 1), before the alphabet gets a say.
  const da = dmRank(a)
  const db = dmRank(b)
  if (da !== db) return da - db
  const na = String(a?.name || '')
  const nb = String(b?.name || '')
  if (na !== nb) return na < nb ? -1 : 1
  const ka = String(keyOf(a) || '')
  const kb = String(keyOf(b) || '')
  return ka < kb ? -1 : ka > kb ? 1 : 0
}

// Best leads first. Copies rather than sorting in place — the caller's array
// belongs to React state upstream and must not be mutated.
export function rankRows(rows) {
  return [...(rows || [])].sort(compareCandidates)
}

// The rubric's priority bands (SCORING_RUBRIC.md): score 8–10 = P1, 6–7 = P2,
// 1–5 = P3. An unscored lead is P3 — unmeasured is not promising. Display and
// trace only; the actual ordering is compareCandidates, which the bands are a
// monotone function of, so the two can never disagree.
export function priorityOf(row) {
  const s = scoreOf(row)
  if (s === null) return 'P3'
  if (s >= 8) return 'P1'
  if (s >= 6) return 'P2'
  return 'P3'
}

// ── concurrency ─────────────────────────────────────────────────────────────
// The React page guards with component state, but that guard dies with the
// component: navigating away mid-run unmounts Chat, and the remount starts
// clean — so a second run could begin while the first was still going, claim
// the same 50 leads, and spend research credits on them twice. This lock lives
// in the module, so it outlives any component and holds for direct callers too.
//
// It is ONE lock for ALL THREE operations, not one per operation. Every
// operation persists the same thing at its cut — the pinned batch (savePin,
// lib/batchPin.js), a read-modify-write over localStorage with no locking of
// its own — so two runs interleaving there lose a pin and the next run stops
// repeating yesterday's list. One run at a time, full stop.
let inFlight = false

export function isRunInFlight() {
  return inFlight
}

// ── abort plumbing ──────────────────────────────────────────────────────────

function abortError() {
  const err = new Error('Run cancelled')
  err.name = 'AbortError'
  return err
}

// ── research awaits are raced, never trusted ────────────────────────────────
// The budget and the abort signal used to be checked only BETWEEN awaits, so
// one enrich fetch that never settled (a hung connection) wedged the research
// node — and with it the module-wide run lock — until the browser's network
// stack gave up, with Cancel apparently dead the whole time. Every research
// await now goes through raceResearch: abort rejects immediately (and the
// enrich fetches carry the signal, so the request itself is torn down), and
// the node's hard deadline rejects with ResearchBudgetError — the run stops
// WAITING even if some socket underneath never answers, and the leads still
// owed an answer fall to the honest timeout admission.
function budgetError() {
  const err = new Error('research budget expired')
  err.name = 'ResearchBudgetError'
  return err
}

function raceResearch(promise, { signal, deadline }) {
  if (signal?.aborted) return Promise.reject(abortError())
  return new Promise((resolve, reject) => {
    let settled = false
    let timer = null
    const finish = (fn, value) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      signal?.removeEventListener?.('abort', onAbort)
      fn(value)
    }
    const onAbort = () => finish(reject, abortError())
    if (Number.isFinite(deadline)) {
      timer = setTimeout(() => finish(reject, budgetError()), Math.max(0, deadline - Date.now()))
    }
    signal?.addEventListener?.('abort', onAbort, { once: true })
    Promise.resolve(promise).then(
      (value) => finish(resolve, value),
      (err) => finish(reject, err),
    )
  })
}

function beat(signal, ms = BEAT_MS) {
  if (signal?.aborted) return Promise.reject(abortError())
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener?.('abort', onAbort)
      resolve()
    }, ms)
    function onAbort() {
      clearTimeout(timer)
      reject(abortError())
    }
    signal?.addEventListener?.('abort', onAbort, { once: true })
  })
}

// ── per-lead provenance trace ───────────────────────────────────────────────
// The flow view is a PROVENANCE view, not a run animation: click one lead and it
// has to say which nodes judged that lead and what each one decided. That answer
// may never be re-derived after the fact — a second guess made from the finished
// result can contradict the run (the CRM answers differently a minute later, the
// history read that was degraded now succeeds). So every node records its own
// verdict AT THE MOMENT IT DECIDES, and a node that never saw a lead writes
// nothing for it: a lead dropped at `reachable` has no entry for anything
// downstream, because nothing downstream ever looked at it.
//
//   trace[leadKey] = [{ nodeId, verdict, note }, …]   in run order
//
// leadKey is keyOf() — the same key CompanyTable.jsx uses for a row
// — so the UI looks a lead up with the key it already holds, no translation.
//
// The three verdicts mean different things and must not be collapsed:
//   'kept'    — this node judged the lead and it carried on.
//   'dropped' — this node is the reason the lead is not in the output.
//   'skipped' — this node could NOT judge it (a system was unreadable, or the
//               lead sat outside a bounded window) and left it alone. An
//               admission, never a finding.
//
// The note is one short sentence a person can read without the code in front of
// them, and it carries the evidence (the address, the company id, the count).
const TRACE_VERDICTS = new Set(['kept', 'dropped', 'skipped'])

// A run can be handed the whole directory — thousands of rows, each judged by
// up to ten nodes — and the result is persisted with the chat thread, where
// every past run's result shares one localStorage budget with the messages. So
// the snapshot keeps every lead that reached the OUTPUT (those are the rows the
// result panel makes clickable, and they must always answer) plus the
// highest-ranked TRACE_LEAD_LIMIT of the rest, and REPORTS what it left out in
// `traceOmitted` rather than truncating silently. 80 leads × ~9 entries is well
// under 100KB — a full 50-lead batch with room for the near-misses above it.
// How many real messages of a thread travel with a lead: enough for a rep to
// see the shape of the conversation ("we wrote three times, they answered
// once, that one bounced") without turning a receipt into a mailbox.
const HISTORY_MESSAGE_MAX = 6

const TRACE_LEAD_LIMIT = 80

function makeTracer() {
  const entries = new Map() // leadKey -> [{ nodeId, verdict, note }]
  // Insertion order, kept separately: object key order is not reliable for
  // numeric-looking keys, and company_id is often a number in a string.
  const order = []

  function note(key, nodeId, verdict, text) {
    const k = String(key || '')
    if (!k || !nodeId || !TRACE_VERDICTS.has(verdict)) return
    let list = entries.get(k)
    if (!list) {
      list = []
      entries.set(k, list)
      order.push(k)
    }
    // One verdict per node per lead. A node that sees the same lead twice (two
    // directory rows for one company, a lead with two open threads) decided
    // once; the first decision is the one it acted on.
    if (list.some((e) => e.nodeId === nodeId)) return
    list.push({ nodeId, verdict, note: String(text || '') })
  }

  // `keep` is whatever ended up in the output — those leads always keep their
  // full trace, because they are the rows the result panel makes clickable.
  function snapshot(keep = []) {
    const kept = new Set()
    for (const row of keep || []) {
      const k = row ? String(keyOf(row) || '') : ''
      if (k && entries.has(k)) kept.add(k)
    }
    for (const k of order) {
      if (kept.size >= TRACE_LEAD_LIMIT) break
      kept.add(k)
    }
    const trace = {}
    let traceOmitted = 0
    for (const k of order) {
      if (!kept.has(k)) {
        traceOmitted += 1
        continue
      }
      // At a fork (the follow-up's freeze ‖ excl) two nodes judge the same
      // lead at the same moment, so a lead dropped in one lane can honestly
      // collect a 'kept' from its sibling — in whichever order the network
      // settled. A drop is terminal in every one of these pipelines (a dropped
      // lead is never judged downstream), so the snapshot orders any 'dropped'
      // verdicts last: each per-node verdict is untouched, and the LAST entry
      // of a lead's story always tells whether it survived to the output.
      const list = entries.get(k)
      const settled = [
        ...list.filter((e) => e.verdict !== 'dropped'),
        ...list.filter((e) => e.verdict === 'dropped'),
      ]
      trace[k] = settled.map((e) => ({ ...e }))
    }
    return { trace, traceOmitted }
  }

  return { note, snapshot }
}

// ── the pipeline ────────────────────────────────────────────────────────────

// Every pass of one operation, status 'pending' — what the UI paints before the
// run starts. Synchronous by contract: it is called during render, so it may
// never read the network and never awaits anything.
export function initialPasses(operationId = OPERATIONS[0].id) {
  if (!getOperation(operationId)) return []
  return (PASS_DEFS[operationId] || []).map((def) => ({
    ...def,
    // Where this node's code lives — see NODE_EXEC. Undefined for a node the
    // table does not describe, and the receipt then stores no claim about it.
    ...(NODE_EXEC[operationId]?.[def.id] ? { exec: NODE_EXEC[operationId][def.id] } : {}),
    // Copied, not shared: these arrays end up on React state and are handed to
    // the canvas, which must never be able to mutate the module's own graph.
    dependsOn: [...(def.dependsOn || [])],
    lane: typeof def.lane === 'number' ? def.lane : null,
    status: 'pending',
    headline: '',
    detail: [],
    metric: null,
    startedAt: null,
    endedAt: null,
  }))
}

// The edges, for anything that wants the shape without the runtime state (a
// static diagram, a test, a layout pass). The nodes returned by initialPasses()
// and by onPass carry `dependsOn` themselves — this is the same data, minus the
// status fields, for callers that only want the topology.
export function operationGraph(operationId = OPERATIONS[0].id) {
  return initialPasses(operationId).map(({ id, title, subtitle, dependsOn, lane }) => ({
    id,
    title,
    subtitle,
    dependsOn,
    lane,
  }))
}

/**
 * Run an operation end to end.
 *
 * @param {object}   opts
 * @param {string}   opts.operationId  one of OPERATIONS[].id
 * @param {object[]} opts.rows         company rows already loaded by the app
 * @param {function} opts.onPass       (index, pass) on every status transition
 * @param {AbortSignal} [opts.signal]  cancels between/inside beats (AbortError)
 * @param {Date}     [opts.now]        clock, injectable so runs are reproducible
 * @param {string}   [opts.category]   list category key; defaults to the row's
 *                                     own `_category` (set by lib/data.jsx)
 * @returns {Promise<{ok, passes, selected, trace, traceOmitted}>}
 *   `selected` is what the run put in the output; `trace` is the per-lead
 *   provenance the nodes recorded (leadKey -> [{nodeId, verdict, note}], keyed
 *   by keyOf()), and `traceOmitted` is how many leads' traces were shed to keep
 *   the result small. Both are present on abandoned runs too.
 */
export async function runOperation(opts = {}) {
  // Refuse rather than queue: a caller asking for a second concurrent run wants
  // THIS run's leads, and by the time the first finished they would be claimed.
  // Surfacing the refusal lets the UI point at the run already going.
  if (inFlight) {
    const err = new Error('A run is already in progress')
    err.name = 'RunInFlightError'
    throw err
  }
  inFlight = true
  try {
    return await _runOperation(opts)
  } finally {
    // finally, not a release at each return: the pipeline exits through many
    // abandon() paths, a success return and an abort throw. A leaked lock would
    // wedge the feature until reload, which is worse than the bug it prevents.
    inFlight = false
  }
}

// The scaffolding every operation shares: the node array, the three state
// transitions the UI is driven by, and the abandon contract.
function makeRun(operationId, onPass, signal) {
  const passes = initialPasses(operationId)
  const indexOf = new Map(passes.map((p, i) => [p.id, i]))
  // One tracer per run, shared by every node: the per-lead provenance record.
  const tracer = makeTracer()
  const emit = (i) => onPass?.(i, passes[i])

  // WHO WE ARE, read ONCE per run (see readOwnOrg). Memoised as the PROMISE
  // rather than the value, so two nodes asking in the same tick — the graph
  // really does run siblings together — share one read instead of racing to
  // two, and so the answer cannot change halfway through a run.
  let ownOrgRead = null
  const ownOrg = () => {
    if (!ownOrgRead) ownOrgRead = readOwnOrg({ signal })
    return ownOrgRead
  }

  async function start(i) {
    passes[i] = { ...passes[i], status: 'running', startedAt: Date.now() }
    emit(i)
    await beat(signal)
  }

  function finish(i, status, headline, detail = [], metric = null) {
    passes[i] = {
      ...passes[i],
      status,
      headline,
      // 0–4 short lines; anything longer belongs on the list page, not here.
      detail: detail.filter(Boolean).slice(0, 4),
      metric,
      endedAt: Date.now(),
    }
    emit(i)
  }

  // A node that leaves nothing to work with ends the run. Every node that had
  // not started is then marked 'skipped' (it never got a chance), and the
  // result carries an empty selection. An empty run must look empty, not
  // half-successful.
  //
  // `status` is 'failed' when something went wrong and 'skipped' when nothing
  // was wrong and there was simply nothing to do (no follow-up is due; the
  // inbox is not connected). Both end the run; only one of them is a fault.
  //
  // IT THROWS rather than returning, which is what makes it work inside a
  // graph: every call site is still `return abandon(...)`, but the throw also
  // unwinds this node's own branch, tells the scheduler to stop launching, and
  // — crucially — leaves the marking of unreached nodes until AFTER every
  // sibling already in flight has settled. A parallel sibling that finished
  // 'done' before this node gave up keeps its result; only nodes that never
  // started are marked. See markUnreached().
  function abandon(i, status, headline, detail, result = {}) {
    finish(i, status, headline, detail)
    const err = new Error(headline || 'Run abandoned')
    err.name = 'RunAbandoned'
    const merged = {
      // `ok:false` is load-bearing, not decoration. The caller renders the
      // "Run complete — here's what it built" panel off the presence of a
      // result object, so an abandoned run that returned a bare object would
      // paint a success panel over empty fields. Callers must branch on this.
      ok: false,
      passes,
      selected: [],
      ...result,
    }
    err.abandon = {
      index: i,
      // An abandoned run still has provenance, and it is the run people ask the
      // most questions about: whatever the nodes that DID run decided about each
      // lead is on the result, exactly as they recorded it.
      result: { ...merged, ...tracer.snapshot(merged.selected) },
    }
    throw err
  }

  // Everything that never got to run, once the graph has stopped. Only 'pending'
  // nodes are touched: a node that already settled — including a parallel
  // sibling that succeeded before its cousin gave up — keeps exactly what it
  // reported. `from` is the node that abandoned; its transitive dependents are
  // marked first so the emission order reads down the branch that died.
  function markUnreached(from) {
    const order = [...downstreamOf(passes, from), ...passes.map((_, i) => i)]
    const seen = new Set()
    for (const j of order) {
      if (seen.has(j) || j === from) continue
      seen.add(j)
      if (passes[j].status !== 'pending') continue
      passes[j] = {
        ...passes[j],
        status: 'skipped',
        // Distinct from a pass that legitimately had nothing to do: this one
        // never ran. The view must not count it as progress, or a run that
        // died on step 1 announces "6 of 6 steps" to a screen reader.
        abandoned: true,
        headline: 'Not reached — the run stopped earlier',
        endedAt: Date.now(),
      }
      emit(j)
    }
  }

  // A cancelled run must not leave a node frozen mid-flight. The abort unwinds
  // through beat()'s rejection, so the node that was in the middle of a beat is
  // still 'running' in the array the UI holds — it gets one last transition,
  // and pending nodes stay pending because they truly never began.
  function markCancelled() {
    for (let j = 0; j < passes.length; j += 1) {
      if (passes[j].status !== 'running') continue
      passes[j] = {
        ...passes[j],
        status: 'skipped',
        abandoned: true,
        headline: 'Cancelled — the run was stopped',
        endedAt: Date.now(),
      }
      emit(j)
    }
  }

  return {
    passes,
    indexOf,
    emit,
    start,
    finish,
    abandon,
    markUnreached,
    markCancelled,
    signal,
    tracer,
    ownOrg,
  }
}

// Every node that depends on `from`, directly or through another node, in
// breadth-first order.
function downstreamOf(passes, from) {
  const out = []
  const seen = new Set([from])
  const queue = [from]
  while (queue.length) {
    const current = queue.shift()
    const id = passes[current].id
    passes.forEach((p, j) => {
      if (seen.has(j) || !p.dependsOn?.includes(id)) return
      seen.add(j)
      out.push(j)
      queue.push(j)
    })
  }
  return out
}

// ── the scheduler ───────────────────────────────────────────────────────────
// Dependency-driven, not wave-driven: each node awaits ITS OWN dependencies and
// nothing else, so siblings that name the same dependencies both start in the
// tick that dependency settles and are genuinely in flight together. There is
// no stagger, no sleep-then-pretend — hs-deals and hs-tasks overlap because
// both are real promises started before either is awaited.
//
// `handlers` maps node id -> async () => void; a handler drives its own node
// through start()/finish() and abandons by throwing (see abandon above).
// `finalize` builds the success result once every node has settled.
async function runGraph(run, handlers, finalize) {
  const { passes, indexOf, signal, markUnreached, markCancelled } = run
  const launched = new Map()
  // The first thing that ended the run: an abort beats an abandon (the user
  // asked for it), and among abandons the earliest node in the array wins, so
  // the result the caller sees is the one furthest upstream.
  let stop = null

  function note(err) {
    if (err?.name === 'AbortError') {
      stop = { kind: 'abort', err }
      return
    }
    if (err?.abandon) {
      if (!stop) stop = { kind: 'abandon', ...err.abandon }
      else if (stop.kind === 'abandon' && err.abandon.index < stop.index) {
        stop = { kind: 'abandon', ...err.abandon }
      }
      return
    }
    // Anything else is a bug, not a contract: keep it so runOperation can
    // rethrow it to the caller's own error handling.
    if (!stop || stop.kind === 'abandon') stop = { kind: 'error', err }
  }

  function runNode(i) {
    if (!launched.has(i)) {
      launched.set(
        i,
        (async () => {
          const deps = passes[i].dependsOn
            .map((id) => indexOf.get(id))
            .filter((d) => d !== undefined)
          // allSettled: a dependency that abandoned has already been recorded,
          // and this node simply never starts. Its rejection is still visible
          // at the top level, so nothing is swallowed.
          const settled = await Promise.allSettled(deps.map((d) => runNode(d)))
          if (stop || settled.some((s) => s.status === 'rejected')) return
          // Inside the try, not above it: a signal that was aborted between the
          // launch and this line has to be RECORDED as the reason the run
          // stopped, or the graph finishes with unsettled nodes and finalize()
          // reads fields no node ever wrote.
          try {
            if (signal?.aborted) throw abortError()
            const handler = handlers[passes[i].id]
            if (!handler) return
            await handler()
          } catch (err) {
            note(err)
            throw err
          }
        })(),
      )
    }
    return launched.get(i)
  }

  const outcomes = await Promise.allSettled(passes.map((_, i) => runNode(i)))
  // Belt and braces: nothing may reach finalize() unnoticed. Every rejection is
  // already recorded by the node that raised it, and re-noting is idempotent —
  // this is here so a future edit that throws outside the try still ends the run
  // honestly instead of building a result out of half-run nodes.
  for (const outcome of outcomes) {
    if (outcome.status === 'rejected') note(outcome.reason)
  }
  if (!stop && passes.some((p) => p.status === 'pending' || p.status === 'running')) {
    stop = { kind: 'error', err: new Error('Run ended with unsettled nodes') }
  }

  if (stop?.kind === 'abort') {
    markCancelled()
    throw stop.err
  }
  if (stop?.kind === 'error') {
    markCancelled()
    throw stop.err
  }
  if (stop?.kind === 'abandon') {
    markUnreached(stop.index)
    return stop.result
  }
  return finalize()
}

async function _runOperation({
  operationId = OPERATIONS[0].id,
  rows = [],
  onPass,
  signal,
  now = new Date(),
  category,
} = {}) {
  const op = getOperation(operationId)
  if (!op) throw new Error(`Unknown operation: ${operationId}`)
  if (signal?.aborted) throw abortError()

  const run = makeRun(operationId, onPass, signal)
  const env = { rows, now, category }

  if (op.id === 'followup-50') return runDailyTasks(op, run, env)
  if (op.id === 'reply-open-threads') return runFollowUp(op, run, env)
  return runInitialOutreach(op, run, env)
}

// ── shared: the outbound history (operation 1, and Follow-Up’s history node) ──
// Everything here is a read: the activity log for outbound touches, the
// HubSpot mirror’s email engagements for Bo’s real sends and replies. No
// helper in this section writes anything anywhere.

// Matches every address in a string; used with String#match, which with a /g
// regex returns all matches and ignores lastIndex, so the module-level literal
// is safe to share.
const ADDR_ALL_RE = /[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}/gi

// One page of the activity log, same bound knownRecipients() reads. The pass
// that reads it says the window out loud rather than implying a full history.
// 200 is the server's hard page cap (and listEvents() clamps to it); asking
// for more would be silently shrunk, and the receipt reports the count that
// actually came back, never this constant.
const HISTORY_EVENT_LIMIT = 200

// YYYY-MM-DD on the America/Chicago calendar — the day key touches dedup on.
const CHI_DAY_FMT = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Chicago',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
})
// "Aug 7" on the same calendar — the date a trace note shows a person.
const CHI_SHORT_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/Chicago',
  month: 'short',
  day: 'numeric',
})

function chicagoDayOf(date) {
  try {
    return CHI_DAY_FMT.format(date)
  } catch {
    return ''
  }
}

function shortDay(date) {
  const d = date instanceof Date ? date : new Date(date)
  if (Number.isNaN(d.getTime())) return ''
  try {
    return CHI_SHORT_FMT.format(d)
  } catch {
    return ''
  }
}

// A Chicago calendar day ('2026-09-28') as the same short label. Not
// shortDay(day): `new Date('2026-09-28')` is UTC midnight, which is the
// evening BEFORE in Chicago, so the label would come out a day early. Noon
// UTC falls on the same calendar day in Chicago all year round.
function chicagoDayLabel(day) {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(day || '')) ? shortDay(`${day}T12:00:00Z`) : ''
}

/**
 * Outbound touch history per recipient, read from the workspace activity log —
 * the same `email_sent` events (GET /api/events) the 'contacted' node of
 * initial-outreach screens against, but keeping WHEN, not just WHO.
 *
 * TOUCH DEDUP: sales_state.py collapses outbound events into touches keyed by
 * (10-minute bucket, normalized subject) before counting. The activity log
 * here does not reliably carry a subject line, so this approximates that rule
 * with the coarser one it CAN compute honestly: all outbound events to the
 * same recipient on the same America/Chicago calendar day count as ONE touch.
 * Coarser only ever undercounts — it can repeat an email step a day early in
 * pathological logs, never invent an eighth touch.
 *
 * Never throws: listEvents() falls back to the local mirror, and `mode` says
 * which read actually answered so the pass can downgrade its claim.
 */
async function readOutboundHistory() {
  let events = []
  let mode = 'unavailable'
  try {
    const res = await listEvents({ limit: HISTORY_EVENT_LIMIT })
    events = Array.isArray(res?.events) ? res.events : []
    mode = res?.mode === 'api' ? 'api' : 'local'
  } catch {
    events = []
    mode = 'unavailable'
  }
  const byAddress = new Map() // addr -> { days:Set<dayKey>, lastTouchAt:Date }
  let sendEvents = 0
  let unstamped = 0
  for (const e of events) {
    if (e.type !== 'email_sent') continue
    // "Sent “subject” to name@company.com" — the subject can itself contain an
    // address, so the LAST one is the recipient (same rule as
    // knownRecipients()). Older rows carry a trailing "(Campaign)" from a send
    // path that no longer exists; taking the last address reads those too.
    const found = String(e.detail || '').match(ADDR_ALL_RE)
    if (!found || !found.length) continue
    const addr = found[found.length - 1].toLowerCase()
    const at = new Date(e.createdAt || '')
    if (Number.isNaN(at.getTime())) {
      // A send with no readable timestamp cannot be wait-gated. It is counted
      // and reported, never guessed at.
      unstamped += 1
      continue
    }
    sendEvents += 1
    let rec = byAddress.get(addr)
    if (!rec) {
      rec = { days: new Set(), lastTouchAt: at, lastSubject: '' }
      byAddress.set(addr, rec)
    }
    rec.days.add(chicagoDayOf(at))
    if (at.getTime() >= rec.lastTouchAt.getTime()) {
      rec.lastTouchAt = at
      // The log's own phrasing: Sent “subject” to addr. The subject is quoted,
      // so it is READ, never reconstructed — a detail line in another shape
      // simply leaves lastSubject empty.
      const quoted = String(e.detail || '').match(/[“"]([^”"]{1,120})[”"]/)
      rec.lastSubject = quoted ? quoted[1].trim() : ''
    }
  }
  return { byAddress, mode, sendEvents, unstamped, eventsRead: events.length }
}

// ── WHO WE ARE: the addresses this run refuses to write to ──────────────────
//
// THE BUG THIS FIXES. The imported masonry row "Chicago Masonry Construction"
// is Revailo's SISTER COMPANY, and its packed contacts blob carries our own
// founders — "Jacob Willner - Co-Founder - jacob@revailo.com" and
// "Yishai Broner - Founder - yishai@revailo.com". Nothing in the pipeline
// refused to write to our own addresses, so emailOf() picked jacob@revailo.com
// as that lead's recipient and a follow-up draft went out opening "Hi Jacob,"
// pitching Revailo to Revailo's co-founder.
//
// WHERE "OURS" COMES FROM. Four sources, and the EXPLICIT ones are preferred
// over the statistical one:
//   1. the signed-in session (lib/userScope.js) — whoever is running this,
//   2. the team roster (lib/team.js) — everyone invited to this workspace,
//   3. the CRM's owner list (lib/hubspot.js) — everyone who owns records in
//      the portal, which is how @revailo.ai is known even though this
//      workspace signs in on @revailo.com,
//   4. readMailHistory()'s own derivation — the domains the mirror shows
//      sending outbound and never writing in. Folded in last, by
//      mergeOwnOrg(), only on the operations that read the mirror.
//
// A DOMAIN, NOT JUST AN ADDRESS. Every corporate domain one of these sources
// names is ours, so a teammate who has never sent a tracked email is still
// refused. Freemail hosts never become "our domain" — a rep signed in on a
// gmail.com address must not put every gmail.com lead in the directory out of
// reach.
//
// A SOURCE THAT FAILS IS ADMITTED, NEVER ASSUMED EMPTY. Each source reports
// { ok, count, reason }; the nodes that use the set print the gaps. "We could
// not read the roster" and "the roster is empty" are different facts, and the
// second one would quietly turn this guard off.

const OWN_ORG_SOURCES = {
  session: 'the signed-in session',
  roster: 'the team roster',
  owners: 'the CRM owner list',
  mirror: 'the HubSpot mirror’s own derivation',
}

/**
 * Fold one source's addresses into an own-org set under construction.
 * Corporate domains are learned from the addresses themselves; freemail hosts
 * are added as EXACT addresses only.
 */
function addOwnAddresses(org, addresses) {
  let added = 0
  for (const raw of addresses || []) {
    const a = String(raw || '').trim().toLowerCase()
    if (!a.includes('@')) continue
    if (!org.addresses.has(a)) added += 1
    org.addresses.add(a)
    const d = mailDomainOf(a)
    if (d && !isFreemailDomain(d)) org.domains.add(d)
  }
  return added
}

/**
 * Build the own-org set from source payloads that were already read. PURE, so
 * the rule can be tested without a network — readOwnOrg() below does the
 * reading and hands the results straight here.
 *
 * @param {object} input
 * @param {string} [input.self]      the signed-in address ('anon' or '' when nobody is)
 * @param {object|null} [input.roster]  lib/team.js listMembers() result, or null when it threw
 * @param {object|null} [input.owners]  lib/hubspot.js hubspotOwners() result, or null when it threw
 * @returns {{addresses:Set<string>, domains:Set<string>,
 *   sources:Array<{id:string,label:string,ok:boolean,count:number,reason:string}>,
 *   gaps:string[]}}
 */
export function buildOwnOrg({ self = '', roster = null, owners = null } = {}) {
  const org = { addresses: new Set(), domains: new Set(), sources: [], gaps: [] }
  const source = (id, ok, count, reason) =>
    org.sources.push({ id, label: OWN_ORG_SOURCES[id] || id, ok, count, reason: reason || '' })

  // 1. the signed-in session. 'anon' is userScope's signed-out sentinel, not an
  //    address — treating it as one would put nothing in the set while
  //    reporting a source that answered.
  const me = String(self || '').trim().toLowerCase()
  if (me.includes('@')) {
    source('session', true, addOwnAddresses(org, [me]), '')
  } else {
    source('session', false, 0, 'nobody is signed in in this browser')
  }

  // 2. the roster. listMembers() never throws — it falls back to this
  //    browser's local copy — so 'api' is the only mode that proves the
  //    workspace answered. Whatever a local copy holds is still real and still
  //    counted; the source is simply reported as unverified.
  if (!roster) {
    source('roster', false, 0, 'the team roster could not be read')
    org.gaps.push('The team roster could not be read, so anyone who is only on the roster is not recognised as one of us.')
  } else {
    const added = addOwnAddresses(org, (roster.members || []).map((m) => m?.email))
    if (roster.mode === 'api') {
      source('roster', true, added, '')
    } else {
      source('roster', false, added, 'only this browser’s local copy of the roster answered')
      org.gaps.push('The team roster came only from this browser’s local copy, so a teammate invited elsewhere may not be recognised as one of us.')
    }
  }

  // 3. the CRM's owners. hubspotOwners() resolves { available:false, reason }
  //    for every failure mode rather than throwing.
  if (!owners || owners.available !== true) {
    source('owners', false, 0, owners?.reason || 'the CRM owner list could not be read')
    org.gaps.push(`The CRM owner list could not be read (${String(owners?.reason || 'no reason given').slice(0, 70)}), so an address known only to HubSpot is not recognised as one of us.`)
  } else {
    source('owners', true, addOwnAddresses(org, (owners.owners || []).map((o) => o?.email)), '')
  }

  return org
}

/**
 * Read the own-org set. Called ONCE per run — see makeRun()'s `ownOrg` memo —
 * and never rejects: each source is caught into an honest failure shape and
 * reported by buildOwnOrg().
 */
async function readOwnOrg({ signal } = {}) {
  const [roster, owners] = await Promise.all([
    listMembers().catch(() => null),
    hubspotOwners().catch((err) => ({ available: false, reason: err?.message || '', owners: [] })),
  ])
  if (signal?.aborted) throw abortError()
  const org = buildOwnOrg({ self: currentScope(), roster, owners })
  // The raw reads ride along for the lane that needs WHO, not just which
  // addresses are ours: the task lane resolves the roster's reps to owners.
  org.roster = roster
  org.owners = owners
  return org
}

/**
 * Is this address one of OURS? Exact address first, then the domain — a
 * teammate on a corporate domain we own is ours whether or not any source
 * named them individually.
 */
export function isOwnAddress(address, org) {
  const a = String(address || '').trim().toLowerCase()
  if (!a || !org) return false
  if (org.addresses instanceof Set && org.addresses.has(a)) return true
  const d = mailDomainOf(a)
  return Boolean(d && org.domains instanceof Set && org.domains.has(d))
}

/**
 * The own-org set with the HubSpot mirror's own derivation folded in. Used by
 * the operations that read the mirror; the explicit sources always win, in the
 * sense that they are already in the set before this adds anything.
 */
function mergeOwnOrg(base, mail) {
  const org = {
    addresses: new Set(base?.addresses || []),
    domains: new Set(base?.domains || []),
    sources: [...(base?.sources || [])],
    gaps: [...(base?.gaps || [])],
  }
  if (mail?.available) {
    const added = addOwnAddresses(org, mail.ourAddresses || [])
    for (const raw of mail.ourDomains || []) {
      const d = String(raw || '').trim().toLowerCase()
      if (d && !isFreemailDomain(d)) org.domains.add(d)
    }
    org.sources.push({ id: 'mirror', label: OWN_ORG_SOURCES.mirror, ok: true, count: added, reason: '' })
  } else {
    org.sources.push({
      id: 'mirror',
      label: OWN_ORG_SOURCES.mirror,
      ok: false,
      count: 0,
      reason: mail?.reason || 'the HubSpot mirror could not be read',
    })
    org.gaps.push('The HubSpot mirror could not be read, so the addresses it would have shown as our own were not added.')
  }
  return org
}

/**
 * One line naming which sources decided who "we" are and how many addresses
 * each contributed, plus one line per gap. Printed by every node that refuses
 * an address on this basis, because a refusal nobody can check is just a
 * disappearance.
 */
function ownOrgLines(org) {
  if (!org) return []
  const ok = (org.sources || []).filter((s) => s.ok)
  const bad = (org.sources || []).filter((s) => !s.ok)
  const lines = []
  lines.push(
    ok.length
      ? `“Ours” is ${fmt(org.addresses.size)} ${plural(org.addresses.size, 'address', 'addresses')} and ${fmt(org.domains.size)} ${plural(org.domains.size, 'domain', 'domains')}, read this run from ${ok.map((s) => `${s.label} (${fmt(s.count)})`).join(', ')} — derived from the workspace, never hardcoded.`
      : 'No source could tell us which addresses are our own, so nothing was refused on that basis — that is an admission, not a clean bill of health.',
  )
  if (bad.length) {
    lines.push(
      `Not read: ${bad.map((s) => `${s.label} (${s.reason || 'no reason given'})`).join('; ')}. An address only those sources knew about would NOT have been recognised as ours.`,
    )
  }
  return lines
}

// ── the REAL send history: HubSpot email engagements ────────────────────────
//
// THE BUG THIS FIXES. readOutboundHistory() above reads `email_sent` events
// this app writes when it sends — and this app never sends. Bo sends from
// HubSpot. So the log was empty, and the follow-up operation answered "No
// outbound send in the activity log" while Bo's real outbound and his
// prospects' real replies sat in the mirror the whole time.
//
// The mirror is now the PRIMARY source and the activity log is an ADDITIONAL
// one: a workspace that does start sending from this app keeps its own log
// counted, and the two are UNIONED on Chicago calendar days so a send that
// somehow appears in both is one touch, never two.

/**
 * Merge the HubSpot mail history with the workspace activity log into the one
 * `byAddress` map the rest of the cadence runs on.
 *
 * Each record carries what the card needs on top of the arithmetic:
 * lastSubject / lastSnippet (our last outbound) and the reply (replySubject,
 * replySnippet, replyAt) when they wrote back.
 *
 * `related:false` records are KEPT in the map and flagged, not dropped — the
 * match node reports them and writes the reason into the lead's trace, because
 * "we excluded this and here is why" is a fact a rep is owed.
 */
async function readCadenceHistory({ signal, now, ours = null } = {}) {
  const [mail, log, sent] = await Promise.all([
    readMailHistory({ signal, now, sharedMailHost: isFreemailDomain }).catch((err) => {
      if (err?.name === 'AbortError') throw err
      return { available: false, reason: err?.message || '', threads: new Map() }
    }),
    readOutboundHistory(),
    // THE REP'S OWN SENT FOLDER. HubSpot only knows the emails somebody logged
    // to it; Bo sends plenty straight from Gmail. Without this lane a company
    // he emailed personally reads as never contacted, and the cadence offers
    // him a cold intro for it.
    readSentRecipients(signal).catch((err) => {
      if (err?.name === 'AbortError') throw err
      return { checked: false, reason: err?.message || 'the sent folder could not be read' }
    }),
  ])
  if (signal?.aborted) throw abortError()

  // WHO WE ARE, finalised for this read: the explicit sources the caller read
  // once for the whole run, plus the mirror's own derivation. The topic gate
  // needs it (our signature must not make a thread "about the product") and so
  // does the cohort match (one of our addresses is never a counterpart), so it
  // travels on the result rather than being rebuilt per caller.
  const org = mergeOwnOrg(ours, mail)

  const byAddress = new Map()
  const rec = (addr) => {
    let r = byAddress.get(addr)
    if (!r) {
      r = {
        days: new Set(),
        lastTouchAt: null,
        lastSubject: '',
        lastSnippet: '',
        lastInboundAt: null,
        lastAutoReplyAt: null,
        replySubject: '',
        replySnippet: '',
        replyAt: '',
        // {at, from, quote} — a human at this company asked us to stop.
        optedOut: null,
        bouncedCount: 0,
        outboundCount: 0,
        companyIds: [],
        // The thread's real messages, dated — see mailHistory.threadMessages.
        // Only the HubSpot mirror carries them; the activity log has no bodies
        // and the sent-mail scan is address-only, so a lead whose history came
        // from those sources honestly shows none rather than a fabricated list.
        messages: [],
        sources: new Set(),
        related: true,
        relatedWhy: '',
        classified: false,
      }
      byAddress.set(addr, r)
    }
    return r
  }

  // 1. HubSpot first — it is the system of record, and it is the only source
  //    that carries subjects, bodies, replies and bounces.
  let related = 0
  let unrelated = 0
  if (mail.available) {
    for (const [addr, thread] of mail.threads) {
      // A thread with no DELIVERED outbound is not a cadence: an inbound-only
      // thread (they wrote to us first) has no touch to follow up on. It is
      // still kept, so the reply lane can surface it, but it carries no touch.
      const verdict = classifyThread(thread, org)
      const r = rec(addr)
      r.sources.add('hubspot')
      r.classified = true
      r.related = verdict.related
      r.relatedWhy = verdict.why
      if (verdict.related) related += 1
      else unrelated += 1
      for (const day of thread.touches) if (day) r.days.add(day)
      r.outboundCount += thread.outboundCount || 0
      r.bouncedCount += thread.bouncedCount || 0
      // Two mirrored threads can land on one lead (two contacts at the same
      // company): merge the messages and let threadMessages' sort decide the
      // order, so the card reads as one conversation with that company.
      for (const m of threadMessages(thread, HISTORY_MESSAGE_MAX)) r.messages.push(m)
      for (const id of thread.companyIds || []) if (!r.companyIds.includes(id)) r.companyIds.push(id)
      const out = thread.lastOutboundAt ? new Date(thread.lastOutboundAt) : null
      if (out && !Number.isNaN(out.getTime())) {
        if (!r.lastTouchAt || out > r.lastTouchAt) {
          r.lastTouchAt = out
          r.lastSubject = thread.lastOutbound?.subject || ''
          r.lastSnippet = thread.lastOutbound?.snippet || ''
        }
      }
      const inb = thread.lastInboundAt ? new Date(thread.lastInboundAt) : null
      // LATEST inbound across the company's threads, not whichever thread the
      // Map happened to yield last: two contacts at one company are one lead,
      // and the lead's "they replied" fact must be the newest reply anyone at
      // the company sent, exactly as lastTouchAt is the newest send.
      if (inb && !Number.isNaN(inb.getTime()) && (!r.lastInboundAt || inb > r.lastInboundAt)) {
        r.lastInboundAt = inb
        r.replySubject = thread.lastInbound?.subject || ''
        r.replySnippet = thread.lastInbound?.snippet || ''
        r.replyAt = thread.lastInbound?.at || ''
      }
      // An opt-out anywhere at the company covers the company: "remove ALL OF
      // US from your contact list" is the request as it was actually written,
      // and honouring it for the one address that sent it while the machine
      // keeps drafting to their colleagues is the bug this field exists for.
      if (thread.optedOut && (!r.optedOut || thread.optedOut.at > r.optedOut.at)) {
        r.optedOut = thread.optedOut
      }
      // An out-of-office is tracked SEPARATELY and never becomes lastInboundAt:
      // it must not freeze a cadence, and the freeze node says out loud that
      // that is what it saw.
      const auto = thread.lastAutoReplyAt ? new Date(thread.lastAutoReplyAt) : null
      if (auto && !Number.isNaN(auto.getTime())) r.lastAutoReplyAt = auto
    }
  }

  // 2. This workspace's own logged sends. They came out of THIS app's own send
  //    path (ComposeDock writes the event), so they are our outreach by
  //    construction and need no topic classification — there is nothing to
  //    classify them from anyway (the log carries no body).
  for (const [addr, ev] of log.byAddress) {
    const r = rec(addr)
    r.sources.add('log')
    for (const day of ev.days) if (day) r.days.add(day)
    if (!r.lastTouchAt || ev.lastTouchAt > r.lastTouchAt) {
      r.lastTouchAt = ev.lastTouchAt
      if (ev.lastSubject) r.lastSubject = ev.lastSubject
      r.lastSnippet = ''
    }
  }

  // 3. The rep's own sent folder. A send is a touch whoever typed it — the
  //    mirror simply never heard about this one. It carries no subject or body
  //    (the index reads headers only), so it can move the CLOCK but never
  //    invents what the email said: lastSubject/lastSnippet stay as they are,
  //    and a draft written off this touch says nothing about a message it
  //    cannot quote.
  let gmailTouched = 0
  if (sent && sent.checked && sent.recipients) {
    for (const [addr, when] of sent.recipients) {
      if (!addr) continue
      const r = rec(addr)
      r.sources.add('gmail')
      gmailTouched += 1
      if (when instanceof Date && !Number.isNaN(when.getTime())) {
        const day = chicagoDayOf(when)
        if (day) r.days.add(day)
        if (!r.lastTouchAt || when > r.lastTouchAt) r.lastTouchAt = when
      }
    }
  }

  // A record known ONLY to the mirror must pass the topic gate. A record the
  // workspace's own log knows about is our own outreach and is in by right.
  for (const r of byAddress.values()) {
    if (r.sources.has('log')) {
      r.related = true
      if (!r.relatedWhy) r.relatedWhy = 'this workspace logged the send itself — our own outreach'
    }
  }

  return {
    byAddress,
    mail,
    log,
    sent,
    org,
    counts: {
      mirrorThreads: mail.available ? mail.threads.size : 0,
      logRecipients: log.byAddress.size,
      gmailRecipients: gmailTouched,
      related,
      unrelated,
    },
  }
}

/**
 * The detail lines that name WHERE the history came from — one per source,
 * each stating what it actually answered. An unread source produces an
 * admission ("could not be read"), never a silent zero.
 */
function historySourceLines(read) {
  const { mail, log, counts } = read
  const lines = []
  if (mail.available) {
    lines.push(
      `HubSpot email engagements: read ${fmt(mail.engagementsRead)} ${plural(mail.engagementsRead, 'engagement', 'engagements')} over ${fmt(mail.pagesRead)} ${plural(mail.pagesRead, 'page', 'pages')} (GET /api/hubspot/activity?family=engagement) — ${fmt(mail.outboundRead)} delivered outbound, ${fmt(mail.inboundRead)} inbound ${plural(mail.inboundRead, 'reply', 'replies')}, across ${fmt(counts.mirrorThreads)} ${plural(counts.mirrorThreads, 'thread', 'threads')}. This is the system of record: Bo sends from HubSpot, not from this app.`,
    )
    if (mail.undelivered) {
      lines.push(
        `${fmt(mail.undelivered)} ${plural(mail.undelivered, 'message was', 'messages were')} bounced, failed, scheduled or still a draft — counted separately and NEVER as a touch, because nobody received them.`,
      )
    }
    if (!mail.complete && mail.reason) {
      lines.push(`READ BOUNDED: ${mail.reason} — touch counts on older threads may be low.`)
    }
    if (mail.unattributedSender) {
      // Refusing to count these is the honest call — HubSpot's direction says
      // "outbound" but nothing on the row ties it to this workspace, and a
      // touch we cannot attribute is a touch we cannot claim Bo made. Saying
      // how many there were is the other half of that honesty.
      lines.push(
        `${fmt(mail.unattributedSender)} outbound ${plural(mail.unattributedSender, 'engagement carried', 'engagements carried')} no readable sender, or a sender this workspace cannot claim as its own — NOT counted as touches, because we cannot say who sent them.`,
      )
    }
    if (mail.undatable) {
      lines.push(
        `${fmt(mail.undatable)} ${plural(mail.undatable, 'engagement carried', 'engagements carried')} no usable timestamp${mail.futureDated ? ` (${fmt(mail.futureDated)} of them stamped in the future, which is a garbled date and not a send)` : ''} — left out rather than dated to now, so nothing is wait-gated off a time we made up.`,
      )
    }
    if (mail.ourAddresses.size) {
      lines.push(
        `“Our” senders were derived from the workspace, not hardcoded: ${[...mail.ourAddresses].slice(0, 6).join(', ')}${mail.ourAddresses.size > 6 ? `, +${fmt(mail.ourAddresses.size - 6)} more` : ''}.`,
      )
    }
  } else {
    lines.push(
      `The HubSpot mirror could not be read (${mail.reason || 'no reason given'}) — that is an admission, not a finding: sends made from HubSpot are NOT counted below.`,
    )
  }
  if (log.byAddress.size) {
    lines.push(
      `Workspace activity log: ${fmt(log.sendEvents)} logged ${plural(log.sendEvents, 'send', 'sends')} to ${fmt(log.byAddress.size)} ${plural(log.byAddress.size, 'recipient', 'recipients')} in the ${fmt(log.eventsRead)} most recent events read${log.mode === 'api' ? ' (GET /api/events)' : ' — DEGRADED: only this browser’s local copy answered'}, deduped to one touch per recipient per Chicago calendar day and unioned with HubSpot.`,
    )
  } else if (log.unstamped) {
    // Sends WERE logged; every one of them was undatable. Saying "none is a
    // logged send" here would be a claim contradicted by data we hold, so the
    // count is admitted instead.
    lines.push(
      `Workspace activity log: read ${fmt(log.eventsRead)} recent ${plural(log.eventsRead, 'event', 'events')}; ${fmt(log.unstamped)} ${plural(log.unstamped, 'is a logged send but carried', 'are logged sends but carried')} no readable timestamp, so ${plural(log.unstamped, 'it', 'they')} cannot be dated or wait-gated — left out rather than guessed.`,
    )
  } else {
    lines.push(
      log.mode === 'api'
        ? `Workspace activity log: read ${fmt(log.eventsRead)} recent ${plural(log.eventsRead, 'event', 'events')}; none is a logged send — expected, because this app never sends.`
        : 'Workspace activity log: only this browser’s local copy answered, and it holds no logged send.',
    )
  }
  // An undatable send alongside datable ones is admitted on every path, not
  // only the 'done' one.
  if (log.byAddress.size && log.unstamped) {
    lines.push(
      `${fmt(log.unstamped)} further logged ${plural(log.unstamped, 'send carried', 'sends carried')} no readable timestamp and cannot be wait-gated — left out rather than guessed.`,
    )
  }
  return lines.filter(Boolean)
}

// ── BO'S OWN TASKS — the one MANDATORY item of his daily pass ───────────────
//
// bo-daily/SKILL.md: "Run `sales_state.py --tasks` and fold his OVERDUE + TODAY
// tasks into the 'Do today' list, using his own wording plus the expanded
// meaning ('fu demo' = follow up to book/run the demo). These are tasks Bo
// typed in HubSpot; never fabricate one and never omit one that is overdue or
// due today."
//
// The worklist had no task lane at all, so the first thing he asks for was the
// one thing missing. The mirror carries them — hs_task_subject is his own
// wording — so this reads them and hands them over verbatim. NOTHING is
// invented and nothing is paraphrased: the subject is his text, and a task with
// no subject is shown as "(no subject)" rather than described.
//
// THE WINDOW (narrowed back 2026-09-01, at the user's instruction): overdue,
// OR due today on the Chicago calendar — nothing due later is listed, so the
// section is the day's worklist and not a forecast. COMPLETED leaves;
// every other status travels RAW (NOT_STARTED, IN_PROGRESS, WAITING,
// DEFERRED, …), because the receipt filters this section on the values
// actually present and collapsing them here would leave it nothing to filter.
//
// WHERE THE ROWS COME FROM — two lessons picked the route, keep both:
//   1. The old GET /api/hubspot/activity read needed detail:1, and that was
//      LOAD-BEARING, not verbosity: the digest line carries due_at only when
//      detail is asked for (hubspot handler _DETAIL_FIELDS), and this lane
//      shipped reading t.due_at off digest lines that never had it — every
//      task was skipped as undated and the lane was permanently, silently
//      empty. Verified against the deployed route on 2026-08-27: 200 lines,
//      0 with due_at.
//   2. Even WITH detail:1 that route never carries hs_task_status for a task
//      (the handler copies `status` onto engagement lines only, and
//      _DETAIL_FIELDS does not list it), so the raw statuses the receipt now
//      filters on simply are not in that payload. The read therefore moved to
//      GET /api/hubspot/metrics?rows=tasks, whose task rows are shaped
//      straight off hs_task_status / hs_task_priority (handler shape_task)
//      and are attributed to a HubSpot owner id. hs_task_type and the body do
//      not ride those rows, so taskType/body are simply absent rather than
//      invented; the interface marks both optional for exactly this reason.
//   3. WHOSE ROWS. The lane used to ask the metrics route for the SIGNED-IN
//      address and nothing else. The route matches an actor to HubSpot owners
//      by e-mail, and Bo signs in as bo@revailo.com while HubSpot knows him as
//      bo@revailo.ai — so the answer was zero rows for Bo himself, and zero
//      for the admin running the briefing at the 2026-09-01 investor demo,
//      rendered as "no tasks are overdue" over 23 overdue and 15 due that day.
//      The rep is now resolved from the ROSTER (role rep → owner by e-mail,
//      then first name — the Activity page's rule) and the owner id is passed
//      explicitly; see resolveTaskReps().
//
// THE WINDOW ENDS TODAY: overdue, or due today on the Chicago calendar. It was
// two business days out for a while and the list read as a forecast.

/**
 * The pure judge over metrics task rows — exported so
 * scripts/test-userLanes.mjs can pin the window, the status rule and the
 * order without a network. `rows` are shape_task rows (raw status/priority,
 * ISO due_at/completed_at, company_id/company_name/company_domain,
 * hubspot_url).
 *
 * `exclude` is an exclusionSet (lib/taskExclusions.js) — the Settings-managed
 * list of our own domains and addresses — or null, which is exactly today's
 * behaviour: nothing is dropped and `excluded.count` is zero. A task row
 * carries company ids only, never a contact or an e-mail, so the ONLY signal
 * that a task is about ourselves is the associated company's domain, which
 * the mirror hands over as company_domain (empty when the company has none —
 * and an empty domain matches nothing, so a company-less task always stays).
 * @returns {{items: object[], undated: number,
 *   excluded: {count: number, companies: string[]}}}
 */
export function judgeBoTasks({ rows = [], now = new Date(), exclude = null } = {}) {
  const today = chicagoDayOf(now)
  // Nothing past today: a task due tomorrow is tomorrow's list.
  const horizon = today
  const items = []
  let undated = 0
  // OUR OWN STAFF AND TEST ACCOUNTS, LEFT OFF BUT COUNTED (owner's ask,
  // 2026-09-08: the daily list kept showing tasks logged against our own
  // people and test companies). The count is of tasks that would OTHERWISE be
  // on this list — the domain check runs after the status and window checks,
  // so "3 hidden" never means three tasks due next month at revailo.com and
  // never double-counts a row the undated admission already owns. Up to five
  // distinct company names ride along so the admission can say WHOSE tasks
  // were hidden, not just how many — a bare number is easy to shrug at.
  let excludedCount = 0
  const excludedCompanies = []
  // COULD THE LIST BE APPLIED AT ALL? A task row that never carried a
  // company_domain key is not a row at a domain nobody excluded — it is a row
  // the mirror answered WITHOUT the field (a lambda one deploy behind). The
  // review that caught this found the two cases rendering identically: zero
  // hidden, no admission, our own test companies back on the list without a
  // word. So when the list has entries and the rows have no such key, the
  // judge says the exclusion did not run, and the receipt says so too.
  const list = Array.isArray(rows) ? rows : []
  const wantsExclusion = Boolean(exclude && ((exclude.domains?.size || 0) + (exclude.addresses?.size || 0) > 0))
  const carriedDomain = list.some((t) => t && typeof t === 'object' && Object.prototype.hasOwnProperty.call(t, 'company_domain'))
  const unapplied = wantsExclusion && list.length > 0 && !carriedDomain
  // WRITTEN OFF (the rep's report, 2026-09-25: companies he "dismissed as they
  // are not a fit … notes stating they are not a fit", and "closed lost we
  // don't care about at all"). The read lambda judges each task's company —
  // every deal Closed Lost, or a note in his dismissal wording — and ships the
  // verdict as `company_dismissed` (see company_dismissals in the hubspot
  // handler). A task is left off when the write-off came AFTER the task was
  // set: that task is what the write-off made stale. A task he created AFTER
  // writing the company off ("fu for concrete and flatwork" in October, on a
  // company whose note says "not a fit for now") is a deliberate re-check and
  // stays. A row with no `company_dismissed` key at all came from a lambda one
  // deploy behind — the check did not run, and the judge says so.
  const carriedDismissal = list.some(
    (t) => t && typeof t === 'object' && Object.prototype.hasOwnProperty.call(t, 'company_dismissed'),
  )
  const dismissalUnapplied = list.length > 0 && !carriedDismissal
  let dismissedCount = 0
  const dismissedCompanies = []
  // SUPERSEDED (same report: "Have tasks but for future dates"). When the rep
  // has already booked the company's NEXT step — an open task of his dated
  // after today — an older task still sitting OVERDUE on that company is the
  // step he replaced, not today's work. It is left off and counted, with the
  // date he booked, so the stale one can be closed in HubSpot. A task due
  // TODAY is never superseded: today's task is today's work.
  const nextByCompany = new Map()
  for (const t of list) {
    const cid = String(t?.company_id || '').trim()
    if (!cid) continue
    if (!isOpenTask({ status: String(t?.status || '').trim(), completed_at: t?.completed_at })) continue
    const due = hubspotDate(t?.due_at)
    const day = due ? chicagoDayOf(due) : ''
    if (!day || day <= today) continue
    const prev = nextByCompany.get(cid)
    if (!prev || day < prev) nextByCompany.set(cid, day)
  }
  let supersededCount = 0
  const supersededCompanies = []
  for (const t of list) {
    const status = String(t?.status || '').trim()
    // ONE DEFINITION OF "OPEN" FOR THE WHOLE APP. This used to test
    // `status === 'COMPLETED'` by hand, which disagreed with lib/hubspot.js's
    // isOpenTask in two ways: it let 'Done' and 'Complete' through as due work,
    // and it listed DEFERRED — a task the rep explicitly pushed off — on the
    // "what must I do today" briefing, while every other surface counted it
    // closed. Two answers to "is this task open" is one too many. Whichever
    // statuses that set holds, this lane now agrees with them.
    // (No live row is affected today: the mirror currently holds only
    // NOT_STARTED and COMPLETED.) The raw status still travels on the row for
    // the receipt's filter chips.
    if (!isOpenTask({ status, completed_at: t?.completed_at })) continue
    const due = hubspotDate(t?.due_at)
    if (!due) {
      // A task with no readable due date cannot be windowed. Counted and
      // reported, never guessed at — same rule as every other unreadable date.
      undated += 1
      continue
    }
    const day = chicagoDayOf(due)
    if (!day || day > horizon) continue
    if (exclude && isExcluded(t?.company_domain, exclude)) {
      excludedCount += 1
      // The company's name for the admission; its domain when it has none.
      const who = String(t?.company_name || t?.company_domain || '').trim()
      if (who && excludedCompanies.length < 5 && !excludedCompanies.includes(who)) excludedCompanies.push(who)
      continue
    }
    const overdue = day < today
    const company = String(t?.company_name || '').trim()
    const companyId = String(t?.company_id || '').trim()
    const writtenOff = t?.company_dismissed && typeof t.company_dismissed === 'object' ? t.company_dismissed : null
    if (writtenOff) {
      const offAt = hubspotDate(writtenOff.at)
      const setAt = hubspotDate(t?.created_at)
      // No readable date on either side: the write-off stands — the rep said
      // he does not care about this company, and nothing says he re-engaged.
      if (!offAt || !setAt || offAt.getTime() >= setAt.getTime()) {
        dismissedCount += 1
        const who = company || String(writtenOff.label || '').trim() || 'a company'
        if (dismissedCompanies.length < 5 && !dismissedCompanies.some((c) => c.name === who)) {
          dismissedCompanies.push({
            name: who,
            reason: writtenOff.reason === 'closed_lost' ? 'closed_lost' : 'not_a_fit',
            ...(writtenOff.at ? { at: String(writtenOff.at) } : {}),
            ...(writtenOff.quote ? { quote: String(writtenOff.quote).slice(0, 160) } : {}),
          })
        }
        continue
      }
    }
    if (overdue && companyId && nextByCompany.has(companyId)) {
      supersededCount += 1
      const who = company || 'a company'
      if (supersededCompanies.length < 5 && !supersededCompanies.some((c) => c.name === who)) {
        supersededCompanies.push({ name: who, next: nextByCompany.get(companyId) })
      }
      continue
    }
    const priority = String(t?.priority || '').trim()
    const taskType = String(t?.task_type || '').trim()
    items.push({
      id: String(t?.id || ''),
      subject: String(t?.subject || '').trim(),
      status,
      dueAt: due.toISOString(),
      day,
      overdue,
      // Every kept task is one or the other: past due, or due today.
      dueToday: !overdue,
      ...(priority ? { priority } : {}),
      ...(taskType ? { taskType } : {}),
      ...(company ? { company } : {}),
      ...(companyId ? { companyId } : {}),
      hubspotUrl: String(t?.hubspot_url || ''),
    })
  }
  // Oldest due first — the ones that have been waiting longest — with the id
  // as the tiebreak so two runs over the same read hand back the same order.
  items.sort((a, b) =>
    a.dueAt < b.dueAt ? -1 : a.dueAt > b.dueAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0,
  )
  return {
    items,
    undated,
    excluded: { count: excludedCount, companies: excludedCompanies, ...(unapplied ? { unapplied: true } : {}) },
    dismissed: { count: dismissedCount, companies: dismissedCompanies, ...(dismissalUnapplied ? { unapplied: true } : {}) },
    superseded: { count: supersededCount, companies: supersededCompanies },
  }
}

function firstNameOf(value) {
  return String(value || '')
    .trim()
    .split(/\s+/)[0]
    .toLowerCase()
}

/**
 * WHOSE TASKS THE WORKLIST SHOWS. The roster's reps — every member whose role
 * normalises to 'rep' — each resolved to a HubSpot owner the way the Activity
 * page resolves one: exact e-mail first, then first name (Bo signs in as
 * bo@revailo.com and owns HubSpot records as bo@revailo.ai). NOT the signed-in
 * session: an admin running the morning briefing is not the rep, and the
 * mirror's honest "no tasks for this address" about the admin was rendered as
 * "no tasks" about Bo.
 *
 * With no rep on the roster (a one-person workspace, or a roster that could
 * not be read) the signed-in address stands in, matched the same two ways
 * with its local part as the first name. Pure: roster and owner list in,
 * { reps, unresolved, how } out, so the rule is unit-tested without a browser.
 */
export function resolveTaskReps({ roster = null, owners = null, self = '' } = {}) {
  // WHETHER THE OWNER LIST WAS READ AT ALL. hubspotOwners() resolves
  // { available: false, reason } for every failure — it never throws — so an
  // unreachable mirror arrives here as an EMPTY owner list, indistinguishable
  // from a healthy portal that has no owners unless this flag is checked. Left
  // unchecked, a 401 made the lane say "no roster rep matches a HubSpot owner
  // (Bo Garcia)", which blames Bo's identity for a network failure.
  const ownersReadable = owners?.available === true
  const ownersReason = String(owners?.reason || '')
  const ownerList = (Array.isArray(owners?.owners) ? owners.owners : [])
    .map((o) => ({
      id: String(o?.id || '').trim(),
      email: String(o?.email || '').trim().toLowerCase(),
      name: String(o?.name || '').trim(),
    }))
    .filter((o) => o.id)
  // How many owners answer to each first name — so an AMBIGUOUS first name can
  // refuse to match instead of silently picking whichever owner the mirror
  // happened to return first. Matching "Bo" to one of two Bos would attribute
  // one person's tasks to another under a heading naming the wrong rep, and
  // nothing on screen would reveal it. Exact e-mail is never ambiguous.
  const byFirstName = new Map()
  for (const o of ownerList) {
    const fn = firstNameOf(o.name)
    if (fn) byFirstName.set(fn, (byFirstName.get(fn) || 0) + 1)
  }
  const match = (email, name) => {
    const byEmail = email ? ownerList.find((o) => o.email === email) : null
    if (byEmail) return { owner: byEmail, how: 'email' }
    const fn = firstNameOf(name)
    if (!fn) return null
    if ((byFirstName.get(fn) || 0) > 1) return { ambiguous: fn }
    const byName = ownerList.find((o) => firstNameOf(o.name) === fn)
    if (byName) return { owner: byName, how: 'first name' }
    return null
  }
  const members = Array.isArray(roster?.members) ? roster.members : []
  const candidates = members
    // roleOrEmpty, NOT normalizeRole: the latter answers 'rep' for any role it
    // does not recognise (and for a missing one), which would quietly pull a
    // manager's or a mis-labelled teammate's HubSpot tasks into this briefing
    // under the rep's name. An unrecognised role is not a rep.
    .filter((m) => roleOrEmpty(m?.role) === 'rep' && String(m?.email || '').includes('@'))
    .map((m) => ({ email: String(m.email).trim().toLowerCase(), name: String(m?.name || '').trim() }))
  // AN UNREADABLE ROSTER IS NOT AN EMPTY ONE — and this distinction is the
  // whole bug, one level up. listMembers() never throws: it falls back to this
  // browser's local copy and reports mode:'local', and if that copy is empty
  // too it hands back zero members. Reading that as "the workspace has no
  // sales rep" and quietly falling back to whoever is SIGNED IN would put the
  // admin's (empty) HubSpot tasks on the briefing under a heading that claims
  // to be the rep's — which is exactly the failure this function was written
  // to end. Only an AUTHORITATIVE roster (mode 'api') that genuinely names no
  // rep earns the session fallback.
  const rosterAuthoritative = roster?.mode === 'api'
  let how = 'roster'
  if (!candidates.length) {
    const me = String(self || '').trim().toLowerCase()
    if (!rosterAuthoritative) {
      how = 'roster-unreadable'
    } else if (me.includes('@')) {
      candidates.push({ email: me, name: me.split('@')[0] })
      how = 'session'
    } else {
      how = 'none'
    }
  }
  const reps = []
  const unresolved = []
  const seen = new Set()
  for (const c of candidates) {
    const hit = match(c.email, c.name)
    if (!hit || hit.ambiguous) {
      unresolved.push({
        email: c.email,
        name: c.name,
        ...(hit?.ambiguous
          ? { why: `more than one HubSpot owner is called ${hit.ambiguous}, so matching on the first name alone could pick the wrong person` }
          : {}),
      })
      continue
    }
    if (seen.has(hit.owner.id)) continue
    seen.add(hit.owner.id)
    reps.push({
      email: c.email,
      name: c.name || hit.owner.name || c.email,
      ownerId: hit.owner.id,
      ownerEmail: hit.owner.email,
      how: hit.how,
    })
  }
  return { reps, unresolved, how, rosterAuthoritative, ownersReadable, ownersReason }
}

const TASK_ORDER = (a, b) =>
  a.dueAt < b.dueAt ? -1 : a.dueAt > b.dueAt ? 1 : a.id < b.id ? -1 : a.id > b.id ? 1 : 0

export async function readBoTasks({
  signal,
  now = new Date(),
  roster = null,
  owners = null,
  self = '',
  // The one outward call, injectable so the merge and the failure paths can be
  // tested without a network. Defaults to the real route.
  readMetrics = hubspotMetrics,
  // The Settings-managed list of our own domains/addresses (lib/
  // taskExclusions.js), resolved at CALL time — a default parameter is
  // evaluated per call, never at module load — so a rep who edits Settings
  // between two runs in the same tab gets the new list on the second run.
  // Injectable so a test can pin a list without touching localStorage.
  exclude = exclusionSet(readExclusions().entries),
} = {}) {
  const { reps, unresolved, how, ownersReadable, ownersReason } = resolveTaskReps({ roster, owners, self })
  if (!reps.length) {
    const reason = !ownersReadable
      ? `the HubSpot owner list could not be read${ownersReason ? ` (${ownersReason})` : ''}, so this pass cannot match anyone to their tasks — that is unknown, not "nobody has tasks"`
      : how === 'roster-unreadable'
        ? 'the team roster could not be read, so this pass cannot say who the rep is — that is unknown, not "nobody has tasks"'
        : how === 'none'
          ? 'nobody is signed in in this browser and the roster names no rep, so the mirror cannot be asked whose tasks to show'
          : how === 'session'
            ? `the roster names no rep and ${self} matches no HubSpot owner by e-mail or first name`
            : `no roster rep matches a HubSpot owner by e-mail or first name (${unresolved.map((u) => u.name || u.email).join(', ')})`
    return { readable: false, reason, items: [], truncated: false, reps: [], unread: [] }
  }
  // One read per rep so every row is attributed to a named person — the owner
  // id is passed EXPLICITLY, which is the whole fix (lesson 3 above).
  //
  // A REJECTED read is caught per rep rather than taken as the lane's answer:
  // hubspotMetrics is documented never to throw, but "documented never to" is
  // how the original bug got its confidence, and one rep's network blip must
  // not decide what the briefing says about the others.
  const reads = await Promise.all(
    reps.map(async (rep) => {
      try {
        return { rep, res: await readMetrics({
          actor: rep.email,
          owner_ids: rep.ownerId,
          addresses: [rep.email, rep.ownerEmail],
          rows: 'tasks',
        }) }
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        return { rep, res: null, threw: String(err?.message || 'the task read failed') }
      }
    }),
  )
  if (signal?.aborted) throw abortError()

  // ONE REP'S FAILURE MUST NOT ERASE ANOTHER'S WORK. The first version of this
  // returned unreadable for the whole lane the moment any single read failed —
  // which would have thrown away a rep's real, readable tasks because a
  // teammate's read blipped, and printing nothing over real work is precisely
  // the bug this whole change exists to fix. Each rep is now judged on its own:
  // whoever could be read is shown, whoever could not is NAMED.
  const items = []
  const unread = []
  let undated = 0
  let truncated = false
  // The hidden-as-ours count is summed across reps and the company names
  // merged (still at most five), so the node admits ONE number for the whole
  // list rather than one per rep.
  let excludedCount = 0
  let excludedUnapplied = false
  const excludedCompanies = []
  // The two write-off admissions (judgeBoTasks), summed the same way: one
  // number for the whole list, at most five named companies each.
  let dismissedCount = 0
  let dismissedUnapplied = false
  const dismissedCompanies = []
  let supersededCount = 0
  const supersededCompanies = []
  for (const { rep, res, threw } of reads) {
    const failure = threw
      ? threw
      : !res || res.available !== true || res.configured === false
        ? res?.reason || 'the HubSpot mirror could not be read'
        : // complete:false is the route's own refusal to answer from a partial
          // feed walk — rows are absent on that shape, and a lane built from
          // nothing would be a zero wearing a finding's clothes.
          res.complete === false
          ? res.reason || 'the task feed could not be read to the end, so the tasks are unread — not absent'
          : ''
    if (failure) {
      unread.push({ name: rep.name, reason: failure })
      continue
    }
    const judged = judgeBoTasks({ rows: Array.isArray(res.rows?.tasks) ? res.rows.tasks : [], now, exclude })
    for (const t of judged.items) items.push({ ...t, rep: rep.name })
    undated += judged.undated
    excludedCount += judged.excluded.count
    for (const c of judged.excluded.companies) {
      if (excludedCompanies.length < 5 && !excludedCompanies.includes(c)) excludedCompanies.push(c)
    }
    // One rep's read without the field is enough: the list was not applied
    // to at least part of what is shown.
    excludedUnapplied = excludedUnapplied || Boolean(judged.excluded.unapplied)
    dismissedCount += judged.dismissed.count
    dismissedUnapplied = dismissedUnapplied || Boolean(judged.dismissed.unapplied)
    for (const c of judged.dismissed.companies) {
      if (dismissedCompanies.length < 5 && !dismissedCompanies.some((d) => d.name === c.name)) dismissedCompanies.push(c)
    }
    supersededCount += judged.superseded.count
    for (const c of judged.superseded.companies) {
      if (supersededCompanies.length < 5 && !supersededCompanies.some((d) => d.name === c.name)) supersededCompanies.push(c)
    }
    // The metrics rows carry their own byte-budget admission; repeat it rather
    // than presenting a cut list as the whole one.
    truncated = truncated || Boolean(res.rows_truncated?.tasks)
  }
  // A ROSTER REP WHO MATCHES NO HUBSPOT OWNER IS ALSO MISSING FROM THIS LIST.
  // Their absence used to be reported only when NOBODY resolved; the moment one
  // rep matched, the others vanished without a word and the briefing looked
  // complete. Same rule as a failed read: name them.
  for (const u of unresolved) {
    unread.push({
      name: u.name || u.email,
      reason:
        u.why ||
        'no HubSpot owner matches this person by e-mail or first name, so their tasks cannot be looked up',
    })
  }
  items.sort(TASK_ORDER)
  // Unreadable ONLY when nothing at all could be read. A partial read is
  // readable and says whose half is missing — never a silent short list.
  if (!reps.length || unread.length >= reps.length + unresolved.length) {
    return {
      readable: false,
      reason: unread.map((u) => `${u.name}: ${u.reason}`).join('; '),
      items: [],
      truncated: false,
      reps,
      unread,
    }
  }
  return {
    readable: true,
    items,
    undated,
    truncated,
    reps,
    unread,
    excluded: { count: excludedCount, companies: excludedCompanies, ...(excludedUnapplied ? { unapplied: true } : {}) },
    dismissed: { count: dismissedCount, companies: dismissedCompanies, ...(dismissedUnapplied ? { unapplied: true } : {}) },
    superseded: { count: supersededCount, companies: supersededCompanies },
  }
}

// ── shared: the read-only HubSpot screen ────────────────────────────────────
// EVERY call in here is a GET (see hubspot.js — the client has no write path at
// all), and every failure mode resolves to "we could not read it", never to an
// invented row and never to a dropped lead. A CRM that is down must cost this
// run nothing but a line of explanation.

// The mirror's own flag for "this key maps to more than one company". Same
// constant lib/data.jsx uses; an ambiguous key is deliberately NOT a match,
// because a shared name in this trade is a coin flip.
const HS_AMBIGUOUS = '__ambiguous__'

// How many companies one branch will open individually. The index is a single
// call for the whole portal; per-company detail is one call each, so it is
// capped and the pass says out loud that it was capped. Deterministic: the cap
// takes the top of an already-ranked list, never a sample.
const HS_PROBE_LIMIT = 12

// Resolve one lead to a mirrored company id, mirroring the backend's
// _resolve_company precedence exactly the way lib/data.jsx does: website
// domain, then contact email, then name slug; first tier the index recognises
// decides. Pure and O(1) per lead, so screening the whole eligible set costs
// one index read and no extra network at all.
function crmCompanyId(index, row, email) {
  const attempts = [
    [normDomain(row?.website), index?.by_domain],
    [String(email || '').toLowerCase(), index?.by_email],
    [nameSlug(row?.name), index?.by_name],
  ]
  for (const [key, map] of attempts) {
    if (!key || !map || !Object.prototype.hasOwnProperty.call(map, key)) continue
    const value = map[key]
    if (value === HS_AMBIGUOUS) return { id: '', ambiguous: true }
    const id = String(value || '')
    if (id) return { id, ambiguous: false }
  }
  return { id: '', ambiguous: false }
}

// Open-deal count off the index rollup. Returns null — not 0 — when the mirror
// carries no rollup for this company: "we did not read it" and "there are none"
// are different facts, and only one of them may exclude a lead.
function rollupOpenDeals(company) {
  const raw = company?.rollup?.open_deals
  if (raw === undefined || raw === null || String(raw).trim() === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
}

// Open one company each, concurrently, never throwing: a company that will not
// load comes back as null and is counted as unread rather than as clean.
async function probeCompanies(ids, signal) {
  const probed = await Promise.all(
    ids.map((id) =>
      hubspotCompany({ id })
        .then((data) => ({ id, data: data && data.available ? data : null }))
        .catch(() => ({ id, data: null })),
    ),
  )
  if (signal?.aborted) throw abortError()
  return probed
}

// WHAT BO'S OWN TASK CALENDAR SAYS ABOUT A COMPANY. He schedules his next
// touch as a HubSpot task with a real due date ("fu demo", due Aug 30), and
// the cadence's 3-business-day arithmetic was overruling that calendar: a
// lead could clear the wait gate on elapsed time while Bo's own task said
// "not until Sunday". His sales guy read the first ten "due" touches against
// HubSpot and most carried a future task date (2026-08-28) — the machine and
// the rep disagreed about what "due" means, and the rep is right: an explicit
// date a person typed outranks a default waiting rule.
//
//   { dueNow, future } — the OPEN task with the earliest due day <= today,
//   and the open task with the earliest FUTURE due day. A company with a
//   future-due open task and nothing due now is SCHEDULED: today is not its
//   day. A completed task is nobody's schedule, and a task with no readable
//   due date pins nothing (never guessed).
export function taskScheduleOf(tasks, now = new Date()) {
  const today = mailChicagoDay(now)
  let dueNow = null
  let future = null
  for (const t of Array.isArray(tasks) ? tasks : []) {
    if (!isOpenTask(t)) continue
    const due = hubspotDate(t.due_at)
    if (!due) continue
    const day = mailChicagoDay(due)
    const view = {
      day,
      subject: String(t.subject || t.name || '').trim().slice(0, 60),
      id: String(t.id || t.object_id || ''),
    }
    if (day <= today) {
      if (!dueNow || day < dueNow.day) dueNow = view
    } else if (!future || day < future.day) {
      future = view
    }
  }
  return { dueNow, future }
}

// ── shared: the company-level "new lead" gate (operation 1) ─────────────────
// "New" is a COMPANY property, not an address property: once anyone at a
// company has been reached out to, every lead at that company is off the
// initial-outreach table. Everything in this section is a read or a pure
// index over reads already made.

// Freemail and ISP mail hosts. A shared mail provider is NOT a company —
// domain matching is skipped for these, or one gmail.com touch would poison
// every gmail.com lead in the directory. Exact-address matches still count.
const FREEMAIL_HOSTS = new Set([
  'gmail.com', 'googlemail.com', 'aol.com', 'icloud.com', 'me.com', 'mac.com',
  'msn.com', 'comcast.net', 'att.net', 'sbcglobal.net', 'verizon.net',
  'bellsouth.net', 'cox.net', 'charter.net', 'earthlink.net',
  'protonmail.com', 'proton.me', 'ymail.com', 'mail.com',
])
// Provider families with many country TLDs (yahoo.com, yahoo.co.uk, gmx.de…).
// Matching is on the host's first label + dot, so 'liveoakmasonry.com' (no dot
// after "live") is untouched while 'live.co.uk' is caught.
const FREEMAIL_HOST_FAMILIES = ['yahoo.', 'hotmail.', 'outlook.', 'live.', 'gmx.']

export function isFreemailDomain(domain) {
  const d = String(domain || '').trim().toLowerCase()
  if (!d) return false
  return FREEMAIL_HOSTS.has(d) || FREEMAIL_HOST_FAMILIES.some((fam) => d.startsWith(fam))
}

// Shared web platforms that masonry rows carry as their "website" (the import
// holds hundreds of facebook.com pages). Same poisoning rule as freemail: a
// facebook.com website is a PAGE on a shared host, not a company domain, so it
// never joins two companies — one touched facebook-page lead must not exclude
// every other facebook-page lead.
const SHARED_SITE_HOSTS = new Set([
  'facebook.com', 'instagram.com', 'linkedin.com', 'yelp.com', 'houzz.com',
  'angi.com', 'angieslist.com', 'thumbtack.com', 'yellowpages.com',
  'google.com', 'goo.gl', 'bbb.org', 'nextdoor.com', 'x.com', 'twitter.com',
])

// The row's website as a COMPANY IDENTITY: normalized host, or '' when the row
// has no usable one (empty, freemail-ish, or a shared platform host).
function siteDomainOf(row) {
  const d = normDomain(row?.website)
  if (!d || isFreemailDomain(d) || SHARED_SITE_HOSTS.has(d)) return ''
  return d
}

// The mail host of one address, '' when there is nothing to parse. An empty or
// unparseable domain matches nothing — it never joins two companies.
function mailDomainOf(addr) {
  const a = String(addr || '').toLowerCase()
  const at = a.lastIndexOf('@')
  if (at === -1) return ''
  const d = a.slice(at + 1).trim()
  return d.includes('.') ? d : ''
}

// EVERY address a row carries: the email column plus everything packed in the
// contacts blob — the company gate matches all of them, where emailOf() picks
// the one we would write to.
export function allEmailsOf(row) {
  const out = new Set()
  if (!row) return out
  for (const src of [row.email, row.contacts]) {
    for (const raw of String(src || '').match(ADDR_ALL_RE) || []) out.add(raw.toLowerCase())
  }
  return out
}

// How many sent messages one company-gate read will scan. One page, said out
// loud in the pass detail — the client API takes a size, not a page cursor.
const SENT_SCAN_MAX = 200

/**
 * Recipient addresses from the connected inbox's REAL sent mail (in:sent).
 *
 * WHOSE mailbox: the signed-in user's own — Gmail tokens are per-user, so this
 * can never read anybody else's sent mail, and the caller must attribute the
 * read to `address` (from gmailStatus()), never to a person by name.
 *
 * Resolves { checked:false, reason } for every failure mode — not connected,
 * API down, a message list that carries no recipient fields — so the node can
 * say exactly what was and wasn't checked. Never rejects except on abort.
 *
 * @returns {Promise<{ checked: boolean, reason?: string, address: string,
 *   recipients?: Map<string, Date|null>, messagesRead?: number }>}
 */
async function readSentRecipients(signal) {
  let st = { available: false, configured: false, connected: false }
  try {
    st = (await gmailStatus()) || st
  } catch {
    /* keep the unavailable default */
  }
  if (signal?.aborted) throw abortError()
  const address = String(st.address || '')
  if (!st.available) return { checked: false, address, reason: 'the Gmail integration API did not answer' }
  if (!st.configured) return { checked: false, address, reason: 'the Gmail integration is not set up for this workspace' }
  if (!st.connected) return { checked: false, address, reason: 'the inbox is not connected' }

  // THE WHOLE SENT FOLDER, from the server-side index — not the newest 15 the
  // per-message route can afford. This is the difference between "Bo emailed
  // 15 people" and the 384 he actually wrote to.
  const idx = await gmailSentIndex()
  if (signal?.aborted) throw abortError()
  if (!idx.checked) {
    return { checked: false, address, reason: idx.reason || 'the sent index could not be read' }
  }
  const entries = Object.entries(idx.recipients || {})
  const recipients = new Map()
  for (const [addr, meta] of entries) {
    const at = new Date(meta?.last || '')
    recipients.set(String(addr).toLowerCase(), Number.isNaN(at.getTime()) ? null : at)
  }
  return {
    checked: true,
    address: idx.address || address,
    recipients,
    messagesRead: Number(idx.messagesRead) || 0,
    truncated: Boolean(idx.truncated),
  }
}

/**
 * Index every touched address for the company gate. Pure — all reads happened
 * before this is called.
 *
 *   touched   addr → { addr, when: Date|null, src: 'log'|'sent' } — every
 *             address the history holds; `when` only when a read actually
 *             carried a date (log timestamps or sent-mail dates).
 *   byDomain  corporate mail host → first touch evidence (freemail skipped).
 *   keyTouch / siteTouch — companies marked touched because a touched address
 *             belongs to one of their OWN directory rows (built over the raw
 *             scope, pre-dedup, so a collapsed duplicate still marks its key).
 */
function buildCompanyGate({ known, logDates, sent, scopeRows }) {
  const touched = new Map()
  for (const addr of known) {
    const rec = logDates.get(addr)
    touched.set(addr, { addr, when: rec ? rec.lastTouchAt : null, src: 'log' })
  }
  if (sent?.checked) {
    for (const [addr, when] of sent.recipients) {
      const prev = touched.get(addr)
      if (prev) {
        if (!prev.when && when) prev.when = when
      } else {
        touched.set(addr, { addr, when: when || null, src: 'sent' })
      }
    }
  }
  const byDomain = new Map()
  for (const ev of touched.values()) {
    const d = mailDomainOf(ev.addr)
    if (!d || isFreemailDomain(d)) continue
    if (!byDomain.has(d)) byDomain.set(d, ev)
  }
  const keyTouch = new Map() // keyOf(row) -> { ev, name }
  const siteTouch = new Map() // normDomain(row.website) -> { ev, name }
  for (const row of scopeRows) {
    let hit = null
    for (const a of allEmailsOf(row)) {
      const ev = touched.get(a)
      if (ev) {
        hit = ev
        break
      }
    }
    if (!hit) continue
    const k = String(keyOf(row) || '')
    if (k && !keyTouch.has(k)) keyTouch.set(k, { ev: hit, name: String(row.name || '') })
    const site = siteDomainOf(row)
    if (site && !siteTouch.has(site)) siteTouch.set(site, { ev: hit, name: String(row.name || '') })
  }
  return { touched, byDomain, keyTouch, siteTouch }
}

// The company-level verdict for ONE candidate: the evidence that somebody at
// its company was reached out to, or null when nothing matched. Strongest
// evidence first — its own addresses, then a shared corporate domain, then a
// sibling directory row of the same company. Freemail domains never match as
// domains (exact addresses only), and an empty domain matches nothing.
function companyTouchOf(candidate, gate) {
  const own = allEmailsOf(candidate.row)
  if (candidate.email) own.add(candidate.email)
  for (const a of own) {
    const ev = gate.touched.get(a)
    if (ev) return { ev, how: 'own' }
  }
  const site = siteDomainOf(candidate.row)
  const domains = new Set()
  if (site) domains.add(site)
  for (const a of own) {
    const d = mailDomainOf(a)
    if (d && !isFreemailDomain(d)) domains.add(d)
  }
  for (const d of domains) {
    const ev = gate.byDomain.get(d)
    if (ev) return { ev, how: 'domain', domain: d }
  }
  const k = String(keyOf(candidate.row) || '')
  const viaKey = k ? gate.keyTouch.get(k) : null
  if (viaKey) return { ev: viaKey.ev, how: 'sibling', name: viaKey.name }
  if (site) {
    const viaSite = gate.siteTouch.get(site)
    if (viaSite) return { ev: viaSite.ev, how: 'sibling', name: viaSite.name }
  }
  return null
}

// One touch, said the way it is actually known: with the date when a read
// carried one, and naming the source — the workspace log, or the connected
// mailbox's own sent mail (by address; whose it is comes from gmailStatus()).
function touchPhrase(ev, sentAddress) {
  const src =
    ev.src === 'sent'
      ? `${sentAddress || 'the connected mailbox'}’s sent mail`
      : 'the workspace log'
  if (ev.when) return `was emailed on ${shortDay(ev.when)} (${src})`
  return ev.src === 'sent' ? `is in ${src}` : 'is in this workspace’s outreach history'
}

// The company name a "new" note can honestly use: the corporate domain when
// one is held, else nothing (the note then says "this company").
function companyLabelOf(candidate) {
  const site = siteDomainOf(candidate.row)
  if (site) return site
  const d = mailDomainOf(candidate.email)
  return d && !isFreemailDomain(d) && !SHARED_SITE_HOSTS.has(d) ? d : ''
}

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION 1 — initial outreach, 50 new leads
// Unchanged behaviour from the original single-operation pipeline; only its id
// and label moved (reach-out-50 → initial-outreach-50).
// ═══════════════════════════════════════════════════════════════════════════
async function runInitialOutreach(op, run, { rows, now, category }) {
  const { passes, indexOf, start, finish, abandon, signal, tracer, emit, ownOrg } = run
  const at = (id) => indexOf.get(id)
  // Provenance. Called by the node that made the decision, with the row it
  // decided about — never afterwards, and never about a lead the node did not
  // actually look at. See makeTracer().
  const note = (row, nodeId, verdict, text) =>
    tracer.note(row ? keyOf(row) : '', nodeId, verdict, text)
  // What the nodes hand each other. A node reads what its dependencies wrote
  // and writes what its dependents need; the graph is what guarantees the order,
  // so nothing here has to check whether an earlier step ran.
  const ctx = {}

  const handlers = {
    // ── 1. source ─────────────────────────────────────────────────────────────
    // Whatever the caller handed us, deduped by company key. Regions overlap, and
    // the same company would otherwise be judged twice, drafted twice and land on
    // the receipt twice — better to collapse it here and say so than to promise
    // 50 leads and show 48.
    source: async () => {
      const i = at('source')
      await start(i)
      const scope = (Array.isArray(rows) ? rows : []).filter(Boolean)
      const seen = new Set()
      const unique = []
      const collapsed = new Set()
      let duplicates = 0
      let keyless = 0
      for (const row of scope) {
        const key = keyOf(row)
        if (!key) {
          // Nothing to record: a row with no key is a row the UI cannot look up
          // either, so it gets no trace rather than a trace nobody can find.
          keyless += 1
          continue
        }
        if (seen.has(key)) {
          duplicates += 1
          collapsed.add(String(key))
          continue
        }
        seen.add(key)
        unique.push(row)
      }

      if (!unique.length) {
        return abandon(
          i,
          'failed',
          'No leads in scope — nothing to run on',
          [
            'The directory handed this run zero usable rows.',
            'Nothing was created and nothing was sent.',
          ],
        )
      }

      // Rank now, once, at the top of the pipeline. Every later pass filters with
      // Array#filter, which preserves order, so the eventual `.slice(0, 50)` takes
      // the 50 highest-scoring survivors without needing to sort again.
      const ranked = rankRows(unique)
      ctx.ranked = ranked
      // WHO THE RUBRIC HELD DOWN. rankRows applies Bo's ICP caps (icpCapOf) —
      // a general contractor can never rank above 1, residential-only or a
      // non-masonry trade above 2 — so the top of this list is core masonry
      // even when the import's own score says otherwise. Counted here so the
      // step can say it out loud: a lead that quietly stopped appearing is
      // indistinguishable from a bug.
      const capped = unique.filter((row) => icpCapOf(row))
      ctx.cappedCount = capped.length
      for (const row of capped) {
        const cap = icpCapOf(row)
        note(row, 'source', 'kept', `held to ${cap.cap} by the ICP rubric — ${cap.why}`)
      }
      // Recorded in RANK order, not in the order the regions happened to load:
      // the tracer keeps leads in first-seen order when it has to shed some, and
      // rank order is the order this pipeline actually cares about.
      for (const row of ranked) {
        const score = scoreOf(row)
        note(
          row,
          'source',
          'kept',
          `${score === null ? 'in scope, no lead score on the row' : `in scope, lead score ${score}`}` +
            (collapsed.has(String(keyOf(row)))
              ? ' (listed twice in the directory, collapsed to one row)'
              : ''),
        )
      }
      const scored = ranked.filter((r) => scoreOf(r) !== null)
      const best = scored.length ? scoreOf(scored[0]) : null
      const worst = scored.length ? scoreOf(scored[scored.length - 1]) : null

      finish(
        i,
        'done',
        `${fmt(ranked.length)} masonry leads in scope, ranked by score`,
        [
          'Read from the lead directory — the same rows the directory page shows.',
          scored.length
            ? `${fmt(scored.length)} carry a lead score (${best} down to ${worst}); highest first, then the decision maker, then by name so the pick repeats.`
            : 'No row carries a lead score — ordered by name, so the pick is still repeatable.',
          capped.length
            ? `${fmt(capped.length)} held down by the ICP rubric — a general contractor can rank no higher than 1, residential-only or a non-masonry trade no higher than 2, whatever the import scored them. They stay on the list, below the masonry shops.`
            : '',
          duplicates
            ? `${fmt(duplicates)} duplicate ${plural(duplicates, 'row', 'rows')} collapsed (same company in two regions).`
            : '',
          keyless
            ? `${fmt(keyless)} ${plural(keyless, 'row', 'rows')} dropped — no company id, profile link, or name.`
            : '',
        ],
        { value: ranked.length, label: 'in scope' },
      )
    },

    // ── 2. reachable ──────────────────────────────────────────────────────────
    // "Reachable" now means reachable AND not us. Revailo's sister company is
    // in this very import with our own founders as its listed contacts, so the
    // first address on a row is not automatically somebody to pitch: our own
    // addresses are stepped past in favour of the next real contact, and a row
    // with nobody on it but us is dropped rather than written to. See
    // readOwnOrg() for where "ours" comes from and what happens when a source
    // that defines it could not be read.
    reachable: async () => {
      const i = at('reachable')
      await start(i)
      const { ranked } = ctx
      const org = await ownOrg()
      if (signal?.aborted) throw abortError()
      ctx.org = org
      const reachable = []
      let fromContacts = 0
      let steppedPast = 0
      let onlyOurs = 0
      for (const row of ranked) {
        const { email, refused } = emailOfExcluding(row, (addr) => isOwnAddress(addr, org))
        if (!email) {
          if (refused.length) {
            onlyOurs += 1
            note(
              row,
              'reachable',
              'dropped',
              `dropped — the only ${plural(refused.length, 'address', 'addresses')} on this row ${plural(refused.length, 'is', 'are')} ours (${refused.slice(0, 2).join(', ')}), and we do not write to our own people. There is no other contact on the row to write to instead.`,
            )
            continue
          }
          note(row, 'reachable', 'dropped', 'no address on the row or in its contacts')
          continue
        }
        // If the address came out of the packed contacts blob rather than the
        // email column, that is worth saying — it is a recovery, not a given.
        const recovered = !String(row.email || '').toLowerCase().includes(email)
        if (recovered) fromContacts += 1
        if (refused.length) steppedPast += 1
        note(
          row,
          'reachable',
          'kept',
          `has a usable email (${email})${recovered ? ', recovered from the packed contacts field' : ''}${
            refused.length
              ? ` — ${refused.slice(0, 2).join(', ')} on this row ${plural(refused.length, 'is', 'are')} ours, so we stepped past ${plural(refused.length, 'it', 'them')} to the next contact`
              : ''
          }`,
        )
        reachable.push({ row, email })
      }
      ctx.reachable = reachable
      const unreachable = ranked.length - reachable.length
      const noAddress = unreachable - onlyOurs
      // Where "ours" came from and which source could not be read. Printed
      // whenever this node actually refused somebody on that basis — a refusal
      // nobody can check is just a disappearance.
      const orgLines = steppedPast || onlyOurs ? ownOrgLines(org) : []

      if (!reachable.length) {
        return abandon(
          i,
          'failed',
          onlyOurs
            ? `None of the ${fmt(ranked.length)} leads has a usable email address that is not one of our own`
            : `None of the ${fmt(ranked.length)} leads has a usable email address`,
          [
            'Checked the email column and the packed contacts field on every row.',
            onlyOurs
              ? `${fmt(onlyOurs)} ${plural(onlyOurs, 'row carried', 'rows carried')} nobody but us — our own people are refused as recipients, so those rows have nobody to write to.`
              : '',
            orgLines[0] || '',
            'Nothing was created and nothing was sent.',
          ],
        )
      }

      finish(
        i,
        'done',
        onlyOurs
          ? `${fmt(reachable.length)} of ${fmt(ranked.length)} have a usable email address — ${fmt(onlyOurs)} dropped as our own people`
          : `${fmt(reachable.length)} of ${fmt(ranked.length)} have a usable email address`,
        [
          // The breakdown has to add up: a row dropped for being ours DID carry
          // an address, so it must not be counted under "no address".
          onlyOurs
            ? `${fmt(unreachable)} dropped: ${fmt(noAddress)} with no address on the row or in its contact block, ${fmt(onlyOurs)} because every address on the row was one of ours.`
            : unreachable
              ? `${fmt(unreachable)} dropped — no address on the row or in its contact block.`
              : 'Every row carried an address.',
          steppedPast || onlyOurs
            ? `${steppedPast ? `${fmt(steppedPast)} ${plural(steppedPast, 'row lists', 'rows list')} one of us among its contacts — we stepped past ${plural(steppedPast, 'that address', 'those addresses')} and wrote down the next real contact instead. ` : ''}${orgLines[0] || ''}`
            : '',
          fromContacts ? `${fmt(fromContacts)} recovered from the packed contacts field.` : '',
          orgLines[1] || '',
        ],
        { value: reachable.length, label: 'reachable' },
      )
    },

    // ── 3. already-contacted COMPANIES ────────────────────────────────────────
    // A lead is "new" only if NOBODY at its company has been reached out to.
    // Three outbound sources, each failing soft with an honest admission:
    //   (1) knownRecipients() — the workspace's own logged `email_sent`
    //       events, and nothing else. An address we merely wrote down
    //       somewhere is not somebody we emailed, and padding this set is
    //       what makes the next run call an untouched lead "already
    //       contacted". See the header of lib/outreachContacts.js.
    //   (2) readOutboundHistory() — the same events, kept WITH their dates, so
    //       an exclusion note can say when the touch happened.
    //   (3) readSentRecipients() — the connected inbox's real sent mail. Per
    //       Gmail's token model this is the SIGNED-IN user's own mailbox; the
    //       notes name it by its address (gmailStatus()), never by a person.
    //
    // None of these rejects on failure — degradation is structural (degraded /
    // sources flags, checked:false), and the claims below are downgraded from
    // "verified new" to "no match in what could be read" accordingly.
    contacted: async () => {
      const i = at('contacted')
      await start(i)
      const { reachable } = ctx
      let known = new Set()
      let sendsSource = { ok: false, mode: 'unavailable', count: 0 }
      let historyComplete = false
      const [kres, hist, sent, mail] = await Promise.all([
        knownRecipients().catch(() => null),
        readOutboundHistory().catch(() => ({ byAddress: new Map() })),
        readSentRecipients(signal).catch((err) => {
          if (err?.name === 'AbortError') throw err
          return { checked: false, address: '', reason: err?.message || 'the sent-mail read failed' }
        }),
        // (4) THE HUBSPOT MIRROR — the system of record for what Bo actually
        // sent. He sends from HubSpot, so this is where his real outbound
        // lives; the workspace log only ever holds sends this app made, and
        // this app never sends. Without this source a company Bo has emailed
        // five times reads as untouched and gets a COLD INTRO, which is the
        // single most embarrassing thing this pipeline could do.
        readMailHistory({ signal, now, sharedMailHost: isFreemailDomain }).catch((err) => {
          if (err?.name === 'AbortError') throw err
          return { available: false, reason: err?.message || 'the HubSpot mirror could not be read', threads: new Map() }
        }),
      ])
      if (kres) {
        known = kres.addresses instanceof Set ? kres.addresses : new Set(kres.addresses || [])
        sendsSource = kres.sources?.sends || sendsSource
        historyComplete = kres.degraded === false
      }
      if (signal?.aborted) throw abortError()

      // How the workspace history fell short, in the user's words. Empty
      // exactly when historyComplete is true — the log is the only lane there
      // is to fall short.
      const historyGap = sendsSource.ok
        ? ''
        : sendsSource.mode === 'local'
          ? 'Send history came only from this browser’s local copy of the activity log — sends from another device or browser are not in it.'
          : 'Send history could not be read at all — the activity log did not answer.'

      // The company gate: every touched address, indexed by address, corporate
      // domain, and the directory rows it belongs to. Built over the RAW scope
      // (pre-dedup), so an address held only by a collapsed duplicate row still
      // marks that company as touched.
      // Every counterpart the mirror shows a DELIVERED outbound touch to, in
      // the same shape the activity log contributes: address -> last touch.
      // Bounced/failed rows are not touches (mailHistory already excludes
      // them), and an inbound-only thread is not a touch either — somebody
      // writing to us does not mean we ever wrote to them.
      const mirrorDates = new Map()
      let mirrorTouched = 0
      if (mail?.available && mail.threads instanceof Map) {
        for (const [addr, t] of mail.threads) {
          if (!t?.lastOutboundAt) continue
          const at = t.lastOutboundAt instanceof Date ? t.lastOutboundAt : new Date(t.lastOutboundAt)
          if (Number.isNaN(at.getTime())) continue
          mirrorTouched += 1
          const prev = mirrorDates.get(addr)
          if (!prev || at.getTime() > prev.lastTouchAt.getTime()) {
            mirrorDates.set(addr, {
              days: new Set([chicagoDayOf(at)]),
              lastTouchAt: at,
              lastSubject: String(t.lastOutbound?.subject || ''),
              source: 'hubspot',
            })
          }
        }
        for (const addr of mirrorDates.keys()) known.add(addr)
      }
      // One combined date index: the log's dates, then the mirror's (the
      // mirror wins a tie only when it is the more recent touch).
      const logDates = hist?.byAddress instanceof Map ? new Map(hist.byAddress) : new Map()
      for (const [addr, rec] of mirrorDates) {
        const prev = logDates.get(addr)
        if (!prev || rec.lastTouchAt.getTime() > prev.lastTouchAt.getTime()) logDates.set(addr, rec)
      }

      const gate = buildCompanyGate({
        known,
        logDates,
        sent,
        scopeRows: (Array.isArray(rows) ? rows : []).filter(Boolean),
      })
      const sentAddr = sent.checked ? sent.address || 'the connected mailbox' : ''
      // What a "new" verdict may honestly claim, given what was actually read:
      // "nobody has been contacted" is only claimable when BOTH lanes answered;
      // anything less is "no match in what could be read", with the gap named.
      const sentGap = String(sent.reason || 'the sent-mail read failed').slice(0, 60)
      const mirrorRead = Boolean(mail?.available)
      // The mirror is the strongest source (it holds what Bo really sent), so
      // it decides how confident the "new" verdict may sound. Unread mirror =>
      // the claim is downgraded and the gap is named, never hidden.
      const mirrorGap = String(mail?.reason || 'the HubSpot mirror could not be read').slice(0, 70)
      const keptNote = (label) => {
        if (!mirrorRead) {
          return `new — no touch at ${label} in what could be read (HubSpot email history was NOT checked — ${mirrorGap})`
        }
        const lanes = ['HubSpot email history']
        if (historyComplete) lanes.push('the workspace log')
        if (sent.checked) lanes.push(`${sentAddr}’s sent mail`)
        // The gap named is the sent-mail read's own reason, and ONLY when that
        // read failed: a partial workspace-log read with Gmail read fine used
        // to print "(the sent-mail read failed)" here, which was untrue.
        return historyComplete && sent.checked
          ? `new — nobody at ${label} has been contacted (checked ${lanes.join(', ')})`
          : `new — no touch at ${label} in ${lanes.join(' or ')}${sent.checked ? '' : ` (${sentGap})`}`
      }

      const fresh = []
      let excluded = 0
      for (const candidate of reachable) {
        const touch = companyTouchOf(candidate, gate)
        if (touch) {
          excluded += 1
          const { ev, how } = touch
          note(
            candidate.row,
            'contacted',
            'dropped',
            how === 'own'
              ? `already contacted — ${ev.addr} on this row ${touchPhrase(ev, sentAddr)}`
              : how === 'domain'
                ? `excluded — ${ev.addr} at ${touch.domain} ${touchPhrase(ev, sentAddr)}`
                : `excluded — ${ev.addr} (${touch.name || 'a directory row'}, same company) ${touchPhrase(ev, sentAddr)}`,
          )
          continue
        }
        const label = companyLabelOf(candidate) || 'this company'
        note(candidate.row, 'contacted', 'kept', keptNote(label))
        fresh.push(candidate)
      }
      ctx.fresh = fresh

      if (!fresh.length) {
        return abandon(
          i,
          'failed',
          `All ${fmt(reachable.length)} reachable leads are at companies already contacted`,
          [
            'Every remaining lead matched the outreach history at the company level — its own addresses, its corporate domain, or a directory row of the same company.',
            'Nothing was created and nothing was sent.',
          ],
        )
      }

      finish(
        i,
        'done',
        excluded
          ? `${fmt(excluded)} at already-contacted companies — excluded, ${fmt(fresh.length)} left`
          : historyComplete && sent.checked
            ? `0 companies already contacted — all ${fmt(fresh.length)} are untouched`
            : `Nobody matched the history we could read — ${fmt(fresh.length)} carried forward`,
        [
          mirrorRead
            ? `${fmt(known.size)} known ${plural(known.size, 'recipient', 'recipients')}: ${fmt(sendsSource.count)} from this workspace’s own logged sends, ${fmt(mirrorDates.size)} from HubSpot email history.`
            : `${fmt(known.size)} known ${plural(known.size, 'recipient', 'recipients')}, all from this workspace’s own logged sends — HubSpot email history could not be read.`,
          sent.checked
            ? `${sentAddr}’s sent mail read too: ${fmt(sent.recipients.size)} distinct ${plural(sent.recipients.size, 'recipient', 'recipients')} across ${fmt(sent.messagesRead)} sent ${plural(sent.messagesRead, 'message', 'messages')} (at most ${fmt(SENT_SCAN_MAX)} scanned).`
            : `Only the workspace activity log was checked — ${sent.reason}.`,
          'New = nobody at the company: matched on exact address, shared corporate domain, and sibling directory rows of the same company. Freemail addresses (gmail.com and the like) only ever match exactly — a shared provider is not a company.',
          historyComplete
            ? ''
            : `${historyGap} So this is a floor, not a verified count — some of these companies may already have been emailed. Review the batch before writing to anyone.`,
        ],
        { value: excluded, label: 'excluded' },
      )
    },

    // ── 4. the HubSpot mirror ─────────────────────────────────────────────────
    // ONE read of the match index for the whole eligible set (it is module-cached
    // in hubspot.js, so a second operation in the same session pays nothing), and
    // then a purely local resolve per lead. This node is the fork point: both
    // branches below need the lead → company mapping and nothing else, so both
    // start the moment this finishes.
    //
    // FAIL SOFT IS THE CONTRACT. hubspotIndex() resolves null for every failure
    // mode there is — never configured, unreachable, not a member of the portal —
    // and a null index means the branches report an honest skip and the join
    // carries every lead forward. A CRM that is down never costs a lead.
    'hs-match': async () => {
      const i = at('hs-match')
      await start(i)
      const { fresh } = ctx
      let index = null
      try {
        index = await hubspotIndex()
      } catch {
        index = null
      }
      if (signal?.aborted) throw abortError()

      const hits = new Map() // position in `fresh` -> company id
      let ambiguous = 0
      if (index && index.companies) {
        fresh.forEach((candidate, position) => {
          const hit = crmCompanyId(index, candidate.row, candidate.email)
          if (hit.ambiguous) ambiguous += 1
          if (hit.id) hits.set(position, hit.id)
          // A match is a lookup, never an exclusion: every verdict here is
          // 'kept', and the note says which of the three keys answered.
          note(
            candidate.row,
            'hs-match',
            'kept',
            hit.ambiguous
              ? 'its key points at more than one HubSpot company, so it counts as no match'
              : hit.id
                ? `matched HubSpot company ${hit.id}`
                : 'no HubSpot company matched its domain, email or name',
          )
        })
      } else {
        for (const candidate of fresh) {
          note(
            candidate.row,
            'hs-match',
            'skipped',
            'the HubSpot mirror could not be read, so no CRM match was attempted',
          )
        }
      }
      ctx.crm = { available: Boolean(index && index.companies), index, hits, ambiguous }

      if (!ctx.crm.available) {
        finish(
          i,
          'skipped',
          'HubSpot mirror not available — no lead is being screened against the CRM',
          [
            'The mirror is not connected for this workspace, or it could not be read from this browser.',
            'Nothing is dropped because a system was unreachable: every eligible lead carries forward.',
            'Read-only either way — this pipeline has no path that writes to HubSpot.',
          ],
          { value: 0, label: 'in HubSpot' },
        )
        return
      }

      finish(
        i,
        'done',
        `${fmt(hits.size)} of ${fmt(fresh.length)} eligible leads are in the HubSpot mirror`,
        [
          'One index read for the whole set, matched locally: website domain, then contact email, then company name.',
          ambiguous
            ? `${fmt(ambiguous)} ${plural(ambiguous, 'lead', 'leads')} hit a key that points at more than one company — treated as no match, because a coin flip is not a match.`
            : 'Every key that matched pointed at exactly one company.',
          'Read-only: deals and tasks are read next, in parallel, and nothing is written back.',
        ],
        { value: hits.size, label: 'in HubSpot' },
      )
    },

    // ── 5a. open deals (parallel branch) ──────────────────────────────────────
    // Cheap for the whole set: the index rollup already carries open_deals per
    // company, so screening every eligible lead costs no extra request. When the
    // rollup is absent the branch falls back to opening a bounded window of
    // companies directly and reading HubSpot's own is_closed flag.
    'hs-deals': async () => {
      const i = at('hs-deals')
      await start(i)
      const { fresh, crm } = ctx
      crm.blocked = new Set()

      if (!crm.available) {
        for (const candidate of fresh) {
          note(
            candidate.row,
            'hs-deals',
            'skipped',
            'the HubSpot mirror could not be read, so no deal check ran and it was left in',
          )
        }
        finish(
          i,
          'skipped',
          'No CRM to read — 0 leads excluded on deals',
          [
            'The HubSpot mirror could not be read, so nobody was screened out on an open deal.',
            'A lead is never dropped because a system was down.',
          ],
          { value: 0, label: 'open deals' },
        )
        return
      }
      if (!crm.hits.size) {
        for (const candidate of fresh) {
          note(
            candidate.row,
            'hs-deals',
            'skipped',
            'not in the HubSpot mirror, so there is no deal for it to collide with',
          )
        }
        finish(
          i,
          'done',
          'None of these leads is in HubSpot — no deal to collide with',
          [
            'Nothing to check: not one eligible lead matched a mirrored company.',
            '0 excluded, and that is a read fact rather than an assumption.',
          ],
          { value: 0, label: 'open deals' },
        )
        return
      }

      // A lead the mirror knows nothing about was never screened here, and says
      // so rather than borrowing the clean verdict of the leads that were.
      fresh.forEach((candidate, position) => {
        if (crm.hits.has(position)) return
        note(
          candidate.row,
          'hs-deals',
          'skipped',
          'not in the HubSpot mirror, so there is no deal for it to collide with',
        )
      })

      const companies = crm.index.companies || {}
      let rollupsRead = 0
      const noRollup = []
      for (const [position, companyId] of crm.hits) {
        const open = rollupOpenDeals(companies[companyId])
        if (open === null) {
          noRollup.push(position)
          continue
        }
        rollupsRead += 1
        if (open > 0) crm.blocked.add(position)
        note(
          fresh[position]?.row,
          'hs-deals',
          open > 0 ? 'dropped' : 'kept',
          open > 0
            ? `HubSpot company ${companyId} has ${fmt(open)} open ${plural(open, 'deal', 'deals')} — somebody is already selling to it`
            : `no open deal on HubSpot company ${companyId}`,
        )
      }

      // No rollup at all: open a bounded window of companies rather than call an
      // unread field "zero deals".
      let probed = 0
      let unread = 0
      let companiesOpened = 0
      if (!rollupsRead) {
        // One request per COMPANY, but every lead sitting on that company is
        // screened by the answer — two leads that resolve to the same HubSpot
        // record must not have one of them quietly skipped.
        const positionsById = new Map()
        for (const [position, companyId] of crm.hits) {
          if (!positionsById.has(companyId)) {
            if (positionsById.size >= HS_PROBE_LIMIT) continue
            positionsById.set(companyId, [])
          }
          positionsById.get(companyId).push(position)
        }
        companiesOpened = positionsById.size
        // Everything the cap left out: not read, so not judged.
        for (const [position, companyId] of crm.hits) {
          if (positionsById.has(companyId)) continue
          note(
            fresh[position]?.row,
            'hs-deals',
            'skipped',
            `outside the ${fmt(HS_PROBE_LIMIT)}-company window this pass opens, so its deals were not read`,
          )
        }
        const results = await probeCompanies([...positionsById.keys()], signal)
        for (const { id, data } of results) {
          const positions = positionsById.get(id) || []
          if (!data) {
            unread += positions.length
            for (const position of positions) {
              note(
                fresh[position]?.row,
                'hs-deals',
                'skipped',
                `HubSpot company ${id} could not be opened — its deals are unread, not clean, so it was left in`,
              )
            }
            continue
          }
          probed += positions.length
          const openDeal = (data.deals || []).some(isOpenDeal)
          for (const position of positions) {
            if (openDeal) crm.blocked.add(position)
            note(
              fresh[position]?.row,
              'hs-deals',
              openDeal ? 'dropped' : 'kept',
              openDeal
                ? `HubSpot company ${id} carries an open deal — somebody is already selling to it`
                : `HubSpot company ${id} was opened directly: no open deal`,
            )
          }
        }
      }

      // Mixed mirror: some companies carried a rollup and some did not. The ones
      // that did not were never checked here (the direct probe only runs when
      // NOTHING had a rollup), so they get an admission, not a verdict.
      if (rollupsRead) {
        for (const position of noRollup) {
          note(
            fresh[position]?.row,
            'hs-deals',
            'skipped',
            'its HubSpot company carries no open-deal rollup, so it was left in rather than dropped',
          )
        }
      }

      const blocked = crm.blocked.size
      // Both counts are in LEADS, never in companies: one company can carry two
      // of these leads, and "checked 12" has to mean twelve leads screened.
      const checked = rollupsRead || probed
      crm.dealsChecked = checked
      crm.dealsUnread = crm.hits.size - checked

      if (!checked) {
        finish(
          i,
          'skipped',
          'Deal status could not be read — 0 leads excluded',
          [
            `${fmt(crm.hits.size)} matched ${plural(crm.hits.size, 'lead', 'leads')} carried no deal rollup, and ${fmt(unread)} could not be opened directly.`,
            'Nothing is excluded on a fact we could not read.',
          ],
          { value: 0, label: 'open deals' },
        )
        return
      }

      finish(
        i,
        'done',
        blocked
          ? `${fmt(blocked)} of ${fmt(checked)} checked ${plural(checked, 'lead is', 'leads are')} already in an open deal`
          : `None of the ${fmt(checked)} checked ${plural(checked, 'lead is', 'leads are')} in an open deal`,
        [
          rollupsRead
            ? 'Read from the mirror’s own open-deal rollup — HubSpot’s is_closed flag, not our guess.'
            : `Opened ${fmt(companiesOpened)} ${plural(companiesOpened, 'company', 'companies')} directly (capped at ${fmt(HS_PROBE_LIMIT)}) because the index carried no deal rollup.`,
          crm.dealsUnread > 0
            ? `${fmt(crm.dealsUnread)} matched ${plural(crm.dealsUnread, 'lead', 'leads')} could not be read for deals and ${plural(crm.dealsUnread, 'is', 'are')} left in rather than dropped.`
            : '',
          blocked
            ? 'Somebody is already selling to these — the join takes them out of this batch.'
            : 'So nobody is excluded on deals. That is what the portal actually says.',
        ],
        { value: blocked, label: 'open deals' },
      )
    },

    // ── 5b. open tasks (parallel branch) ──────────────────────────────────────
    // Runs at the same time as the deal branch and asks a different question of a
    // different endpoint: per-company task lists, one request each, bounded and
    // deterministic (the top of an already-ranked list, never a sample). It is
    // strictly informational — an open task never excludes a lead, it just means
    // a colleague is already on it, and the join says so.
    'hs-tasks': async () => {
      const i = at('hs-tasks')
      await start(i)
      const { fresh, crm } = ctx
      crm.tasks = { probed: 0, withOpen: 0, open: 0, unread: 0, capped: 0 }

      if (!crm.available) {
        for (const candidate of fresh) {
          note(
            candidate.row,
            'hs-tasks',
            'skipped',
            'the HubSpot mirror could not be read, so no CRM task could be counted',
          )
        }
        finish(
          i,
          'skipped',
          'No CRM to read — open tasks unknown',
          [
            'The HubSpot mirror could not be read, so no task could be counted.',
            'Nothing is claimed about who is already working these leads.',
          ],
          { value: 0, label: 'open tasks' },
        )
        return
      }
      if (!crm.hits.size) {
        for (const candidate of fresh) {
          note(
            candidate.row,
            'hs-tasks',
            'skipped',
            'not in the HubSpot mirror, so it has no CRM task to find',
          )
        }
        finish(
          i,
          'done',
          'None of these leads is in HubSpot — no task to find',
          ['Nothing to check: not one eligible lead matched a mirrored company.'],
          { value: 0, label: 'open tasks' },
        )
        return
      }

      const ids = []
      for (const companyId of crm.hits.values()) {
        if (ids.includes(companyId)) continue
        if (ids.length >= HS_PROBE_LIMIT) break
        ids.push(companyId)
      }
      const distinct = new Set(crm.hits.values()).size
      const capped = Math.max(0, distinct - ids.length)

      // Which leads sit on each company we are about to open, so one answer can
      // be recorded against every lead it actually covers.
      const opening = new Set(ids)
      const positionsById = new Map()
      fresh.forEach((candidate, position) => {
        const companyId = crm.hits.get(position)
        if (!companyId) {
          note(
            candidate.row,
            'hs-tasks',
            'skipped',
            'not in the HubSpot mirror, so it has no CRM task to find',
          )
          return
        }
        if (!opening.has(companyId)) {
          note(
            candidate.row,
            'hs-tasks',
            'skipped',
            `outside the ${fmt(HS_PROBE_LIMIT)}-company window this pass opens, so its tasks were not read`,
          )
          return
        }
        if (!positionsById.has(companyId)) positionsById.set(companyId, [])
        positionsById.get(companyId).push(position)
      })

      const results = await probeCompanies(ids, signal)
      let open = 0
      let withOpen = 0
      let unread = 0
      for (const { id, data } of results) {
        const positions = positionsById.get(id) || []
        if (!data) {
          unread += 1
          for (const position of positions) {
            note(
              fresh[position]?.row,
              'hs-tasks',
              'skipped',
              `HubSpot company ${id} could not be opened — its tasks are unread, not clean`,
            )
          }
          continue
        }
        const count = (data.tasks || []).filter(isOpenTask).length
        if (count > 0) withOpen += 1
        open += count
        for (const position of positions) {
          // An open task never excludes anybody — this branch is a note on the
          // lead, which is why every verdict here is 'kept'.
          note(
            fresh[position]?.row,
            'hs-tasks',
            'kept',
            count > 0
              ? `${fmt(count)} open CRM ${plural(count, 'task', 'tasks')} on HubSpot company ${id} — a colleague is already on it`
              : `no open CRM task on HubSpot company ${id}`,
          )
        }
      }
      crm.tasks = { probed: ids.length - unread, withOpen, open, unread, capped }

      // Every read failed: "No open CRM task on the 0 companies checked" would
      // be a read failure wearing the costume of a clean result. Say what
      // actually happened — nothing was checked, so nothing is claimed.
      if (ids.length > 0 && unread === ids.length) {
        finish(
          i,
          'skipped',
          'Task status could not be read — nothing is claimed',
          [
            `All ${fmt(ids.length)} matched ${plural(ids.length, 'company', 'companies')} failed to open, so not one task list was read.`,
            'An unread company is unread, not clean: nothing is claimed about who is already working these leads.',
          ],
          { value: 0, label: 'open tasks' },
        )
        return
      }

      finish(
        i,
        'done',
        open
          ? `${fmt(open)} open CRM ${plural(open, 'task', 'tasks')} across ${fmt(withOpen)} of ${fmt(ids.length - unread)} companies checked`
          : `No open CRM task on the ${fmt(ids.length - unread)} ${plural(ids.length - unread, 'company', 'companies')} checked`,
        [
          'Read one company at a time from the mirror, at the same time as the deal check — HubSpot’s own task status, not a guess.',
          capped
            ? `${fmt(distinct)} matched ${plural(distinct, 'company', 'companies')} in total; the top ${fmt(ids.length)} by lead score were opened and ${fmt(capped)} were not.`
            : 'Every matched company was checked.',
          unread ? `${fmt(unread)} could not be opened and ${plural(unread, 'is', 'are')} counted as unread, not as clean.` : '',
          open
            ? 'An open task does not exclude anybody — it is a note that a colleague is already on them.'
            : '',
        ],
        { value: open, label: 'open tasks' },
      )
    },

    // ── 6. the join ───────────────────────────────────────────────────────────
    // Uses BOTH branches: the deal branch decides who is excluded, the task
    // branch adds the note. It runs only once both have settled — that is what
    // dependsOn: ['hs-deals','hs-tasks'] means — and it proceeds with the full
    // eligible set whenever the CRM could not be read.
    'hs-screen': async () => {
      const i = at('hs-screen')
      await start(i)
      const { fresh, crm } = ctx
      const blocked = crm.blocked || new Set()
      const screened = fresh.filter((_, position) => !blocked.has(position))
      ctx.screened = screened
      // The join is where an open deal actually costs a lead its place, so this
      // is the node that records the drop. The reason was found by hs-deals and
      // is on that node's own entry.
      fresh.forEach((candidate, position) => {
        const out = blocked.has(position)
        note(
          candidate.row,
          'hs-screen',
          out ? 'dropped' : 'kept',
          out
            ? 'taken out of this batch — the CRM says it is already in an open deal'
            : crm.available
              ? 'nothing in the CRM excludes it'
              : 'carried forward — the CRM could not be read and nothing is dropped on that',
        )
      })
      const excluded = fresh.length - screened.length
      ctx.crmExcluded = excluded

      if (!screened.length) {
        return abandon(
          i,
          'skipped',
          `Every one of the ${fmt(fresh.length)} eligible leads is already in an open deal`,
          [
            'There is nobody left to open a cold conversation with — they are all live opportunities already.',
            'Nothing was created and nothing was sent.',
          ],
        )
      }

      const tasks = crm.tasks || { open: 0, withOpen: 0, probed: 0 }
      finish(
        i,
        excluded ? 'done' : 'skipped',
        excluded
          ? `${fmt(excluded)} excluded — already in an open deal, ${fmt(screened.length)} left`
          : `0 excluded — none of these leads is in an open deal`,
        [
          crm.available
            ? 'Both CRM checks ran at the same time; this step waited for both and used both.'
            : 'The HubSpot mirror could not be read, so every eligible lead carried forward — nothing was dropped because a system was down.',
          tasks.open
            ? `${fmt(tasks.open)} open ${plural(tasks.open, 'task', 'tasks')} on ${fmt(tasks.withOpen)} of these companies — kept, but somebody is already on them.`
            : crm.available && tasks.probed
              ? 'No open CRM task on the companies checked, so nobody is being double-worked.'
              : '',
          excluded
            ? 'Excluded, not deleted: they stay in the directory and in HubSpot, they are just not cold-outreach targets today.'
            : 'On this portal that is the usual answer, and saying so beats implying work that did not happen.',
        ],
        { value: excluded, label: 'excluded' },
      )
    },

    // ── 7. web research ───────────────────────────────────────────────────────
    // THE KEY PASS, and the one place this engine may spend money.
    //
    // Real research through the enrichment API, for the leads that made the
    // batch and for nobody else:
    //   1. A free cache lookup per lead (GET /enrich/lead) — a dossier anyone
    //      in the workspace already paid for merges at no cost.
    //   2. Cache misses go to the paid pipeline (POST /enrich/start, batches
    //      of MAX_ENRICH_ROWS, the server enforces the same cap), polled until
    //      done or until the hard time budget expires.
    //
    // The merge NEVER changes who is in the batch. A lead whose research
    // failed, timed out, or could not be asked for stays selected, carries no
    // research field at all, and its trace says the draft derives from the
    // import row — an admission, not a failure. That is the standing rule: a
    // researched claim may only exist where a dossier actually exists.
    enriched: async () => {
      const i = at('enriched')
      await start(i)

      // RESEARCHED LEADS GO FIRST. Every lead still standing has passed the
      // gates, so all that is left is the order we take them in — and a lead
      // the workspace ALREADY researched carries real web evidence into its
      // draft for free, while an unresearched one usually cannot (the paid
      // pass below is bounded by a hard time budget, so most misses stay
      // unresearched on any given run). Cutting by score alone meant a
      // fifty-lead run could land entirely on unresearched companies while
      // paid-for dossiers sat unused, and every card then read "from their
      // directory row".
      //
      // This reorders WITHIN the eligible set and changes nothing about who is
      // eligible — the gate, the exclusions and the score still decide that.
      // Array#sort is stable and the rank is the only key, so score order
      // survives inside each group. A failed hint yields an empty set and the
      // order is exactly what it was.
      // HOW MANY LEADS THE REORDER ACTUALLY PROMOTED past the cut is measured
      // here, and it is not decoration: this node's own summary has always
      // said "the N highest-scoring of M eligible leads", and the moment this
      // reorder really fires that sentence stops being true. (It never fired
      // before — researchedKeys threw on every call and returned an empty set,
      // so the claim was accidentally accurate. Fixing that bug is what made
      // this one reachable.) The count is kept so the summary can tell the
      // truth about the batch it actually cut.
      let researchedFirst = 0
      let promoted = 0
      try {
        const known = await researchedKeys(category || '')
        if (known && known.size) {
          const rank = (c) => (known.has(String(keyOf(c.row) || '')) ? 0 : 1)
          const byScore = new Set(
            ctx.screened.slice(0, op.targetCount).map((c) => String(keyOf(c.row) || '')),
          )
          ctx.screened = ctx.screened.slice().sort((a, b) => rank(a) - rank(b))
          const taken = ctx.screened.slice(0, op.targetCount)
          researchedFirst = taken.filter((c) => rank(c) === 0).length
          // Leads in the batch that the score alone would NOT have taken.
          promoted = taken.filter((c) => !byScore.has(String(keyOf(c.row) || ''))).length
        }
      } catch {
        /* the hint is an optimisation; its absence is never an error */
      }
      ctx.researchPromoted = promoted

      // THE PINNED BATCH outranks both the score and the research reorder:
      // the leads the last run showed come back first, in the order they were
      // shown, until each is done or reached out to. The reorder above still
      // decides which NEW leads fill a freed slot. Scope is the deduped source
      // pool — a pinned lead outside it (a scoped run) is held, not retired.
      // See applyBatchPin() / lib/batchPin.js.
      const pin = applyBatchPin({
        op,
        category: category || ctx.screened[0]?.row?._category || '',
        eligible: ctx.screened,
        keyOf: (c) => String(keyOf(c.row) || ''),
        inScopeKeys: ctx.ranked.map((r) => String(keyOf(r) || '')),
        now,
        note: (key, nodeId, verdict, text) => tracer.note(key, nodeId, verdict, text),
        nodeId: 'enriched',
      })
      ctx.screened = pin.sequence
      ctx.pin = pin

      const candidates = ctx.screened.slice(0, op.targetCount)
      ctx.candidates = candidates

      // This node is where the batch is actually cut to size, so it is the node
      // that owes the leftovers an explanation: they survived every filter and
      // simply ranked below the line.
      ctx.screened.forEach((candidate, position) => {
        if (position >= op.targetCount) {
          note(
            candidate.row,
            'enriched',
            'dropped',
            `ranked #${fmt(position + 1)} of ${fmt(ctx.screened.length)} eligible — below the ${fmt(op.targetCount)} this run takes${
              pin.carriedKeys.has(String(keyOf(candidate.row) || ''))
                ? '; still pinned from an earlier run, it keeps its place in line'
                : ''
            }`,
          )
        }
      })

      // Live progress: the node is long-running (a fresh batch takes minutes),
      // so it re-emits its own headline as results land instead of going dark.
      // `pending` is not a failure count. It is the number of leads whose
      // dossier the pipeline is still producing — leads this run deliberately
      // leaves undrafted so the draft can be written from the research instead
      // of from the row it was going to arrive too late for.
      const stats = { cached: 0, fresh: 0, timedOut: 0, failed: 0, pending: 0 }
      const report = () => {
        passes[i] = {
          ...passes[i],
          headline: `researched ${fmt(stats.cached + stats.fresh)} of ${fmt(candidates.length)} — ${fmt(stats.cached)} cached, ${fmt(stats.fresh)} fresh`,
          metric: { value: stats.cached + stats.fresh, label: 'researched' },
        }
        emit(i)
      }

      const cat = category || candidates[0]?.row?._category || ''
      // Stored on the run so the receipt can finish what this node started: a
      // free cache read is keyed by (category, lead key), and the client must
      // not have to guess the category it was researched under.
      ctx.researchCategory = cat
      const deadline = Date.now() + researchBudgetMs
      const items = candidates.map((c) => ({ c, key: String(keyOf(c.row) || '') }))

      // 1) the free cache pass
      const looked = await lookupCachedDossiers({
        items,
        category: cat,
        signal,
        deadline,
        merge: (item, dossier, at, exec) => {
          item.c.row = researchedRowOf(item.c.row, dossier, { mode: 'cached', at, exec })
        },
      })
      stats.cached = looked.cached
      let unavailable = looked.unavailable
      if (stats.cached) report()

      // 2) the paid pipeline for the misses. A lookup that errored is retried
      // here too — the server re-checks its own cache before spending.
      const pending = new Map() // key -> item, still owed an answer
      // WHO THE PIPELINE ACTUALLY TOOK. A lead only belongs here once the
      // server has accepted responsibility for researching it — either it took
      // the job now, or its spend ledger says the workspace already bought this
      // dossier and something else is producing it. Membership is the whole
      // difference between "the answer is still coming" (the card waits for it)
      // and "nobody is buying this" (draft from the row and say so), and the
      // budget expiring must not blur the two: a lead the loop never even
      // reached was never asked for, and waiting on it would be waiting on
      // nothing.
      const accepted = new Set()
      if (!unavailable) {
        for (const item of looked.misses) pending.set(item.key, item)
        const batches = []
        for (let s = 0; s < looked.misses.length && !unavailable; s += MAX_ENRICH_ROWS) {
          if (Date.now() >= deadline) break // unstarted rows fall to the timeout admission
          if (signal?.aborted) throw abortError()
          const chunk = looked.misses.slice(s, s + MAX_ENRICH_ROWS)
          try {
            const started = await raceResearch(
              startEnrich(chunk.map((item) => item.c.row), cat, { signal }),
              { signal, deadline },
            )
            if (started?.configured === false) {
              unavailable = 'enrichment is not configured on the API (no research key)'
              break
            }
            // NO BATCH ID, NO POLLING. startEnrich answers { batchId: '' } when
            // its spend ledger says every row in this chunk was already paid
            // for, so nothing was enqueued and there is no batch to ask about.
            // Registering it anyway meant polling `status?batchId=` — a query
            // for no batch — and then merging whatever jobs came back into any
            // lead whose key happened to match. That is a dossier arriving from
            // somewhere other than this lead's own research: the exact
            // fabrication the key guard below exists to prevent, walked around
            // by an empty id. These rows stay in `pending` and are admitted as
            // unresearched rather than decorated with someone else's facts.
            // THE LEDGER-SKIPPED ROWS. startEnrich strips rows its spend ledger
            // says the workspace already paid for and reports them as
            // `skipped`. They were NOT enqueued now, and whether an answer is
            // coming is a question only the SERVER can answer: the ledger's own
            // 30-minute grace window keeps entries for a purchase that bought
            // nothing at all (the credit-outage case enrich.js documents), and
            // waiting on one of those is waiting on nothing. So a skipped row
            // is accepted only when the free lookup above saw the server's
            // in-flight marker for it. They are deliberately NOT registered on
            // the batch below — the server never returns a job for a row it
            // never took.
            const skipped = new Set((Array.isArray(started?.skipped) ? started.skipped : []).map(String))
            // THE ROWS THE SERVER ITSELF SAYS ARE IN FLIGHT. _start loses the
            // in-flight claim to the backfill (or another tab) and answers a
            // job with `pending:true` and no jobId — nothing enqueued, and when
            // that is every row of the chunk, `batchId` is '' too. Those rows
            // are not `skipped` (the ledger let them through), so they must be
            // accepted HERE, before the no-batch `continue` below: a dossier IS
            // coming for them, and letting them fall to the timeout admission
            // drafts them from the row now and freezes the write-once slot
            // before it lands. The free lookup's own marker counts the same.
            const inflight = new Set(
              (Array.isArray(started?.jobs) ? started.jobs : [])
                .filter((j) => j?.pending === true)
                .map((j) => String(j?.key || '')),
            )
            for (const item of chunk) {
              if (item.serverPending || inflight.has(item.key)) accepted.add(item.key)
            }
            const batchId = String(started?.batchId || '')
            if (!batchId) continue
            // ONLY THE KEYS THE SERVER ACTUALLY TOOK. Registering the whole
            // chunk left `b.keys` permanently non-empty whenever the ledger had
            // stripped even one row: the poll loop below then span until the
            // 240s budget expired and labelled every lead in the batch
            // 'timeout' — "research did not finish in time" — when the truth
            // was that some of them were never asked for in this run at all.
            const owed = chunk.map((item) => item.key).filter((k) => !skipped.has(k))
            // A chunk the ledger emptied owes nothing and gets no batch entry.
            if (!owed.length) continue
            for (const key of owed) accepted.add(key)
            batches.push({ id: batchId, keys: new Set(owed) })
          } catch (err) {
            if (err?.name === 'AbortError') throw err
            // Time ran out, or the enqueue request hung past the budget. This
            // is the one branch where the chunk's fate is genuinely UNKNOWN:
            // raceResearch stops us waiting, it does not cancel the POST, so
            // the server may still accept and bill every row in it. Unknown is
            // not "nobody is researching them" — file the chunk as accepted and
            // let the receipt ask the server, which is the only authority.
            // A POST that never landed costs exactly one free cache read: the
            // card gets a clean found:false with no pending marker and drafts
            // from the row immediately.
            if (err?.name === 'ResearchBudgetError') {
              for (const item of chunk) accepted.add(item.key)
              break
            }
            unavailable = String(err?.message || 'the enrichment API refused the batch')
            break
          }
        }
        // Poll every outstanding batch until everything settles or time is up.
        // First poll is immediate: cache hits the server found on enqueue are
        // already done.
        while (batches.some((b) => b.keys.size) && Date.now() < deadline) {
          if (signal?.aborted) throw abortError()
          for (const b of batches) {
            if (!b.keys.size) continue
            let st = null
            try {
              st = await raceResearch(enrichStatus(b.id, { signal }), { signal, deadline })
            } catch (err) {
              if (err?.name === 'AbortError') throw err
              // Deadline passed while this poll was on the wire — possibly a
              // fetch that will never answer. Stop polling entirely; the
              // while-condition and waitMs guard end the loop, and everything
              // still pending falls to the timeout admission.
              if (err?.name === 'ResearchBudgetError') break
              continue // transient — the budget bounds how long we keep trying
            }
            for (const job of Array.isArray(st?.jobs) ? st.jobs : []) {
              const jobKey = String(job?.key || '')
              if (!b.keys.has(jobKey)) continue
              const item = pending.get(jobKey)
              if (!item) {
                b.keys.delete(jobKey)
                continue
              }
              if (job.status === 'done') {
                const mode = job.cached ? 'cached' : 'fresh'
                item.c.row = researchedRowOf(item.c.row, job.result || {}, {
                  mode,
                  // A cached job carries WHEN the research really ran; a fresh
                  // one finished just now, and says so.
                  at: job.cached ? String(job.cachedFrom || '') : new Date().toISOString(),
                  exec: job.exec || null,
                })
                stats[mode] += 1
                b.keys.delete(jobKey)
                pending.delete(jobKey)
              } else if (job.status === 'error') {
                item.failure = 'failed'
                stats.failed += 1
                b.keys.delete(jobKey)
                pending.delete(jobKey)
              }
            }
          }
          report()
          if (!batches.some((b) => b.keys.size)) break
          const waitMs = Math.min(researchPollMs, Math.max(0, deadline - Date.now()))
          if (waitMs <= 0) break
          await beat(signal, waitMs)
        }
      }

      // Whatever is still owed an answer, split three ways — because "no
      // dossier yet" has three genuinely different futures and the draft that
      // gets written depends on which one this is:
      //
      //   unavailable  the API refused. Nothing is coming. Draft from the row.
      //   pending      the pipeline TOOK this lead (or the ledger says the
      //                workspace is already buying it) and the run's own budget
      //                expired first. The dossier is still being produced and
      //                will land in the workspace cache minutes from now — so
      //                this lead is NOT drafted from its row here. It carries
      //                researchPending, and the receipt's own resolver finishes
      //                the job with a free cache read.
      //   timeout      the budget expired before this lead was ever handed to
      //                the pipeline. Nobody is researching it, so waiting on it
      //                would be waiting on nothing. Draft from the row.
      //
      // The old code called all of it 'timeout', which is how ~47 of 50
      // purchased dossiers ended up landing minutes after their drafts had
      // already been written from the import rows and frozen onto the receipt.
      // ACCEPTED OUTRANKS UNAVAILABLE, and the order is the whole point. The
      // obvious reading — "the API is unavailable, so nothing is coming" — is
      // false for a lead an EARLIER chunk successfully enqueued: `unavailable`
      // can be set half way through the enqueue loop (a token that expired, one
      // 500), and the twenty leads already accepted and already BILLED are
      // still being produced. Testing `unavailable` first relabelled all of
      // them 'unavailable', drafted them from their rows, and left twenty paid
      // dossiers to land in the cache with nobody waiting — the exact failure
      // this whole node was changed to end, surviving in the error path.
      for (const item of pending.values()) {
        if (accepted.has(item.key)) {
          item.failure = 'pending'
          stats.pending += 1
          // The flag rides on the ROW, because ctx.selected is {...row, email}
          // and the receipt is built from that — see chat.js#leadStub, which
          // refuses to draft a lead whose research is still on its way.
          item.c.row = { ...item.c.row, researchPending: true }
        } else if (unavailable) {
          item.failure = 'unavailable'
          stats.failed += 1
        } else {
          item.failure = 'timeout'
          stats.timedOut += 1
        }
      }
      if (unavailable && !looked.misses.length && !stats.cached) {
        // The cache probe itself was the refusal: every lead is unresearched.
        for (const item of items) item.failure = 'unavailable'
        stats.failed = items.length
      }

      // The per-lead record, off the merged rows themselves — real dates and
      // real domains only, and an honest admission everywhere else. Every
      // verdict is 'kept': research decorates, it never decides.
      const reasonShort = String(unavailable || 'the enrichment API could not be reached').slice(0, 80)
      items.forEach((item, position) => {
        const row = item.c.row
        // Its place in the batch first (the pin: carried / new / first batch),
        // then the research verdict — one note per lead for this node.
        const place = pinClause(pin, item.key)
        note(
          row,
          'enriched',
          'kept',
          `#${fmt(position + 1)} of ${fmt(candidates.length)}${place ? ` — ${place}` : ''}; ${
            row.researched
              ? researchNoteOf(row)
              : item.failure === 'pending'
                ? 'research still running — the draft waits for it rather than being written from the row'
                : item.failure === 'timeout'
                  ? 'research did not finish in time — drafted from the import row'
                  : item.failure === 'failed'
                    ? 'research failed — drafted from the import row'
                    : `research unavailable — ${reasonShort}; drafted from the import row`
          }`,
        )
      })

      ctx.researchStats = stats
      if (unavailable) ctx.researchUnavailable = unavailable
      const researched = stats.cached + stats.fresh

      // THE RUN'S OUTPUT, fixed here. The address we selected on is the address
      // that travels: a row whose email was recovered from the packed contacts
      // blob would otherwise reach the receipt with an empty email column and
      // every downstream counter would undercount. Read AFTER the merges above,
      // because researchedRowOf() replaced `item.c.row` on the leads it decorated
      // and this is the row each card and each draft is written from.
      ctx.selected = candidates.map(({ row, email }) => ({ ...row, email }))

      // THIS IS THE LAST NODE, so it owes the run a closing sentence: what the
      // batch is, how it was ordered, and what was left behind. Nothing is built
      // out of it here — the batch travels on the run receipt and a human writes
      // the mail — so this summary is the only place the composition is stated.
      const short = candidates.length < op.targetCount
      const leftOver = Math.max(0, ctx.screened.length - candidates.length)
      const batchLine = short
        ? `The pipeline ran out of untouched, reachable, CRM-clear leads at ${fmt(candidates.length)} — that is everything available, not a cap.`
        : ctx.pin?.pinnedBefore && ctx.pin.carriedTaken
          ? // THE PINNED BATCH: this batch is the last run's batch, in its order,
            // minus the leads that are done or reached out to, with the freed
            // slots filled at the end — so the sentence says that, not
            // "highest-scoring".
            `${fmt(ctx.pin.carriedTaken)} of these ${fmt(candidates.length)} carried over from the last run in the same order — a lead keeps its place until it is done or reached out to${
              ctx.pin.retired.length ? `; ${fmt(ctx.pin.retired.length)} left the batch since` : ''
            }${ctx.pin.fresh.length ? `; ${fmt(ctx.pin.fresh.length)} new at the end` : ''}. ${fmt(leftOver)} eligible left over for the next run.`
          : ctx.researchPromoted
            ? // The batch is NOT the top N by score whenever the researched-first
              // reorder promoted anyone, so the sentence says what was really
              // done. Every run where the reorder changed nothing keeps the
              // original wording byte-for-byte.
              `${fmt(candidates.length)} of ${fmt(ctx.screened.length)} eligible leads — the ones the workspace has already researched first (${fmt(ctx.researchPromoted)} of them ranked below the score cut), then the highest-scoring of the rest; ${fmt(leftOver)} left over for the next run. Pinned from here: the next run shows these same leads in this order until each is done or reached out to.`
            : `The ${fmt(candidates.length)} highest-scoring of ${fmt(ctx.screened.length)} eligible leads — ${fmt(leftOver)} left over for the next run. Pinned from here: the next run shows these same leads in this order until each is done or reached out to.`

      // "EVERY draft written from the import rows" is only writable when there
      // is nothing still coming. A run that enqueued twenty leads and then hit
      // a refusal has twenty dossiers in production, and those twenty drafts
      // are waiting, not written — so the summary may not claim otherwise.
      if (unavailable && !researched && !stats.pending) {
        finish(
          i,
          'skipped',
          `${fmt(candidates.length)} ${plural(candidates.length, 'lead', 'leads')} selected — research unavailable, every draft written from the import rows`,
          [
            batchLine,
            `The enrichment API could not be used: ${String(unavailable).slice(0, 120)}.`,
            'No researched claim will be made for any of these leads — each draft derives from its own import row, and its provenance says so.',
            'Nothing was dropped: research is personalization, never eligibility.',
          ],
          { value: candidates.length, label: 'selected' },
        )
        return
      }

      finish(
        i,
        'done',
        short
          ? `Only ${fmt(candidates.length)} of the ${fmt(op.targetCount)} asked for — that is everything available; ${fmt(researched)} researched`
          : `${fmt(candidates.length)} ${plural(candidates.length, 'lead', 'leads')} selected — ${fmt(researched)} researched, ${fmt(stats.cached)} cached and ${fmt(stats.fresh)} fresh`,
        [
          batchLine,
          stats.cached
            ? `${fmt(stats.cached)} served from the workspace research cache — already paid for, nothing spent on them.`
            : '',
          stats.fresh
            ? `${fmt(stats.fresh)} researched fresh — OpenAI web search through the enrichment API, in batches of ${fmt(MAX_ENRICH_ROWS)}. This spent research credits.`
            : '',
          stats.pending
            ? `${fmt(stats.pending)} are still being researched — the pipeline took them and the ${fmt(Math.round(researchBudgetMs / 1000))}s budget ran out first. Their drafts are not written yet: each one waits for its own dossier and is written from it when it lands.`
            : '',
          stats.timedOut
            ? `${fmt(stats.timedOut)} were never handed to the pipeline before the ${fmt(Math.round(researchBudgetMs / 1000))}s budget ran out — nobody is researching them, so they draft from their import rows and their traces say so.`
            : '',
          stats.failed
            ? `${fmt(stats.failed)} could not be researched${unavailable ? ` (${String(unavailable).slice(0, 60)})` : ''} — kept in the batch, drafting from their import rows.`
            : '',
        ],
        { value: candidates.length, label: 'selected' },
      )
    },
  }

  return runGraph(run, handlers, () => ({
    ok: true,
    passes,
    selected: ctx.selected,
    // The pin ledger for this run — how many of the selected leads were carried
    // over from the last run versus new — so a downstream count can tell a
    // lead re-selected five days running from five different leads.
    batch: ctx.pin
      ? {
          carried: Number(ctx.pin.carriedTaken) || 0,
          fresh: Array.isArray(ctx.pin.fresh) ? ctx.pin.fresh.length : 0,
          retired: Array.isArray(ctx.pin.retired) ? ctx.pin.retired.length : 0,
          pinnedBefore: Number(ctx.pin.pinnedBefore) || 0,
        }
      : null,
    // What the research step actually did (and did not) manage — the receipt
    // and the provenance story read these rather than re-deriving.
    ...(ctx.researchStats ? { researchStats: ctx.researchStats } : {}),
    ...(ctx.researchUnavailable ? { researchUnavailable: ctx.researchUnavailable } : {}),
    // The category the research ran under, so the receipt can read the same
    // workspace cache this node read. Only useful when something is still
    // pending, but carried whenever research ran at all.
    ...(ctx.researchCategory ? { researchCategory: ctx.researchCategory } : {}),
    // How each lead got here, as the nodes recorded it. See makeTracer().
    ...tracer.snapshot(ctx.selected),
  }))
}

// ── THE PINNED BATCH — initial outreach’s stable order ─────────────────────
//
// THE RULE (user, 2026-08-22): initial outreach, the follow-up and the daily
// worklist show THE SAME leads in THE SAME order, run after run, until a lead
// is done or has been reached out to. Ranking alone could not keep that
// promise — every run re-ranked from scratch, and the inputs move (research
// promotes leads, the due set is recomputed each morning, pools change) — so
// each operation remembers the batch it showed and carries it forward first.
// lib/batchPin.js holds the contract; this is the step that applies it.
//
// ORDER ONLY, NEVER ELIGIBILITY. `eligible` is exactly the set the gates
// produced; the pin arranges it and nothing else. A pinned lead the gates
// dropped is retired here with a trace note pointing at the step that dropped
// it, and it can only come back — at the END — by becoming eligible again.
//
// `inScopeKeys` is every lead this run actually considered (the source pool,
// the cohort). A pinned key outside it is HELD, not retired: a scoped run, or
// a history source that answered partially, must not erase a rep's list.
function applyBatchPin({ op, category, eligible, keyOf: key, inScopeKeys, now, note, nodeId }) {
  const cat = String(category || '')
  const pinnedBefore = readPin(op.id, cat).keys
  const inScope = new Set((inScopeKeys || []).map((k) => String(k || '')).filter(Boolean))
  const pin = pinOrder({
    pinned: pinnedBefore,
    eligible,
    keyOf: key,
    inScope: (k) => inScope.has(k),
    cap: op.targetCount,
  })
  // Persist at the cut, not at the end of the run: a run that stops later
  // (the research budget, a cancel) still showed — or was about to show —
  // exactly this batch, and the next run must repeat it.
  savePin(op.id, cat, pin.nextPin, { now })
  if (typeof note === 'function' && nodeId) {
    for (const k of pin.retired) {
      note(
        k,
        nodeId,
        'dropped',
        'was on the last run’s list and left it this run — no longer eligible (reached out to, replied, cadence complete, said no, or in a deal); the step that dropped it says which',
      )
    }
  }
  return {
    ...pin,
    pinnedBefore: pinnedBefore.length,
    carriedKeys: new Set(pin.carried.map(key)),
    freshKeys: new Set(pin.fresh.map(key)),
    line: pinSummaryLine({ ...pin, pinnedBefore: pinnedBefore.length }),
  }
}

// The clause a kept lead's rank note carries about its place in the batch.
function pinClause(pin, key) {
  if (!pin) return ''
  if (!pin.pinnedBefore) return 'pinned from this run on — same place next run until done or reached out to'
  if (pin.carriedKeys.has(key)) return 'carried from the last run in the same place — it stays there until done or reached out to'
  if (pin.freshKeys.has(key)) return 'new to the batch — fills a freed slot behind the carried leads'
  return ''
}

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION 2 — Daily Tasks: the rep's own HubSpot tasks
//
// Recut 2026-09-23 at the owner's request: Daily Tasks IS what used to be
// section 1 of the morning briefing — the rep's HubSpot tasks, overdue or due
// today on the Chicago calendar, in the rep's own wording — and nothing else.
// The cadence engine that used to live under this id (wait gate, reply freeze,
// touch ladder, rank-and-pin) has no operation any more and left this file
// with it; lib/cadence.js and lib/batchPin.js stay because initial outreach
// and the stored receipts' provenance views still read them.
//
// Three linear nodes, each reading what the one before produced: WHO the rep
// is (roster → HubSpot owner, never the signed-in session), WHICH tasks are
// due (the mirror's metrics rows, windowed and with our own domains left off
// and counted), then the assembled list. It creates nothing anywhere: the
// list is the run's whole output, and unread is always said as unread.
// ═══════════════════════════════════════════════════════════════════════════

// The one-line ledger summary the daily-task tile shows after the run.
function tasksSummaryLine({ tasks, unread, unreadFor }) {
  if (unread) return 'HubSpot tasks could not be read'
  const n = tasks.length
  if (!n) {
    return unreadFor.length
      ? `No task readable — ${unreadFor.join(' and ')} unread`
      : 'No task overdue or due today'
  }
  const overdue = tasks.filter((t) => t.overdue).length
  const today = n - overdue
  const drafted = tasks.filter((t) => t.recipient).length
  const bits = []
  if (overdue) bits.push(`${fmt(overdue)} overdue`)
  if (today) bits.push(`${fmt(today)} due today`)
  if (drafted) bits.push(`${fmt(drafted)} ${plural(drafted, 'email', 'emails')}`)
  return `${fmt(n)} ${plural(n, 'task', 'tasks')} — ${bits.join(' · ')}`
}

// ── THE EMAIL BEHIND EACH TASK (owner's ask, 2026-10-09) ────────────────────
//
// "Daily tasks page should be full of email cards instead of just tasks,
// drafted based on the current situation and past convo." A HubSpot task is
// the rep's own shorthand for what to do next with a company ("fu demo",
// "fu move to paid"); the card is the email that does it. Everything the
// draft is written from is read HERE, from the mirror, and travels on the
// item — the model (POST /api/enrich/draft, kind task_followup) sees only
// held facts, and the card shows the same facts beside the email so the rep
// can check it against what the thread actually says.
//
// WHO THE EMAIL GOES TO, in order: the company contact we have most recently
// been in a real mail thread with (the conversation the task continues);
// else the first company contact with an address. One of our own addresses
// is never the recipient, a robot address (no-reply@ and kin) never is, and a
// company with nobody writable keeps its task on the receipt as a plain row
// that SAYS so — nothing is invented to fill the card.

// How many of the thread's real messages ride on the item (newest first).
// Same cap the lead stubs use (chat.js#STUB_HISTORY_MAX), for the same
// reason: the receipt is persisted with the conversation.
const TASK_HISTORY_MAX = 6

// The rep's own notes on the company — the "current situation" in their
// words — newest first, bounded because note bodies can be pages long.
const TASK_NOTES_MAX = 2
const TASK_NOTE_CHARS = 320

// Companies are opened in batches: one GET /api/hubspot/company each, and a
// 44-task morning is ~35 companies.
const TASK_PROBE_BATCH = 8

function isoOf(value) {
  if (!value) return ''
  const d = value instanceof Date ? value : new Date(value)
  return Number.isNaN(d.getTime()) ? '' : d.toISOString()
}

// A HubSpot note body is stored as HTML (<div><p>…</p></div>, &nbsp;, <br>).
// The card quotes the rep's words and the model reads them, and neither
// should see markup: tags become spaces, the common entities become their
// characters, whitespace collapses.
const NOTE_ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ' }
export function noteText(html) {
  return String(html || '')
    .replace(/<\s*(br|\/p|\/div|\/li|\/tr)\s*\/?\s*>/gi, ' ')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (whole, code) => {
      const key = code.toLowerCase()
      if (key in NOTE_ENTITIES) return NOTE_ENTITIES[key]
      if (key.startsWith('#x')) return String.fromCodePoint(parseInt(key.slice(2), 16)) || whole
      if (key.startsWith('#')) return String.fromCodePoint(parseInt(key.slice(1), 10)) || whole
      return whole
    })
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * The pure judge behind the email cards: tasks + what the mirror holds about
 * their companies -> the same tasks, each with the person the email goes to
 * and the facts the draft is written from. Exported for scripts/test-taskEmails.mjs.
 *
 * @param {object}   input
 * @param {object[]} input.tasks           judgeBoTasks items (id, subject, companyId, …)
 * @param {Map}      input.companies       companyId -> hubspotCompany() payload (or null)
 * @param {Map}      input.byAddress       readCadenceHistory().byAddress (address -> thread record)
 * @param {object}   input.usersByCompany  hubspotUsers().by_company (companyId -> usage aggregate)
 * @param {object}   input.org             the run's own org identity (readOwnOrg) or null
 * @param {Date}     input.now
 * @returns {{items: object[], withRecipient: number, withoutRecipient: number,
 *   withThread: number, unprobed: number}}
 */
export function judgeTaskEmails({ tasks = [], companies = null, byAddress = null, usersByCompany = null, org = null, now = new Date() } = {}) {
  const probes = companies instanceof Map ? companies : new Map()
  const threads = byAddress instanceof Map ? byAddress : new Map()
  const usage = usersByCompany && typeof usersByCompany === 'object' ? usersByCompany : {}
  const ourDomains = org?.domains instanceof Set ? [...org.domains].slice(0, 12) : []
  let withRecipient = 0
  let withoutRecipient = 0
  let withThread = 0
  let unprobed = 0

  const writable = (email) => {
    const a = String(email || '').trim().toLowerCase()
    if (!a || !a.includes('@')) return ''
    if (isRobotAddress(a)) return ''
    if (org && isOwnAddress(a, org)) return ''
    return a
  }
  const when = (record) => {
    const out = record?.lastTouchAt instanceof Date ? record.lastTouchAt.getTime() : 0
    const inb = record?.lastInboundAt instanceof Date ? record.lastInboundAt.getTime() : 0
    return Math.max(out, inb)
  }

  const items = tasks.map((task) => {
    const companyId = String(task?.companyId || '')
    const probe = companyId ? probes.get(companyId) : null
    if (companyId && !probes.has(companyId)) unprobed += 1
    const rawContacts = Array.isArray(probe?.contacts) ? probe.contacts : []
    const addressed = rawContacts.filter((c) => String(c?.email || '').includes('@')).length
    const contacts = rawContacts
      .map((c) => ({
        id: String(c?.id || ''),
        email: writable(c?.email),
        name: String(c?.name || '').trim(),
        firstName: String(c?.first_name || '').trim(),
        role: String(c?.job_title || '').trim(),
      }))
      .filter((c) => c.email)
    const contactByEmail = new Map(contacts.map((c) => [c.email, c]))

    // Every real thread with this company: by the mirror's own company link
    // on the thread, or by the address being one of the company's contacts.
    const companyThreads = []
    for (const [addr, record] of threads) {
      const email = writable(addr)
      if (!email) continue
      const linked = Array.isArray(record?.companyIds) && companyId && record.companyIds.includes(companyId)
      if (linked || contactByEmail.has(email)) companyThreads.push({ email, record })
    }
    companyThreads.sort((a, b) => when(b.record) - when(a.record) || (a.email < b.email ? -1 : 1))

    const picked = companyThreads[0] || null
    const recipientEmail = picked ? picked.email : contacts[0]?.email || ''
    const contact = contactByEmail.get(recipientEmail) || null
    const record = picked ? picked.record : threads.get(recipientEmail) || null

    const history = []
    for (const t of companyThreads.length ? companyThreads : record ? [{ email: recipientEmail, record }] : []) {
      for (const m of Array.isArray(t.record?.messages) ? t.record.messages : []) {
        if (m && m.at) history.push(m)
      }
    }
    history.sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))

    const notes = (Array.isArray(probe?.engagements) ? probe.engagements : [])
      .filter((e) => e && e.kind === 'note' && String(e.body || '').trim())
      .map((e) => ({ at: isoOf(e.occurred_at || e.created_at), text: noteText(e.body).slice(0, TASK_NOTE_CHARS) }))
      .filter((n) => n.text)
      .sort((a, b) => (a.at < b.at ? 1 : a.at > b.at ? -1 : 0))
      .slice(0, TASK_NOTES_MAX)
    const openDeal = (Array.isArray(probe?.deals) ? probe.deals : []).find((d) => d && !d.is_closed) || null
    const account = companyId && usage[companyId] && typeof usage[companyId] === 'object' ? usage[companyId] : {}
    const portalMatch = String(task?.hubspotUrl || '').match(/\/contacts\/(\d+)\//)
    const contactUrl = contact?.id && portalMatch ? `https://app.hubspot.com/contacts/${portalMatch[1]}/record/0-1/${contact.id}` : ''

    if (recipientEmail) withRecipient += 1
    else withoutRecipient += 1
    if (history.length) withThread += 1

    const dueMs = new Date(task?.dueAt || '').getTime()
    const overdueDays = task?.overdue && Number.isFinite(dueMs) ? Math.max(0, Math.round((now.getTime() - dueMs) / 86400000)) : 0

    return {
      ...task,
      key: `task:${task?.id || ''}`,
      kind: 'task_followup',
      ...(recipientEmail
        ? {
            recipient: recipientEmail,
            email: recipientEmail,
            name: contact?.name || '',
            ...(contact?.role ? { contactRole: contact.role } : {}),
            ...(contactUrl ? { contactUrl } : {}),
            // The resolver's cue, same as a lead stub's: the model writes it
            // on the receipt, the template is the labelled fallback.
            draftPending: true,
          }
        : {
            noRecipientReason: !companyId
              ? 'the task names no company'
              : !probe
                ? 'the company could not be read from the mirror'
                : addressed || companyThreads.length
                  ? 'every address on this company is ours or automated'
                  : 'no contact with an email address on this company in HubSpot',
          }),
      ...(overdueDays ? { overdueDays } : {}),
      ...(history.length ? { history: history.slice(0, TASK_HISTORY_MAX) } : {}),
      ...(record?.lastTouchAt ? { lastOutboundAt: isoOf(record.lastTouchAt) } : {}),
      ...(record?.lastInboundAt ? { lastInboundAt: isoOf(record.lastInboundAt) } : {}),
      ...(record?.lastSubject ? { lastSubject: String(record.lastSubject).slice(0, 120) } : {}),
      ...(record?.lastSnippet ? { lastSnippet: String(record.lastSnippet).slice(0, 160) } : {}),
      ...(notes.length ? { notes } : {}),
      // The open deal by NAME: the stage the mirror holds is HubSpot's opaque
      // id (1311840060), which tells a reader and a writer nothing.
      ...(openDeal && String(openDeal.name || '').trim() ? { dealName: String(openDeal.name).trim().slice(0, 80) } : {}),
      ...(account.last_software_access ? { lastSoftwareAccess: String(account.last_software_access) } : {}),
      ...(account.trial_ends ? { trialEnds: String(account.trial_ends) } : {}),
      ...(account.subscription_end ? { subscriptionEnd: String(account.subscription_end) } : {}),
      ...(typeof account.payment_method_attached === 'boolean' ? { paymentMethodAttached: account.payment_method_attached } : {}),
      ...(ourDomains.length ? { ourDomains } : {}),
    }
  })

  return { items, withRecipient, withoutRecipient, withThread, unprobed }
}

async function runDailyTasks(op, run, { now }) {
  const { passes, indexOf, start, finish, signal, tracer, ownOrg } = run
  const at = (id) => indexOf.get(id)
  const ctx = {
    org: null,
    reps: [],
    tasks: [],
    tasksUnread: false,
    tasksReason: '',
    tasksFor: [],
    tasksUnreadFor: [],
    tasksTruncated: false,
    tasksUndated: 0,
    tasksExcluded: { count: 0, companies: [] },
    // Left off as written off (Closed Lost / a "not a fit" note after the
    // task was set) and as superseded (an overdue task on a company the rep
    // already booked a later task for) — both counted and named, never silent.
    tasksDismissed: { count: 0, companies: [] },
    tasksSuperseded: { count: 0, companies: [] },
    exclusions: [],
  }

  const handlers = {
    // ── 1. who the rep is ─────────────────────────────────────────────────────
    // The roster's rep, resolved to a HubSpot owner by e-mail, then first
    // name (resolveTaskReps). NEVER the signed-in session: the admin who runs
    // the briefing owns no HubSpot records, and asking for their tasks once
    // printed "no tasks" over 38 overdue ones in front of investors.
    identity: async () => {
      const i = at('identity')
      await start(i)
      const org = await ownOrg()
      if (signal?.aborted) throw abortError()
      ctx.org = org
      const who = resolveTaskReps({ roster: org.roster, owners: org.owners, self: currentScope() })
      ctx.reps = who.reps
      const named = who.reps.map((r) => `${r.name || r.email}${r.ownerId ? ` → HubSpot owner ${r.ownerId}` : ''}`)
      const unresolvedLine = who.unresolved?.length
        ? `${fmt(who.unresolved.length)} roster ${plural(who.unresolved.length, 'rep matches', 'reps match')} no HubSpot owner (${who.unresolved.map((u) => u.name || u.email).join(', ')}) — named, not silently dropped.`
        : ''
      if (!who.reps.length) {
        finish(
          i,
          'skipped',
          'No rep could be matched to a HubSpot owner — the tasks cannot be read',
          [
            !who.ownersReadable
              ? `The HubSpot owner list could not be read${who.ownersReason ? ` (${who.ownersReason})` : ''}.`
              : who.how === 'roster-unreadable'
                ? 'The team roster could not be read, so this pass cannot say who the rep is.'
                : who.how === 'none'
                  ? 'Nobody is signed in and the roster names no rep.'
                  : 'The roster names no rep, and the signed-in address matches no HubSpot owner by e-mail or first name.',
            unresolvedLine,
            'Unknown is not “nobody has tasks”: the next node reports the list as unread.',
          ],
        )
        return
      }
      finish(
        i,
        'done',
        named.join(' · '),
        [
          `Matched by ${[...new Set(who.reps.map((r) => r.how))].join(' and ')} — the roster’s rep role decides WHO, the CRM owner list decides WHICH records are theirs.`,
          unresolvedLine,
          'Never the signed-in session: the person running the briefing is not the person whose tasks it lists.',
        ],
        { value: who.reps.length, label: plural(who.reps.length, 'rep', 'reps') },
      )
    },

    // ── 2. the tasks ──────────────────────────────────────────────────────────
    // One metrics read per rep with the owner id passed explicitly (readBoTasks),
    // windowed to overdue-or-due-today by judgeBoTasks, our own domains left
    // off through the Settings list and COUNTED. Fail-soft per rep: one rep's
    // failed read never erases another's tasks, and the failed rep is named.
    tasks: async () => {
      const i = at('tasks')
      await start(i)
      // Read at RUN time, never at module load: an edit in Settings between
      // two runs in the same tab must reach the second run.
      const exclusions = readExclusions().entries
      ctx.exclusions = exclusions
      let boTasks = { readable: false, items: [], reason: 'not attempted', reps: [], unread: [] }
      try {
        boTasks = await readBoTasks({
          signal,
          now,
          roster: ctx.org?.roster || null,
          owners: ctx.org?.owners || null,
          self: currentScope(),
          exclude: exclusionSet(exclusions),
        })
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        boTasks = { readable: false, items: [], reason: String(err?.message || 'the task read failed'), reps: [], unread: [] }
      }
      if (signal?.aborted) throw abortError()
      ctx.tasks = boTasks.items
      ctx.tasksUnread = !boTasks.readable
      ctx.tasksReason = boTasks.readable ? '' : String(boTasks.reason || '')
      const unreadReps = boTasks.unread || []
      ctx.tasksFor = (boTasks.reps || [])
        .map((r) => r.name)
        .filter((n) => n && !unreadReps.some((u) => u.name === n))
      ctx.tasksUnreadFor = unreadReps.map((u) => u.name).filter(Boolean)
      ctx.tasksTruncated = Boolean(boTasks.truncated)
      ctx.tasksUndated = Number(boTasks.undated) || 0
      ctx.tasksExcluded = boTasks.excluded || { count: 0, companies: [] }
      ctx.tasksDismissed = boTasks.dismissed || { count: 0, companies: [] }
      ctx.tasksSuperseded = boTasks.superseded || { count: 0, companies: [] }

      if (ctx.tasksUnread) {
        finish(
          i,
          'skipped',
          'HubSpot tasks could not be read — unread, not empty',
          [
            `GET /api/hubspot/metrics?rows=tasks did not answer for any rep: ${ctx.tasksReason || 'no reason given'}.`,
            'Nothing is claimed about what is overdue: an unread list is an admission, never a zero.',
          ],
        )
        return
      }
      const n = ctx.tasks.length
      const overdueN = ctx.tasks.filter((t) => t.overdue).length
      const whose = ctx.tasksFor.length ? `${ctx.tasksFor.join(' and ')}’s` : 'the rep’s'
      finish(
        i,
        'done',
        n
          ? `${fmt(n)} of ${whose} tasks ${plural(n, 'is', 'are')} overdue or due today${overdueN ? ` (${fmt(overdueN)} overdue)` : ''}`
          : `${ctx.tasksFor.length ? ctx.tasksFor.join(' and ') : 'The rep'} has no HubSpot task overdue or due today`,
        [
          'Read from the mirror’s task rows (hs_task_subject, hs_task_status, due date) in the rep’s own wording — nothing paraphrased, nothing invented. Only open tasks count; COMPLETED and DEFERRED are closed.',
          ctx.tasksUnreadFor.length
            ? `${ctx.tasksUnreadFor.join(' and ')}’s tasks could not be read at all and are missing from this count — unread, not none.`
            : '',
          ctx.tasksUndated
            ? `${fmt(ctx.tasksUndated)} more ${plural(ctx.tasksUndated, 'task carries', 'tasks carry')} a due date the mirror could not read — counted, not listed.`
            : '',
          ctx.tasksExcluded?.count
            ? `${fmt(ctx.tasksExcluded.count)} ${plural(ctx.tasksExcluded.count, 'task', 'tasks')} at our own domains (${exclusions.join(', ')}) left off — our own staff and test accounts${ctx.tasksExcluded.companies?.length ? ` (${ctx.tasksExcluded.companies.join(', ')})` : ''}; change the list in Settings.`
            : ctx.tasksExcluded?.unapplied
              ? `The excluded-domains list (${exclusions.join(', ')}) could NOT be applied: the mirror answered without a company domain on its task rows, so tasks at our own domains may be on this list.`
              : ctx.tasksTruncated
                ? 'The mirror cut the task list at its payload limit — this is the most recent slice, not all of it.'
                : '',
          [
            ctx.tasksSuperseded.count
              ? `${fmt(ctx.tasksSuperseded.count)} older overdue ${plural(ctx.tasksSuperseded.count, 'task', 'tasks')} left off — the rep already booked a later task on ${plural(ctx.tasksSuperseded.count, 'that company', 'those companies')} (${ctx.tasksSuperseded.companies.map((c) => `${c.name}, next ${chicagoDayLabel(c.next)}`).join('; ')}).`
              : '',
            ctx.tasksDismissed.count
              ? `${fmt(ctx.tasksDismissed.count)} ${plural(ctx.tasksDismissed.count, 'task', 'tasks')} left off — the company was written off after the task was set (${ctx.tasksDismissed.companies.map((c) => `${c.name}: ${c.reason === 'closed_lost' ? 'Closed Lost' : 'a “not a fit” note'}`).join('; ')}).`
              : ctx.tasksDismissed.unapplied
                ? 'The Closed Lost / not-a-fit check could NOT run: the mirror answered without it on the task rows (a lambda one deploy behind), so a written-off company may still be listed.'
                : '',
          ]
            .filter(Boolean)
            .join(' '),
        ],
        { value: n, label: plural(n, 'task due', 'tasks due') },
      )
    },

    // ── 3. each task's conversation ───────────────────────────────────────────
    // The situation each email is drafted from, read per company from the
    // mirror: contacts, open deals and the rep's notes (GET /api/hubspot/
    // company), the real mail threads (HubSpot email engagements) and the
    // account's product usage (GET /api/hubspot/users). Every read is
    // fail-soft and ADMITTED: a company that could not be opened keeps its
    // task as a plain row that says why, never a card with an invented
    // recipient.
    context: async () => {
      const i = at('context')
      await start(i)
      if (ctx.tasksUnread || !ctx.tasks.length) {
        finish(i, 'skipped', ctx.tasksUnread ? 'No tasks were read, so there is nothing to draft' : 'No task is due, so there is nothing to draft', [])
        return
      }
      const ids = [...new Set(ctx.tasks.map((t) => String(t.companyId || '')).filter(Boolean))]
      const companies = new Map()
      let probeFailed = 0
      for (let k = 0; k < ids.length; k += TASK_PROBE_BATCH) {
        const probed = await probeCompanies(ids.slice(k, k + TASK_PROBE_BATCH), signal)
        for (const { id, data } of probed) {
          if (data) companies.set(id, data)
          else probeFailed += 1
        }
      }
      let read = null
      try {
        read = await readCadenceHistory({ signal, now, ours: ctx.org })
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        read = null
      }
      if (signal?.aborted) throw abortError()
      let users = null
      try {
        users = await hubspotUsers({ signal })
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        users = null
      }
      if (signal?.aborted) throw abortError()
      const usersUnread = customerUsageUnread(users)
      const judged = judgeTaskEmails({
        tasks: ctx.tasks,
        companies,
        byAddress: read?.byAddress || null,
        usersByCompany: usersUnread ? null : users?.by_company || null,
        // The EXPLICIT identity only (session, roster, CRM owners, the
        // floor) — never the mirror's "who writes more than they receive"
        // derivation that read.org folds in. That tally flagged
        // grandemasonry.com, a paying customer, as one of ours (2026-10-09)
        // and the task's email went to nobody. Here "ours" has to mean a
        // teammate, and a chatty customer is not one.
        org: ctx.org || null,
        now,
      })
      ctx.tasks = judged.items
      const mailOk = Boolean(read?.mail?.available)
      finish(
        i,
        'done',
        `${fmt(judged.withRecipient)} of ${fmt(ctx.tasks.length)} ${plural(ctx.tasks.length, 'task has', 'tasks have')} somebody to write to · ${fmt(judged.withThread)} with a real thread behind ${plural(judged.withThread, 'it', 'them')}`,
        [
          `${fmt(companies.size)} of ${fmt(ids.length)} ${plural(ids.length, 'company', 'companies')} opened in the mirror for contacts, open deals and the rep’s notes${probeFailed ? ` — ${fmt(probeFailed)} could not be read, and ${plural(probeFailed, 'its task stays', 'their tasks stay')} a plain row` : ''}.`,
          mailOk
            ? `Mail threads: ${fmt(read.byAddress.size)} emailed ${plural(read.byAddress.size, 'address', 'addresses')} carry real HubSpot engagements; the recipient is the contact most recently in a thread with us, else the first contact with an address. Our own and automated addresses are never written to.`
            : 'The HubSpot mail history could not be read: recipients fall back to the first contact on each company, and the drafts are written with no thread behind them — said on each card, never dressed as a cold start.',
          usersUnread
            ? `Product usage could not be read (${usersUnread}) — the drafts carry no trial, subscription or last-access facts.`
            : 'Product usage (trial end, subscription end, last software access) rides on each task’s company, so the email can speak to where the account actually stands.',
          judged.withoutRecipient
            ? `${fmt(judged.withoutRecipient)} ${plural(judged.withoutRecipient, 'task has', 'tasks have')} nobody writable — no contact with an address, or only our own — and ${plural(judged.withoutRecipient, 'is', 'are')} listed as a plain row with the reason.`
            : '',
        ],
        { value: judged.withRecipient, label: 'emails to draft' },
      )
    },

    // ── 4. the list ───────────────────────────────────────────────────────────
    // Assembles and counts. Nothing stored, nothing created: the receipt's
    // email cards are the deliverable, and Bo sends each one from HubSpot.
    briefing: async () => {
      const i = at('briefing')
      await start(i)
      const n = ctx.tasks.length
      const overdueN = ctx.tasks.filter((t) => t.overdue).length
      const todayN = n - overdueN
      finish(
        i,
        'done',
        ctx.tasksUnread
          ? 'Nothing to list — the HubSpot tasks could not be read'
          : n
            ? `${fmt(n)} ${plural(n, 'task', 'tasks')} · ${fmt(overdueN)} overdue · ${fmt(todayN)} due today`
            : ctx.tasksUnreadFor.length
              ? `Nothing due — but ${ctx.tasksUnreadFor.join(' and ')}’s tasks could not be read`
              : 'Nothing due today — no task overdue or due today',
        [
          'Due today first, then the longest overdue — one email card per task, each linked to its HubSpot task and contact.',
          `${fmt(ctx.tasks.filter((t) => t.recipient).length)} ${plural(ctx.tasks.filter((t) => t.recipient).length, 'email is', 'emails are')} being written on the receipt from the task, the real thread and the rep’s notes (POST /api/enrich/draft, kind task_followup); the template stands in only if the model cannot answer, and the card says so.`,
          'Drafts, not sends: nothing created, nobody enrolled, nothing sent. Customer follow-ups (inactive users, trials, payments) live in Follow-Up.',
        ],
        { value: n, label: 'tasks' },
      )
    },
  }

  return runGraph(run, handlers, () => ({
    ok: true,
    passes,
    selected: [],
    // Which receipt shape this is — the packer (chat.js) and the receipt
    // (Chat.jsx#RunMessage) branch on it; a stored receipt without it keeps
    // its own older layout.
    briefing: 'tasks',
    tasks: ctx.tasks,
    ...(ctx.tasksUnread ? { tasksUnread: true } : {}),
    ...(ctx.tasksFor.length ? { tasksFor: ctx.tasksFor } : {}),
    ...(ctx.tasksUnreadFor.length ? { tasksUnreadFor: ctx.tasksUnreadFor } : {}),
    ...(ctx.tasksExcluded?.count || ctx.tasksExcluded?.unapplied
      ? { tasksExcluded: { ...ctx.tasksExcluded, entries: ctx.exclusions } }
      : {}),
    // Carried only when they say something, so a quiet day's receipt is the
    // same shape it always was (chat.js#packRunMessage copies both).
    ...(ctx.tasksDismissed?.count || ctx.tasksDismissed?.unapplied ? { tasksDismissed: ctx.tasksDismissed } : {}),
    ...(ctx.tasksSuperseded?.count ? { tasksSuperseded: ctx.tasksSuperseded } : {}),
    // The ledger tile's one line (lib/dailyTasks.js#recordRunResult).
    summary: tasksSummaryLine({ tasks: ctx.tasks, unread: ctx.tasksUnread, unreadFor: ctx.tasksUnreadFor }),
    // "Nothing to do" only over a list that was READ, for every rep.
    ...(!ctx.tasksUnread && !ctx.tasks.length && !ctx.tasksUnreadFor.length ? { nothingToDo: true } : {}),
    ...tracer.snapshot([]),
  }))
}

// ═══════════════════════════════════════════════════════════════════════════
// OPERATION 3 — Follow-Up: the customer lanes
//
// Recut 2026-09-23 at the owner's request: Follow-Up is sections 2–4 of the
// old morning briefing, re-cut into four customer lanes off the portal's
// product-usage attributes in HubSpot (GET /api/hubspot/users), each card
// wearing a status pill:
//   INACTIVE USERS       — no software access in over 7 days; the one lane
//                          with a check-in draft (written on the receipt by
//                          POST /api/enrich/draft, template as the fallback).
//   TRIALS ENDING SOON   — "Trial Ends" inside the next 7 days, soonest first.
//   EXPIRED TRIALS       — a trial or subscription end already past, with
//                          real usage on record. (The live portal writes the
//                          same instant to trial_ends and subscription_end,
//                          verified 2026-09-23: 40 contacts past on both.)
//   PAYMENT FAILED       — a payment-failure timestamp on the contact. The
//                          portal does NOT write one to HubSpot yet (no such
//                          property existed on 2026-09-23); until it does the
//                          lane says so, and never says "nobody failed".
// The HubSpot-tasks section that used to lead this briefing is Daily Tasks
// now. The old cadence lanes, the inbox read and the reply freeze are gone
// with the cadence engine — nothing here reads Gmail.
//
// Customers are not prospects and inherit none of the prospect machinery:
// no touch cap, no open-deal or said-no block, never the pinned batch. The
// lanes are stable BY CONSTRUCTION — each sorts on a fixed portal timestamp
// with the address as the tiebreak — so an entry only moves by the fact
// itself changing. Contact history, when this run's mail read holds any for
// the address, rides on the item: SHOWN to Bo, never used to filter.
//
// THE DATA HAZARD: many carriers of these attributes are our own dogfooders
// and the CloudWatch canaries. The Settings exclusion list is consulted
// FIRST, then the run's org-identity derivation (readOwnOrg), with the two
// corporate domains as a hard floor; fixtures are matched on the canaries'
// local-part shape; robots are refused. Every drop is counted and said.
// ═══════════════════════════════════════════════════════════════════════════

// Strictly MORE than seven full days of silence — ">" and not ">=", pinned by
// scripts/test-userLanes.mjs: 6.9 days is still inside the window, 7.1 is out.
export const CUSTOMER_INACTIVE_MS = 7 * 24 * 60 * 60 * 1000

// "About to expire": a trial whose end is inside this window from now (and
// not past). A trial ending in 12 days is not on the list — it is tomorrow's.
export const CUSTOMER_TRIAL_WINDOW_MS = 7 * 24 * 60 * 60 * 1000

// The payment-failure signal: the mirror's normalized name and the raw
// HubSpot property it comes from (hubspot-sync mirrors it, the read lambda's
// CONTACT_USAGE_PROPS renames it). Named here so the admission on the node can
// tell the portal team EXACTLY which property to start writing.
export const PAYMENT_FAILED_PROP = 'last_payment_failed'
export const PAYMENT_FAILED_RAW_PROP = 'custom_last_payment_failed_timestamp'

// How many items each lane carries onto the result. The remainder is counted
// ("+N more") on the node summary, never silently dropped.
export const CUSTOMER_LANE_CAP = 30

// The load-test fixtures' local-part shape (cloudwatch-test-user-3,
// test-user-12): fixtures, not people, whatever domain they sit at.
const TEST_USER_LOCAL_RE = /^(cloudwatch-)?test-user-/i

// The hard floor beneath the org-identity derivation — see the block comment
// above. A floor, not the mechanism: isOwnAddress() over the derived set is
// the rule, and these two literals only add anything on a run where the
// derivation came back empty-handed.
const OWN_DOMAIN_FLOOR = new Set(['revailo.com', 'revailo.ai'])

// `exclude` is the Settings-managed exclusionSet (lib/taskExclusions.js) or
// null. It is consulted FIRST because an explicit list the owner edits beats
// anything derived: a domain he added is out even when no identity source
// knows it. Taking a domain OFF the list does not un-own it — the derivation
// and the floor still stand, which is what a floor is for.
function isOursCustomer(email, org, exclude = null) {
  if (exclude && isExcluded(email, exclude)) return true
  if (org && isOwnAddress(email, org)) return true
  return OWN_DOMAIN_FLOOR.has(mailDomainOf(email))
}

// One portal timestamp, parsed the way the mirror hands them out: ISO strings
// normally, an epoch-ms digit string when a property was mirrored raw (the
// same shapes the users lambda guards against). `held` says the portal
// carries A value at all; `date` is null when that value cannot be read as a
// time — different facts, and only a readable one may put somebody in a lane.
function customerWhen(value) {
  if (value === null || value === undefined) return { held: false, date: null }
  const s = String(value).trim()
  if (!s) return { held: false, date: null }
  const d = /^\d{11,}$/.test(s) ? new Date(Number(s)) : new Date(s)
  return { held: true, date: Number.isNaN(d.getTime()) ? null : d }
}

// Whole calendar days between two instants on the America/Chicago calendar —
// the calendar Bo works. "9 days inactive" has to mean nine of HIS days, not
// 216 UTC hours. null when either end has no readable Chicago day.
function chicagoDaysBetween(from, to) {
  const a = chicagoDayOf(from)
  const b = chicagoDayOf(to)
  if (!a || !b) return null
  const [ay, am, ad] = a.split('-').map(Number)
  const [by, bm, bd] = b.split('-').map(Number)
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / 86400000)
}

// A lane's total order: its fixed timestamp ascending (or descending), then
// the address — unique within a lane by construction (one item per email), so
// two runs over the same read hand back the same lane in the same order. A
// lane order that depends on Map iteration order is a lane that shuffles.
function compareLane(field, direction = 1) {
  return (a, b) => {
    const fa = String(a[field] || '')
    const fb = String(b[field] || '')
    if (fa !== fb) return (fa < fb ? -1 : 1) * direction
    return a.email < b.email ? -1 : a.email > b.email ? 1 : 0
  }
}

// The EXPIRED lane's total order (user-directed, 2026-08-27): latest
// last_software_access FIRST — the most recently active lapsed customer is
// the easiest to convert, and the lane leads with them. An expired item can
// hold no access timestamp at all (it earned its place through projects or
// blueprints instead — see the everUsed gate); those sort LAST, never
// interleaved by an empty string pretending to be a date. Email ASC is the
// tiebreak, so the order is total and repeatable. ISO strings compare
// correctly as strings.
function compareExpiredLane(a, b) {
  const fa = String(a.lastSoftwareAccess || '')
  const fb = String(b.lastSoftwareAccess || '')
  if (fa !== fb) {
    if (!fa) return 1
    if (!fb) return -1
    return fa < fb ? 1 : -1
  }
  return a.email < b.email ? -1 : a.email > b.email ? 1 : 0
}

/**
 * Was anything actually READ? '' when the users response is usable; the
 * honest reason string when it is not. PURE and exported so
 * scripts/test-userLanes.mjs can pin every unread shape without a network.
 *
 * `available:false` is the obvious case — hubspotUsers() resolves it for every
 * transport/HTTP failure. But the lambda also answers HTTP 200 for two states
 * where NOTHING was read, and hubspotUsers() stamps both `available:true`
 * ("the API answered"): the mirror was never configured (configured:false,
 * users:[]), and the feed walk blew up before reading a single row
 * (complete:false, users:[]). Treating those as readable would build lanes
 * from users:[] and report "0 inactive · 0 expired" as a FINDING over data
 * that was never read. A COMPLETED walk over a portal with no prop-carrying
 * contacts comes back complete:true, users:[] — an honest empty — and a
 * bounded partial walk that DID return rows stays readable.
 */
export function customerUsageUnread(res) {
  if (!res || res.available !== true) {
    return String(res?.reason || 'the customer usage data could not be read')
  }
  if (res.configured === false) {
    return String(
      res.reason || 'the HubSpot mirror is not configured — the customer usage data was never read',
    )
  }
  const users = Array.isArray(res.users) ? res.users : []
  if (res.complete === false && !users.length) {
    return String(
      res.reason || 'the contact feed could not be walked — no usage data was read',
    )
  }
  return ''
}

/**
 * The pure judge behind Follow-Up's four lanes. Exported so
 * scripts/test-userLanes.mjs can pin every boundary without a network.
 *
 * @param {object}   input
 * @param {object[]} input.users        GET /api/hubspot/users rows
 * @param {Date}     input.now
 * @param {object}   input.org          the run's org identity (readOwnOrg) or null
 * @param {Map}      input.mailByAddress this run's mail history by address, or null
 * @param {object|null} input.exclude   an exclusionSet (lib/taskExclusions.js):
 *                                      the Settings list of our own domains and
 *                                      addresses, checked before the org
 *                                      derivation — null leaves only the
 *                                      derivation and the floor
 * @param {number}   input.trialWindowMs how far ahead "ending soon" reaches
 * @returns lanes (`inactiveUsers`, `trialUsers`, `expiredSubscriptions`,
 *   `paymentFailed`, each capped at CUSTOMER_LANE_CAP), their PRE-cap counts
 *   (`*Count`, so `count - lane.length` is the honest "+N more"), `trialLater`
 *   (trials ending beyond the window — real trials, deliberately not listed),
 *   `paymentSignalHeld` (how many contacts carry the payment-failed property at
 *   all — zero means the portal is not writing it, and the lane is UNREAD, not
 *   empty) and the exclusion tallies.
 */
export function judgeCustomerLanes({
  users = [],
  now = new Date(),
  org = null,
  mailByAddress = null,
  exclude = null,
  trialWindowMs = CUSTOMER_TRIAL_WINDOW_MS,
} = {}) {
  const nowMs = now.getTime()
  const inactive = []
  const expired = []
  const trial = []
  const paymentFailed = []
  let trialLater = 0
  let paymentSignalHeld = 0
  let excludedOwn = 0
  let neverUsed = 0
  let excludedTest = 0
  let excludedRobots = 0
  let noAddress = 0
  let unreadable = 0
  // WRITTEN OFF (the rep's report, 2026-09-25): "closed lost we don't care
  // about at all", and companies he dismissed with a "not a fit" note. The
  // read lambda judges each customer's company and ships the verdict as
  // `company_dismissed` (company_dismissals in the hubspot handler: every
  // deal Closed Lost, or a note in his dismissal wording — an open or won
  // deal outranks both). A written-off customer leaves EVERY lane, counted
  // and named. A row without the key was not checked (a lambda one deploy
  // behind, or a read the route admitted as cut short): counted as unchecked
  // and said, never read as "nobody written off".
  let dismissedClosedLost = 0
  let dismissedNotAFit = 0
  const dismissedCompanies = []
  let dismissalUnchecked = 0
  const seen = new Set()
  for (const u of Array.isArray(users) ? users : []) {
    const email = String(u?.email || '').trim().toLowerCase()
    if (!email || !email.includes('@')) {
      // A lane item is somebody to WRITE to; a contact with no address cannot
      // be one. Counted, never silently skipped.
      noAddress += 1
      continue
    }
    // Two contact records on one mailbox are one person to check in with.
    if (seen.has(email)) continue
    seen.add(email)

    // ── the exclusions, each named for what it is ──
    if (isRobotAddress(email)) {
      excludedRobots += 1
      continue
    }
    const local = email.slice(0, email.lastIndexOf('@'))
    if (TEST_USER_LOCAL_RE.test(local)) {
      excludedTest += 1
      continue
    }
    if (isOursCustomer(email, org, exclude)) {
      excludedOwn += 1
      continue
    }
    if (u && typeof u === 'object' && Object.prototype.hasOwnProperty.call(u, 'company_dismissed')) {
      const off = u.company_dismissed && typeof u.company_dismissed === 'object' ? u.company_dismissed : null
      if (off) {
        const closedLost = off.reason === 'closed_lost'
        if (closedLost) dismissedClosedLost += 1
        else dismissedNotAFit += 1
        const who = String(u?.company_name || '').trim() || String(off.label || '').trim() || email
        if (dismissedCompanies.length < 8 && !dismissedCompanies.some((c) => c.name === who)) {
          dismissedCompanies.push({
            name: who,
            reason: closedLost ? 'closed_lost' : 'not_a_fit',
            ...(off.at ? { at: String(off.at) } : {}),
            ...(off.quote ? { quote: String(off.quote).slice(0, 160) } : {}),
          })
        }
        continue
      }
    } else {
      dismissalUnchecked += 1
    }

    const props = u?.props && typeof u.props === 'object' ? u.props : {}
    const access = customerWhen(props.last_software_access)
    const subEnd = customerWhen(props.subscription_end)
    const trialEnd = customerWhen(props.trial_ends)
    const payFail = customerWhen(props[PAYMENT_FAILED_PROP])
    if (payFail.held) paymentSignalHeld += 1
    // A value the portal holds but this run cannot read as a time is admitted,
    // never bucketed — the exact opposite of a silent zero.
    if (
      (access.held && !access.date) ||
      (subEnd.held && !subEnd.date) ||
      (trialEnd.held && !trialEnd.date) ||
      (payFail.held && !payFail.date)
    ) {
      unreadable += 1
    }

    // The shared face of an item, whichever lane(s) it lands in.
    const version = String(props.software_version || '').trim()
    const projects = Number(props.project_count)
    const blueprints = Number(props.blueprint_count)
    const platformLogin = customerWhen(props.last_platform_login)
    // The bool prop, only when the portal actually holds one — "no value" and
    // "false" are different facts, and the draft route treats the field as
    // optional for exactly that reason.
    const payRaw = props.payment_method_attached
    const payHeld = payRaw !== null && payRaw !== undefined && String(payRaw).trim() !== ''
    const payAttached = payRaw === true || String(payRaw).trim().toLowerCase() === 'true'
    const mail = mailByAddress instanceof Map ? mailByAddress.get(email) : null
    const lastOut =
      mail?.lastTouchAt instanceof Date && !Number.isNaN(mail.lastTouchAt.getTime())
        ? mail.lastTouchAt.toISOString()
        : ''
    const lastIn =
      mail?.lastInboundAt instanceof Date && !Number.isNaN(mail.lastInboundAt.getTime())
        ? mail.lastInboundAt.toISOString()
        : ''
    const base = {
      // The stable copy/draft key the receipt and its resolver address the
      // item by — `cust:<email>`, never the row id, so the same person keeps
      // the same key across runs and across lanes. `recipient` is the one
      // address the draft may greet; the draft route refuses anyone else.
      key: `cust:${email}`,
      recipient: email,
      email,
      name: String(u?.name || '').trim(),
      company: String(u?.company_name || '').trim(),
      companyId: String(u?.company_id || ''),
      hubspotUrl: String(u?.hubspot_url || ''),
      ...(version ? { softwareVersion: version } : {}),
      ...(Number.isFinite(projects) ? { projectCount: projects } : {}),
      ...(Number.isFinite(blueprints) ? { blueprintCount: blueprints } : {}),
      ...(platformLogin.date ? { lastPlatformLogin: platformLogin.date.toISOString() } : {}),
      ...(payHeld ? { paymentMethodAttached: payAttached } : {}),
      // Contact history, when this run's mail read holds it for the address:
      // shown on the item so Bo sees the last conversation, never a filter.
      ...(lastOut ? { lastOutboundAt: lastOut } : {}),
      ...(lastIn ? { lastInboundAt: lastIn } : {}),
    }
    // Only the INACTIVE lane carries an email. Its item lands on the receipt
    // draftPending with kind 'inactive_user'; the receipt's own resolver
    // (lib/runHost.js) asks POST /api/enrich/draft to write the check-in, and
    // the template in lib/outreachDraft.js stands in only when that fails.
    // Every other lane is facts plus the doors into HubSpot — a pending mark
    // nobody resolves would spin a card for ever.
    const facts = base
    const drafted = { ...base, draftPending: true }

    // HAS THIS PERSON EVER USED THE PRODUCT AT ALL: any usage timestamp, or
    // work actually produced. The expired lane REQUIRES it (below); the trial
    // lane only STATES it — a trial nobody has opened is the one most worth a
    // call, not one to hide.
    const everUsed = Boolean(
      access.date ||
        platformLogin.date ||
        customerWhen(props.last_software_download).date ||
        customerWhen(props.last_elevation_analysis).date ||
        customerWhen(props.last_floorplan_analysis).date ||
        customerWhen(props.last_complete_analysis).date ||
        (Number.isFinite(projects) && projects > 0) ||
        Number(props.blueprint_count) > 0,
    )

    // A user may sit in SEVERAL lanes; each lane's item states its own fact and
    // names its own kind.
    if (access.date && nowMs - access.date.getTime() > CUSTOMER_INACTIVE_MS) {
      const days = chicagoDaysBetween(access.date, now)
      inactive.push({
        ...drafted,
        kind: 'inactive_user',
        lastSoftwareAccess: access.date.toISOString(),
        ...(days !== null ? { daysInactive: days } : {}),
      })
    }

    // EXPIRED: every end date the portal holds for this person is already
    // past. A trial that ended with a subscription still running is a
    // CUSTOMER, not an expiry; a subscription end tomorrow is live. (On the
    // live portal the two props carry the same instant, so this is one lane
    // however the portal spells it.)
    const ends = [subEnd.date, trialEnd.date].filter(Boolean)
    const lapsed = ends.length > 0 && ends.every((d) => d.getTime() < nowMs)
    if (lapsed) {
      // "Remind USERS who has expired" — and a user is somebody who USED the
      // software. Sorted longest-expired-first, the top of this lane was once
      // throwaway-domain signups that expired having never opened the
      // product, above real customers with genuine usage. A win-back note to
      // an address that never used anything is spam, not customer care.
      if (!everUsed) {
        neverUsed += 1
      } else {
        const endedAt = new Date(Math.max(...ends.map((d) => d.getTime())))
        const days = chicagoDaysBetween(endedAt, now)
        expired.push({
          ...facts,
          kind: 'expired_winback',
          expiredAt: endedAt.toISOString(),
          ...(subEnd.date ? { subscriptionEnd: subEnd.date.toISOString() } : {}),
          ...(trialEnd.date ? { trialEnds: trialEnd.date.toISOString() } : {}),
          ...(days !== null ? { daysExpired: days } : {}),
          // An access AFTER the end means they are still using the product —
          // the easiest renewal in the building, and the lane leads with it.
          ...(access.date ? { lastSoftwareAccess: access.date.toISOString() } : {}),
        })
      }
    }

    // ENDING SOON: the trial end is still ahead of this instant (`>=` on
    // purpose — a trial that ends later today is still a trial this morning)
    // AND inside the window. Further out is counted, not listed.
    if (trialEnd.date && trialEnd.date.getTime() >= nowMs) {
      if (trialEnd.date.getTime() - nowMs > trialWindowMs) {
        trialLater += 1
      } else {
        const left = chicagoDaysBetween(now, trialEnd.date)
        trial.push({
          ...facts,
          kind: 'trial_convert',
          trialEnds: trialEnd.date.toISOString(),
          ...(left !== null ? { daysLeft: left } : {}),
          everUsed,
          ...(access.date ? { lastSoftwareAccess: access.date.toISOString() } : {}),
          ...(subEnd.date ? { subscriptionEnd: subEnd.date.toISOString() } : {}),
        })
      }
    }

    // PAYMENT FAILED: the portal recorded a failed payment for this contact.
    if (payFail.date) {
      const days = chicagoDaysBetween(payFail.date, now)
      paymentFailed.push({
        ...facts,
        kind: 'payment_failed',
        paymentFailedAt: payFail.date.toISOString(),
        ...(days !== null ? { daysSinceFailure: days } : {}),
        ...(access.date ? { lastSoftwareAccess: access.date.toISOString() } : {}),
        ...(subEnd.date ? { subscriptionEnd: subEnd.date.toISOString() } : {}),
        ...(trialEnd.date ? { trialEnds: trialEnd.date.toISOString() } : {}),
      })
    }
  }
  inactive.sort(compareLane('lastSoftwareAccess'))
  expired.sort(compareExpiredLane)
  // Soonest-ending first: the trial about to lapse is the call to make today.
  trial.sort(compareLane('trialEnds'))
  // Newest failure first: the card the customer has not yet given up on.
  paymentFailed.sort(compareLane('paymentFailedAt', -1))
  return {
    inactiveUsers: inactive.slice(0, CUSTOMER_LANE_CAP),
    expiredSubscriptions: expired.slice(0, CUSTOMER_LANE_CAP),
    trialUsers: trial.slice(0, CUSTOMER_LANE_CAP),
    paymentFailed: paymentFailed.slice(0, CUSTOMER_LANE_CAP),
    inactiveUserCount: inactive.length,
    expiredSubscriptionCount: expired.length,
    trialUserCount: trial.length,
    paymentFailedCount: paymentFailed.length,
    trialLater,
    paymentSignalHeld,
    excludedOwn,
    neverUsed,
    excludedTest,
    excludedRobots,
    noAddress,
    unreadable,
    // Customers left off because their company is written off, by reason,
    // with up to eight company names; and how many rows carried no verdict.
    dismissed: {
      count: dismissedClosedLost + dismissedNotAFit,
      closedLost: dismissedClosedLost,
      notAFit: dismissedNotAFit,
      companies: dismissedCompanies,
    },
    dismissalUnchecked,
  }
}

// The one-line ledger summary the daily-task tile shows after the run.
function followUpSummaryLine(ctx) {
  if (ctx.usersUnread) return 'Customer usage could not be read'
  const bits = []
  if (ctx.inactiveUserCount) bits.push(`${fmt(ctx.inactiveUserCount)} inactive`)
  if (ctx.trialUserCount) bits.push(`${fmt(ctx.trialUserCount)} ending soon`)
  if (ctx.expiredSubscriptionCount) bits.push(`${fmt(ctx.expiredSubscriptionCount)} expired`)
  if (ctx.paymentFailedCount) bits.push(`${fmt(ctx.paymentFailedCount)} payment failed`)
  return bits.length ? bits.join(' · ') : 'No customer to follow up'
}

async function runFollowUp(op, run, { now }) {
  const { passes, indexOf, start, finish, signal, tracer, ownOrg } = run
  const at = (id) => indexOf.get(id)
  const ctx = {
    users: null,
    // The reason string when the usage read did not answer — truthy means
    // unread, and unread is never rendered as empty.
    usersUnread: '',
    org: null,
    read: null,
    historyReason: '',
    inactiveUsers: [],
    expiredSubscriptions: [],
    trialUsers: [],
    paymentFailed: [],
    inactiveUserCount: 0,
    expiredSubscriptionCount: 0,
    trialUserCount: 0,
    paymentFailedCount: 0,
    trialLater: 0,
    // The reason string when NO contact carries the payment-failed property:
    // the portal is not writing it yet, so that lane is unknown, not empty.
    paymentUnread: '',
    exclusions: [],
    // Customers left off because their company is written off (Closed Lost,
    // or a "not a fit" note), and the admission when that check did not run.
    customersDismissed: { count: 0, closedLost: 0, notAFit: 0, companies: [] },
    dismissalsUnread: '',
  }

  const handlers = {
    // ── 1a. the customer usage read (root) ────────────────────────────────────
    // GET /api/hubspot/users — the portal's product-usage attributes for every
    // contact that carries at least one. FAIL-SOFT IS THE CONTRACT:
    // hubspotUsers() resolves { available:false, reason } rather than
    // throwing, and an unreadable users API costs this run its lanes plus one
    // honest sentence — never a crash, never a zero.
    users: async () => {
      const i = at('users')
      await start(i)
      let res = null
      try {
        res = await hubspotUsers({ signal })
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        res = { available: false, reason: String(err?.message || 'the users read failed') }
      }
      if (signal?.aborted) throw abortError()
      ctx.users = res
      const unread = customerUsageUnread(res)
      if (unread) {
        ctx.usersUnread = unread
        finish(
          i,
          'skipped',
          'Customer usage could not be read — the lanes are unknown, not empty',
          [
            `GET /api/hubspot/users did not yield the usage data: ${ctx.usersUnread}.`,
            'Nothing is claimed about inactive users, trials or payments today: unread is an admission, never a zero.',
          ],
        )
        return
      }
      const n = Array.isArray(res.users) ? res.users.length : 0
      finish(
        i,
        'done',
        `${fmt(n)} portal ${plural(n, 'contact carries', 'contacts carry')} product usage data`,
        [
          'Read from the HubSpot mirror’s custom contact attributes — last software access, subscription end, trial end, payment method, project counts. Read-only, like every mirror read.',
          res.complete === false
            ? `READ BOUNDED: ${res.reason || 'the walk stopped early'} — the lanes below may undercount, and that is an admission, not a finding.`
            : 'The whole contact feed was walked — the counts below are complete as of the last sync.',
          res.last_sync_at
            ? `Mirror last synced ${shortDay(res.last_sync_at)} — the attributes are only as fresh as the sync.`
            : '',
        ],
        { value: n, label: 'with usage data' },
      )
    },

    // ── 1b. who we are, and the mail history (root) ───────────────────────────
    // Two facts the lanes need and the usage read cannot give: which addresses
    // are OURS (never a customer to chase — readOwnOrg, from the session, the
    // roster and the CRM owners), and each address's last conversation off the
    // HubSpot email engagements, shown on the card so Bo knows whether his
    // check-in lands cold. Fail-soft: an unread history costs the cards their
    // "we last wrote" line and nothing else.
    history: async () => {
      const i = at('history')
      await start(i)
      const org = await ownOrg()
      if (signal?.aborted) throw abortError()
      ctx.org = org
      let read = null
      try {
        read = await readCadenceHistory({ signal, now, ours: org })
      } catch (err) {
        if (err?.name === 'AbortError') throw err
        read = null
        ctx.historyReason = String(err?.message || 'the mail history could not be read')
      }
      if (signal?.aborted) throw abortError()
      ctx.read = read
      if (!read) {
        finish(
          i,
          'skipped',
          'The mail history could not be read — cards will carry no last conversation',
          [
            `${ctx.historyReason}.`,
            ...ownOrgLines(org).slice(0, 1),
            'Shown, never a filter: a customer is owed a check-in whatever the thread says, so an unread history changes nothing about who is listed.',
          ],
        )
        return
      }
      const n = read.byAddress.size
      finish(
        i,
        'done',
        `${fmt(n)} emailed ${plural(n, 'address carries', 'addresses carry')} a last conversation`,
        [
          ...ownOrgLines(org).slice(0, 1),
          ...historySourceLines(read).slice(0, 2),
          'Shown on each card as “we last wrote / they last wrote” — never a filter.',
        ],
        { value: n, label: 'with mail history' },
      )
    },

    // ── 2. the four lanes (users ⋈ history) ──────────────────────────────────
    customers: async () => {
      const i = at('customers')
      await start(i)
      if (ctx.usersUnread) {
        finish(
          i,
          'skipped',
          'Customer lanes not built — the usage data was not read',
          [
            `The users read did not answer (${ctx.usersUnread}), so no lane can be computed.`,
            'Unread is not empty: no claim is made that every customer is active, on a live trial, or paid up.',
          ],
        )
        return
      }
      // The Settings list of our own domains/addresses, read at RUN time so an
      // edit between two runs in the same tab reaches the second run. Read
      // once, so the judge and the admission below name the same list.
      const exclusions = readExclusions().entries
      ctx.exclusions = exclusions
      const lanes = judgeCustomerLanes({
        users: ctx.users?.users || [],
        now,
        org: ctx.org || null,
        mailByAddress: ctx.read?.byAddress || null,
        exclude: exclusionSet(exclusions),
      })
      ctx.inactiveUsers = lanes.inactiveUsers
      ctx.expiredSubscriptions = lanes.expiredSubscriptions
      ctx.trialUsers = lanes.trialUsers
      ctx.paymentFailed = lanes.paymentFailed
      ctx.inactiveUserCount = lanes.inactiveUserCount
      ctx.expiredSubscriptionCount = lanes.expiredSubscriptionCount
      ctx.trialUserCount = lanes.trialUserCount
      ctx.paymentFailedCount = lanes.paymentFailedCount
      ctx.trialLater = lanes.trialLater
      ctx.customersDismissed = lanes.dismissed
      // Unchecked is said, never read as "nobody written off": the route's own
      // reason when it admitted a cut-short read, else the plain count of rows
      // that arrived without a verdict (a lambda one deploy behind).
      if (ctx.users?.dismissals_unread) {
        ctx.dismissalsUnread = String(ctx.users.dismissals_unread)
      } else if (lanes.dismissalUnchecked) {
        ctx.dismissalsUnread = `${fmt(lanes.dismissalUnchecked)} ${plural(lanes.dismissalUnchecked, 'customer', 'customers')} arrived without a Closed Lost / not-a-fit verdict from the HubSpot mirror, so ${plural(lanes.dismissalUnchecked, 'that one was', 'those were')} not checked — unchecked, not cleared`
      }
      if (!lanes.paymentSignalHeld) {
        ctx.paymentUnread = `no contact carries “${PAYMENT_FAILED_RAW_PROP}” in HubSpot — the portal is not writing a payment-failure timestamp yet`
      }

      const more = [
        lanes.inactiveUserCount - lanes.inactiveUsers.length,
        lanes.trialUserCount - lanes.trialUsers.length,
        lanes.expiredSubscriptionCount - lanes.expiredSubscriptions.length,
        lanes.paymentFailedCount - lanes.paymentFailed.length,
      ]
      const moreTotal = more.reduce((s, n) => s + n, 0)
      const neverOpened = lanes.trialUsers.filter((t) => t.everUsed === false).length
      const droppedOurs = lanes.excludedOwn + lanes.excludedTest
      finish(
        i,
        'done',
        `${fmt(lanes.inactiveUserCount)} inactive · ${fmt(lanes.trialUserCount)} ending soon · ${fmt(lanes.expiredSubscriptionCount)} expired · ${
          ctx.paymentUnread ? 'payment failed unread' : `${fmt(lanes.paymentFailedCount)} payment failed`
        }`,
        [
          `Inactive = no software access in over 7 days. Ending soon = a “Trial Ends” inside the next 7 days, soonest first${lanes.trialLater ? ` (${fmt(lanes.trialLater)} more ${plural(lanes.trialLater, 'trial ends', 'trials end')} later than that and ${plural(lanes.trialLater, 'is', 'are')} not listed)` : ''}${neverOpened ? ` — ${fmt(neverOpened)} ${plural(neverOpened, 'has', 'have')} never opened the product, which is the call to make first` : ''}. Expired = every end date the portal holds is past AND some real usage is on record${lanes.neverUsed ? ` (${fmt(lanes.neverUsed)} never-used ${plural(lanes.neverUsed, 'signup', 'signups')} left out — a win-back to an address that never opened the product is spam)` : ''}.`,
          ctx.paymentUnread
            ? `Payment failed: ${ctx.paymentUnread}. That lane is UNREAD, not empty — it fills the day the portal starts writing that property.`
            : `Payment failed = a “${PAYMENT_FAILED_RAW_PROP}” value on the contact, newest first — ${fmt(lanes.paymentSignalHeld)} ${plural(lanes.paymentSignalHeld, 'contact carries', 'contacts carry')} the property.`,
          [
            lanes.dismissed.count
              ? `Left off ${fmt(lanes.dismissed.count)} ${plural(lanes.dismissed.count, 'customer', 'customers')} whose company is written off — ${[
                  lanes.dismissed.closedLost ? `${fmt(lanes.dismissed.closedLost)} Closed Lost` : '',
                  lanes.dismissed.notAFit ? `${fmt(lanes.dismissed.notAFit)} with a “not a fit” note` : '',
                ]
                  .filter(Boolean)
                  .join(', ')} (${lanes.dismissed.companies.map((c) => c.name).join(', ')}). An open or won deal on the company would have kept them.`
              : '',
            ctx.dismissalsUnread ? `The Closed Lost / not-a-fit check did not fully run: ${ctx.dismissalsUnread}.` : '',
            droppedOurs || lanes.excludedRobots
              ? `Excluded ${fmt(droppedOurs)} of our own / test ${plural(droppedOurs, 'account', 'accounts')} (${fmt(lanes.excludedOwn)} ours, ${fmt(lanes.excludedTest)} test ${plural(lanes.excludedTest, 'fixture', 'fixtures')})${lanes.excludedRobots ? ` and ${fmt(lanes.excludedRobots)} automated ${plural(lanes.excludedRobots, 'address', 'addresses')}` : ''} — the team dogfoods the product. “Ours” is the run’s org identity plus the excluded list in Settings (${exclusions.length ? exclusions.join(', ') : 'empty'}).`
              : '',
          ]
            .filter(Boolean)
            .join(' '),
          `Only the inactive lane carries an email, written by the model in Bo’s voice from the held facts (the template stands in only if the model cannot answer, and the card says so); the other three are facts and the doors into HubSpot. A user can sit in several lanes — each states its own fact.${moreTotal ? ` Each lane shows at most ${fmt(CUSTOMER_LANE_CAP)}: ${[more[0] ? `+${fmt(more[0])} inactive` : '', more[1] ? `+${fmt(more[1])} ending soon` : '', more[2] ? `+${fmt(more[2])} expired` : '', more[3] ? `+${fmt(more[3])} payment failed` : ''].filter(Boolean).join(', ')} — counted, not hidden.` : ''}${lanes.noAddress ? ` ${fmt(lanes.noAddress)} ${plural(lanes.noAddress, 'contact has', 'contacts have')} no email address and cannot be written to.` : ''}${lanes.unreadable ? ` ${fmt(lanes.unreadable)} carried an unreadable timestamp — left out rather than guessed.` : ''}`,
        ],
        {
          value: lanes.inactiveUserCount + lanes.trialUserCount + lanes.expiredSubscriptionCount + lanes.paymentFailedCount,
          label: 'customer items',
        },
      )
    },

    // ── 3. the follow-up ──────────────────────────────────────────────────────
    // Assembles the four sections and CREATES NOTHING: nothing stored, nobody
    // enrolled, no event that implies outreach happened. The receipt IS the
    // deliverable, and Bo carries out every step himself.
    briefing: async () => {
      const i = at('briefing')
      await start(i)
      const inactiveN = ctx.inactiveUsers.length
      const trialN = ctx.trialUsers.length
      const expiredN = ctx.expiredSubscriptions.length
      const payN = ctx.paymentFailed.length
      const empty = !inactiveN && !trialN && !expiredN && !payN
      const unreadLanes = [
        ctx.usersUnread ? 'the customer lanes' : '',
        !ctx.usersUnread && ctx.paymentUnread ? 'the payment-failed lane' : '',
      ].filter(Boolean)
      finish(
        i,
        'done',
        empty
          ? unreadLanes.length
            ? `Nothing to follow up — but ${unreadLanes.join(' and ')} could not be read`
            : 'Nothing to follow up today — nobody inactive, on an ending trial, expired, or failed a payment'
          : `${fmt(inactiveN)} inactive · ${fmt(trialN)} ending soon · ${fmt(expiredN)} expired · ${ctx.paymentUnread ? 'payment failed unread' : `${fmt(payN)} payment failed`}`,
        [
          'A follow-up, not a build: nothing created, nobody enrolled, nothing sent. The rep’s HubSpot tasks live in Daily Tasks.',
          ctx.usersUnread
            ? 'All four sections were SKIPPED — the usage data could not be read, which is not the same as “every customer is active, paid up, and nobody is on trial”.'
            : `${inactiveN ? `${fmt(inactiveN)} inactive ${plural(inactiveN, 'user', 'users')} — each check-in email is written on the receipt. ` : 'No inactive user. '}${trialN ? `${fmt(trialN)} ${plural(trialN, 'trial ends', 'trials end')} within 7 days — facts only, the pitch is Bo’s.` : 'No trial ends within 7 days.'}${ctx.trialLater ? ` ${fmt(ctx.trialLater)} more ${plural(ctx.trialLater, 'ends', 'end')} later.` : ''}`,
          ctx.usersUnread
            ? ''
            : `${expiredN ? `${fmt(expiredN)} expired ${plural(expiredN, 'trial', 'trials')}, most recently active first — the win-back is Bo’s own.` : 'No expired trial with real usage on record.'} ${
                ctx.paymentUnread
                  ? `Payment failed: UNREAD — ${ctx.paymentUnread}.`
                  : payN
                    ? `${fmt(payN)} failed ${plural(payN, 'payment', 'payments')}, newest first.`
                    : 'No failed payment on record.'
              }`,
        ],
        { value: inactiveN + trialN + expiredN + payN, label: 'follow-up items' },
      )
    },
  }

  return runGraph(run, handlers, () => ({
    ok: true,
    passes,
    selected: [],
    tasks: [],
    // The receipt shape (see runDailyTasks). `worklist` rides along for the
    // readers that still key on it (the receipt's "N more" wording).
    worklist: true,
    briefing: 'customers',
    inactiveUsers: ctx.inactiveUsers,
    expiredSubscriptions: ctx.expiredSubscriptions,
    trialUsers: ctx.trialUsers,
    paymentFailed: ctx.paymentFailed,
    inactiveUserCount: ctx.inactiveUserCount,
    expiredSubscriptionCount: ctx.expiredSubscriptionCount,
    trialUserCount: ctx.trialUserCount,
    paymentFailedCount: ctx.paymentFailedCount,
    trialLater: ctx.trialLater,
    // Unread ≠ empty, on the result itself: the flag plus the read's own
    // reason, so a receipt line can say WHY a lane is missing.
    ...(ctx.usersUnread ? { usersUnread: true, usersUnreadReason: String(ctx.usersUnread) } : {}),
    ...(ctx.paymentUnread ? { paymentFailedUnread: String(ctx.paymentUnread) } : {}),
    // Who was left off as written off, and whether that check ran — carried
    // only when they say something (chat.js#packRunMessage copies both).
    ...(ctx.customersDismissed?.count ? { customersDismissed: ctx.customersDismissed } : {}),
    ...(ctx.dismissalsUnread ? { dismissalsUnread: String(ctx.dismissalsUnread) } : {}),
    summary: followUpSummaryLine(ctx),
    // "Nothing to do" may only be claimed when every lane is empty AND every
    // lane was actually readable — an unread lane is UNKNOWN, not empty.
    ...(!ctx.inactiveUsers.length &&
    !ctx.trialUsers.length &&
    !ctx.expiredSubscriptions.length &&
    !ctx.paymentFailed.length &&
    !ctx.usersUnread &&
    !ctx.paymentUnread
      ? { nothingToDo: true }
      : {}),
    ...tracer.snapshot([]),
  }))
}
